/*
 * terrain-studio.js — <terrain-studio>, the V13 standalone Terrain Studio.
 *
 * Flow: Route (Strava / GPX / sample) → Product → Map (frame + features + Generate) →
 * Customise (backpiece preview, terrain height, colours) → Add to cart (AJAX).
 *
 * Classic script (Dawn web-component pattern). The heavy modules — GPX parser, elevation,
 * map data, geometry worker, Three.js preview, MapLibre framing, 3MF export — are
 * imported lazily, only when a step needs them.
 *
 * Engine calls are lifted from the v12 terrain-visualiser.js: same geometry constants,
 * same colour budget and presets, same cart property contract. Spec: V13-PLAN.md.
 */
(() => {
  if (customElements.get('terrain-studio')) return;

  const STEPS = ['route', 'product', 'map', 'customise'];
  const MAP_FEATURES = ['water', 'forest', 'roads', 'buildings'];
  const ALL_FEATURES = ['water', 'forest', 'roads', 'buildings', 'snow'];
  const PRESETS = { plain: [], trail: ['forest', 'water'], city: ['roads', 'buildings'] };
  // Shipped defaults of the retired snow_pct section setting: a saved snowPct equal to one of
  // these was never the shopper's choice (SNOW-DESIGN §6.3).
  const LEGACY_SNOW_PCT_DEFAULTS = [55, 60];
  const STORE_KEY = 'ttv_studio_v13';
  const SID_KEY = 'ttv_strava_sid';
  const SAVED_KEY = 'ttv_studio_v13_saved'; // localStorage: the last design, across visits
  const IDB_NAME = 'vestige-studio'; // IndexedDB: that design's route files (up to 25 MB)
  const IDB_STORE = 'routes';
  const MAX_STORED_GPX = 2 * 1024 * 1024;
  const AUTOSAVE_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // "Continue your last design" is offered for 30 days
  const EARTH_R = 6378137;
  const SPAN_HARD_MAX_KM = 150;
  const SPAN_WARN_KM = 60;
  // Roads on wide tiles (20 Oct 2026): every mapped road is printed at least about 1 mm wide, so on a tile that
  // covers more than a few kilometres residential streets merge into solid blobs (Port Macquarie at 33 km across).
  // Keep only the road classes that still read as lines at that scale; small tiles keep every drivable road.
  // Tiers (second cut, same day, after the founder found the first one too sparse at 33 km): residential and living
  // streets go first, the unclassified country lanes only on very wide tiles; tertiary roads always stay.
  const ROAD_CLASS_TIERS = [
    [7, null],
    [45, ['motorway', 'trunk', 'primary', 'secondary', 'tertiary', 'unclassified',
      'motorway_link', 'trunk_link', 'primary_link', 'secondary_link', 'tertiary_link']],
    [Infinity, ['motorway', 'trunk', 'primary', 'secondary', 'tertiary', 'motorway_link', 'trunk_link', 'primary_link']],
  ];
  function roadLinesForSpan(lines, spanKm) {
    if (!Array.isArray(lines) || !(spanKm > 0)) return lines || [];
    const tier = ROAD_CLASS_TIERS.find(([upTo]) => spanKm <= upTo);
    if (!tier || !tier[1]) return lines;
    const keep = new Set(tier[1]);
    return lines.filter((l) => keep.has(l && l.highway));
  }
  const SPAN_MIN_KM = 0.5;
  const COVERAGE_WARN = 0.6;
  const HEX_WINDOW = 0.42; // hexagon circumradius as a share of the stage's shorter side
  // Route width (ROUTE-AND-BUILDINGS-DESIGN §B.2, §E.1): tube radius and the offset above the
  // surface, in print mm. The offset rides with the width (Bold stands a little prouder).
  const ROUTE_WIDTHS = Object.freeze({
    fine: Object.freeze({ radiusMm: 0.8, offsetMm: 0.3 }),
    standard: Object.freeze({ radiusMm: 1.1, offsetMm: 0.3 }),
    bold: Object.freeze({ radiusMm: 1.5, offsetMm: 0.5 }),
  });
  const DEFAULT_ROUTE_WIDTH = 'standard';
  // Every new design uses route cover v4 (terrain-solid ROUTE_V4, §E.2b: a half-round cord lying on
  // ground that is eased under it) and the Softened terrain (terrain-worker §C). A design saved with
  // routeVersion 2 keeps rendering as v2, one saved with 4 as v4 (_restoreDesignOptions). A design
  // saved with routeVersion 3 (1 Oct 2026 only), with any other value or with none restores as v4
  // with the §E.3 notice. Legacy (keys absent) exists only for old orders.
  const ROUTE_VERSION = 4;
  const ROUTE_VERSIONS_RESTORABLE = [2, 4];
  const BUILDINGS_MODES = ['individual', 'blocks'];
  // Buildings cap for Individual, in parseOverpass units (water-data BUILDINGS_CAP.individual).
  const INDIVIDUAL_BUILDINGS_CAP = 2000;
  // Switchback hint (§B.2): merged centreline over 5 mm at the chosen width, and Fine at least
  // 50 % shorter.
  const SWITCHBACK_MIN_MM = 5;
  const SWITCHBACK_FINE_RATIO = 0.5;
  // Map lookups (SCALE-AND-SPEED-REPORT §7 #4): one overall budget for every kind, instead of up
  // to 70 s per attempt. A kind that misses it is built without (and named in the status line);
  // its request finishes in the background and fills the cache for the next Generate.
  // When such a kind does arrive and the shopper has changed nothing since that Generate, it is
  // added to the preview automatically ("Adding forest…"); Update preview stays as the fallback.
  const MAP_BUDGET_MS = 18000;
  // A cart app can overwrite our order attributes as they are written (order #1002): re-read, and
  // write once more after this pause if any of ours is missing (_recordOrderAttributes).
  const ORDER_ATTRIBUTES_RETRY_MS = 800;
  const MAP_ATTEMPT_MS = 30000; // per provider attempt, for that background finish
  // A lookup made only for the automatic coastal Water (the studio's choice, not the shopper's) waits
  // less: past this the tile is built as the shopper set it, and the answer still fills the cache.
  const AUTO_WATER_BUDGET_MS = 8000;
  const PREFETCH_DELAY_MS = 1200; // a settled framing on the Map step starts the lookup early
  const PREFETCH_MAX = 12; // framings prefetched per visit (a shopper panning back and forth)
  // Elevation for a settled framing on the Map step (§7 #8 / report #6): fetched before Generate,
  // one framing at a time, and the last two framings' rasters kept by fit key.
  const ELEVATION_PREFETCH_DELAY_MS = 700;
  const ELEVATION_PREFETCH_KEEP = 2;
  // Prebuilt export on Customise (§7 #3): start once the preview has been settled this long (a
  // slider drag is many changes), in an idle moment; the 3MF waits a little longer after a colour
  // change, and an optional pre-upload longer still. Pre-uploads are capped per visit.
  const PREBUILD_SETTLE_MS = 1500;
  const PREZIP_SETTLE_MS = 800;
  const PREUPLOAD_SETTLE_MS = 3000;
  const PREUPLOAD_MAX = 4;
  // Progressive status copy (§7 #12) for steps that can run long.
  const LOOKUP_SLOW_MS = 6000;
  const BUILD_SLOW_MS = 8000;
  // A coastal tile (§7 #7): the lowest ground inside the hexagon at or below this is sea floor.
  const SEA_FLOOR_M = -10;
  // Print order of the map features in shopper copy ("Adding forest and water").
  const FEATURE_ORDER = ['forest', 'water', 'roads', 'buildings'];
  // MAP-DATA-LONG-TERM-PLAN Phase 0 #1: when nothing arrived inside the lookup budget, the answers
  // still on their way get this long past the deadline ("Still looking for map details…"), and
  // what lands is built without another click. Kinds still missing then get LATE_RETRY_BUDGET_MS
  // in the lookup that follows, and are left out as usual (#7).
  const LATE_WAIT_MS = 30000;
  const LATE_RETRY_BUDGET_MS = 8000;
  // Phase 0 #3: a 3D build that has not reported by then is stopped and its worker replaced.
  // Heavy builds get longer (_buildIsHeavy): the export grid, a tile wider than SPAN_WARN_KM,
  // Blocks buildings, or more map features than the Individual buildings cap. These are the
  // limits on the reference desktop: a slower or busier device gets them times its measured
  // slowness (_deviceSlowness), and a build never less than WATCHDOG_ESTIMATE_X times its own
  // expected length (_buildWatchdogMs).
  const BUILD_WATCHDOG_MS = 90000;
  const BUILD_WATCHDOG_HEAVY_MS = 180000;
  const WATCHDOG_ESTIMATE_X = 3;
  // The reference desktop: a terrain-only preview build takes 3.8-5.9 s (phase7-scale quiet runs),
  // each map layer adds at least BUILD_ESTIMATE_PER_LAYER_MS (three times that on a heavy tile),
  // and CPU_CAL_LOOPS square roots on its page take 6-7 ms (26-32 ms at a 4x CPU throttle).
  const TERRAIN_BUILD_REF_MS = 4000;
  const CPU_CAL_LOOPS = 3e6;
  const CPU_CAL_REF_MS = 7;
  const CPU_CAL_EVERY_MS = 30000;
  const DEVICE_SLOWNESS_MAX = 8;
  // Phase 0 #7: a map feature left out of the preview is looked up again in the background, this
  // often and this many times; Add to cart waits until the preview has every feature chosen.
  const LEFT_OUT_RETRY_MS = 20000;
  const LEFT_OUT_RETRIES = 3;
  // Phase 0 #4: where the bar runs during each part of Generate, and the first guess at a build's
  // length for its stage text (later builds go by the last one's measured time).
  const PROGRESS_ELEVATION = [0.05, 0.35];
  const PROGRESS_LOOKUP = [0.35, 0.6];
  const PROGRESS_BUILD = [0.6, 0.96];
  // "Still looking for map details…" runs on from the lookup to here, evenly over LATE_WAIT_MS;
  // the build after it starts where the bar is.
  const PROGRESS_LATE_TOP = 0.8;
  const BUILD_ESTIMATE_MS = 6000;
  const BUILD_ESTIMATE_PER_LAYER_MS = 3000;
  // The engine builds the terrain, then each layer in this order (the stage text follows it).
  const BUILD_STAGE_ORDER = ['water', 'forest', 'roads', 'buildings'];

  class TerrainStudio extends HTMLElement {
    connectedCallback() {
      if (this._mounted) return;
      this._mounted = true;

      // The Strava return fragment (#strava=<sid>) is a bearer credential: take it out of the
      // URL now, synchronously, before any other script on the page can read location.href.
      this._stravaReturn = this._takeStravaFragment();

      this.config = this._readConfig();
      this.t = this._readStrings();
      this.els = this._queryEls();
      this.products = this._readProducts();
      // Developer tools (3MF/STL download, audit) are for our own checks on a local server or an
      // unpublished theme only, never on the published store, where they would hand out the print file.
      this._devMode =
        /[?&]ttv_dev=1/.test(window.location.search) &&
        (/^(localhost|127.0.0.1)$/.test(window.location.hostname) ||
          (window.Shopify && window.Shopify.theme && window.Shopify.theme.role !== 'main'));
      this._geometryCache = new Map();
      this._mapRevision = 0;
      this._loadId = 0;

      this.state = {
        step: 'route',
        route: null,
        product: null,
        features: { water: false, forest: false, roads: false, buildings: false, snow: false },
        zMult: this.config.defaultZMult,
        // Snow line in real metres (SNOW-DESIGN §1.4): null = auto (the engine picks the default
        // on every build); snowPctLegacy carries an old saved snowPct into the first build only.
        snowLineM: null,
        snowLineUser: false,
        snowPctLegacy: null,
        colours: { ...this.config.colors },
        preset: 'plain',
        backpiece: this.config.backpieceDefaultOn,
        view: 'map',
        // ROUTE-AND-BUILDINGS-DESIGN §E.1: route width, the advanced Sculpted surface (off =
        // Softened), and the Buildings style. buildingsMode is the shopper's own pick (null until
        // they pick); the style actually used follows the span (_buildingsMode).
        routeWidth: DEFAULT_ROUTE_WIDTH,
        sculpted: false,
        buildingsMode: null,
        buildingsModeSource: 'auto',
        // The route cover this design is built with (saved with the design; not a control).
        routeVersion: ROUTE_VERSION,
      };

      if (this.els.attribution) this.els.attribution.textContent = this.config.attribution;

      if (!this._isSupported()) {
        if (this.els.unsupported) this.els.unsupported.hidden = false;
        this.querySelectorAll('.ts__actions, .ts__dropzone').forEach((el) => (el.hidden = true));
        return;
      }

      this._bind();
      this._syncInputsFromState();
      this._render();
      this._warmCells();
      // The Strava health probe runs alongside the restore, so the reserved button box settles sooner.
      this._stravaInit = this._initStrava();
      this._restore()
        .catch((err) => console.warn('[terrain-studio] restore failed', err))
        .finally(() => {
          this._syncInputsFromState();
          this._render();
          if (this._devMode) this._mountDevPanel();
        });
    }

    disconnectedCallback() {
      this._abort?.abort();
      this._stageObserver?.disconnect();
      clearTimeout(this._zTimer);
      clearTimeout(this._saveTimer);
      clearTimeout(this._prefetchTimer);
      this._cancelPrefetch();
      this._cancelElevationPrefetch();
      this._cancelPrebuild('disconnected');
      if (this._worker) {
        this._worker.terminate();
        this._worker = null;
        this._workerJobs = null;
      }
      if (this._preview && typeof this._preview.dispose === 'function') this._preview.dispose();
      this._preview = null;
      this._previewPromise = null;
      this._mapApi?.dispose?.();
      this._mapApi = null;
    }

    /* =====================================================================
     * Config, strings, elements
     * ===================================================================*/

    _readConfig() {
      const d = this.dataset;
      const num = (v, fallback) => {
        const n = parseFloat(v);
        return Number.isFinite(n) ? n : fallback;
      };
      let magnets = [];
      try {
        magnets = JSON.parse(d.magnetJson || '[]');
      } catch (e) {
        magnets = [];
      }
      const palette = String(d.palette || '')
        .split(',')
        .map((entry) => {
          // Name|#HEX|Filament — shoppers see the name; the filament is recorded on the order.
          const [name, hex, filament] = entry.split('|').map((s) => (s || '').trim());
          return name && hex ? { name, hex: hex.toUpperCase(), filament: filament || '' } : null;
        })
        .filter(Boolean);
      return {
        sectionId: d.sectionId,
        hexOrientation: d.hexOrientation || 'flat_top',
        sideMm: num(d.sideMm, 85),
        baseMm: num(d.baseMm, 16),
        maxHeightMm: num(d.maxHeightMm, 50),
        defaultZMult: num(d.defaultZmult, 2.0),
        gridMm: num(d.gridMm, 0.5),
        exportGridMm: num(d.exportGridMm, 0.4),
        waterRaiseMm: num(d.waterRaiseMm, 0.6),
        magnets,
        magnetDiamMm: num(d.magnetDiamMm, 18.2),
        magnetDepthMm: num(d.magnetDepthMm, 3.2),
        colors: {
          terrain: (d.colorTerrain || '#E8DDD0').toUpperCase(),
          water: (d.colorWater || '#1B2A4A').toUpperCase(),
          route: (d.colorRoute || '#C12E1F').toUpperCase(),
          roads: (d.colorRoads || d.colorRoute || '#1B2A4A').toUpperCase(),
          forest: (d.colorForest || '#0A4526').toUpperCase(),
          buildings: (d.colorBuildings || '#E8DDD0').toUpperCase(),
          snow: (d.colorSnow || '#FFFFFF').toUpperCase(),
        },
        palette,
        showSnow: d.showSnow === 'true',
        // SNOW-DESIGN §3.4: offer Snow only where the tile reaches the latitude's snow line
        // (section setting snow_climate_guard, default on). The default line comes from the
        // engine (meta.snowDefaultM); the old snow_pct section setting is retired.
        snowClimateGuard: d.snowClimateGuard !== 'false',
        // §7 #7: turn Water on for a tile whose ground dips below sea level (section setting
        // auto_water_coast, default on). Also waits for an engine that caps map-feature complexity.
        autoWaterCoast: d.autoWaterCoast !== 'false',
        // Only up to this span (km across points; section setting auto_water_coast_max_km, default
        // 15, 5–40). A wider coastal tile is built without Water and its sea flattened to 0 m in
        // the worker (seaFlatten), so the first preview does not wait for a build with the sea.
        autoWaterCoastMaxKm: Math.min(40, Math.max(5, num(d.autoWaterCoastMaxKm, 15))),
        // §7 #3: build the 0.4 mm export and its 3MF in the background on Customise (default on;
        // data-prebuild-export="false" turns it off). The 3MF stays in this browser until Add to cart.
        prebuildExport: d.prebuildExport !== 'false',
        // §7 #3: also upload that 3MF before Add to cart. Off unless data-preupload-model="true":
        // the privacy policy says the print file is uploaded when the shopper adds the tile to the
        // cart, and a Turnstile check (when configured) is per upload, so it always waits for the click.
        preuploadModel: d.preuploadModel === 'true',
        // §D.4: Individual up to 2 km, Blocks up to 5 km; beyond that Buildings are not offered.
        buildingsIndividualMaxSpanKm: num(d.buildingsIndividualMaxSpanKm, 2),
        buildingsBlocksMaxSpanKm: num(d.buildingsBlocksMaxSpanKm, 5),
        defaultProduct: d.defaultProduct || 'none',
        preorder: (d.preorder || '').trim(),
        backpieceDefaultOn: d.backpieceDefaultOn !== 'false',
        backpieceColour: d.backpieceColour || '#16233D',
        basemapStyleUrl: d.basemapStyleUrl || '',
        tileUrlTemplate: d.tileUrlTemplate,
        workerUrl: (d.workerUrl || '').replace(/\/+$/, ''),
        // Phase 1 map cells (MAP-DATA-LONG-TERM-PLAN): Australian map details from the pre-built cells behind the
        // worker's /cell (section setting cells_enabled, default off). data-ts-cells also turns them on, for tests;
        // when it holds a URL the cells come from that worker (staging) while /map stays on workerUrl.
        cellsEnabled: d.cellsEnabled === 'true' || this.hasAttribute('data-ts-cells'),
        cellsUrl: /^https?:\/\//.test(d.tsCells || '') ? d.tsCells.replace(/\/+$/, '') : '',
        stravaWorkerUrl: (d.stravaWorkerUrl || '').replace(/\/+$/, ''),
        turnstileSitekey: d.turnstileSitekey || '',
        cartAddUrl: d.cartAddUrl || '/cart/add',
        cartUrl: d.cartUrl || '/cart',
        sampleUrl: d.sampleUrl || '',
        threeModuleUrl: d.threeModuleUrl,
        workerModuleUrl: d.workerModuleUrl,
        previewModuleUrl: d.previewModuleUrl,
        gpxModuleUrl: d.gpxModuleUrl,
        elevationModuleUrl: d.elevationModuleUrl,
        geometryModuleUrl: d.geometryModuleUrl,
        solidModuleUrl: d.solidModuleUrl,
        kernelModuleUrl: d.kernelModuleUrl,
        wasmUrl: d.wasmUrl,
        meshModuleUrl: d.meshModuleUrl,
        waterModuleUrl: d.waterModuleUrl,
        mapModuleUrl: d.mapModuleUrl || '',
        maplibreJsUrl: d.maplibreJsUrl || '',
        maplibreCssUrl: d.maplibreCssUrl || '',
        stravaModuleUrl: d.stravaModuleUrl || '',
        backpieceUrl: d.backpieceUrl || '',
        attribution: d.attribution || '',
        maxGpxBytes: 25 * 1024 * 1024,
      };
    }

    _readStrings() {
      const defaults = {
        next: 'Next',
        generate: 'Generate preview',
        regenerate: 'Update preview',
        add: 'Add to cart',
        adding: 'Adding…',
        viewCart: 'View cart',
        soldOut: 'Currently unavailable',
        notConfigured: 'Checkout not yet configured',
        studio: 'Terrain Studio',
        confirmChange: 'Start again with a different route? Your current design will be cleared.',
        span: 'This tile covers [km] km across',
        buildingsLimit: 'Available under 5 km',
        buildingsOver: 'Available under 5 km — zoom in to add buildings',
        removeLeg: 'Remove [name]',
        heightReadout: 'Tile height: [mm] mm',
        heightAdjustedMax: 'Adjusted to the maximum for this route, [x]×.',
        heightAdjustedMin: 'Adjusted to the minimum, [x]×.',
        routeSwitchback: 'Some tight switchbacks join into one line at this width. Try Fine to keep them separate.',
        buildingsStyleIndividual: 'Individual buildings suit a map this size.',
        buildingsStyleBlocks: 'Blocks suits a map this size.',
        buildingsIndividualOver: 'Individual buildings are available on maps under [km] km across; Blocks keeps them printable on bigger maps.',
        snow: {
          m: '[m] m',
          auto: 'Auto',
          generate: 'Generate the preview to place the snow line.',
          cover: 'Snow above [m] m covers about [pct]% of the land. Drag left for more snow, right for less.',
          moved: 'Snow line moved to [m] m to stay within this tile.',
          flat: 'Too flat for snow — this tile has only [m] m of height.',
          none: 'No snow at [m] m — the ground above it is too small to print. Lower the snow line.',
          climate: 'Snow is not offered here: this area sits below the [m] m where snow settles at this latitude.',
          budget: 'Snow adds a fifth colour. Turn off a feature or match a colour to keep four.',
        },
        added: 'Added — your model is attached to this item.',
        strava: {},
        msg: {},
      };
      try {
        const node = this.querySelector('[data-ts-strings]');
        return { ...defaults, ...(node ? JSON.parse(node.textContent) : {}) };
      } catch (e) {
        return defaults;
      }
    }

    /** A shopper message from locales (vestige_studio.messages) with its [placeholders] filled in. */
    _m(key, vars) {
      let text = (this.t.msg && this.t.msg[key]) || '';
      if (vars) for (const [name, value] of Object.entries(vars)) text = text.split(`[${name}]`).join(String(value));
      return text;
    }

    _queryEls() {
      const q = (s) => this.querySelector(s);
      const qa = (s) => Array.from(this.querySelectorAll(s));
      return {
        stage: q('[data-ts-stage]'),
        entryArt: q('[data-ts-entry-art]'),
        routeCanvas: q('[data-ts-route-canvas]'),
        map: q('[data-ts-map]'),
        canvas: q('[data-ts-canvas]'),
        stageToggle: q('[data-ts-stage-toggle]'),
        viewButtons: qa('[data-ts-view]'),
        stageNote: q('[data-ts-stage-note]'),
        progress: q('[data-ts-progress]'),
        progressBar: q('[data-ts-progress-bar]'),
        attribution: q('[data-ts-attribution]'),
        panel: q('[data-ts-panel]'),
        panes: qa('[data-ts-pane]'),
        tabs: qa('[data-ts-tab]'),
        entry: q('[data-ts-entry]'),
        stravaConnect: q('[data-ts-strava-connect]'),
        file: q('[data-ts-file]'),
        upload: q('[data-ts-upload]'),
        sample: q('[data-ts-sample]'),
        dropzone: q('[data-ts-dropzone]'),
        stravaPicker: q('[data-ts-strava-picker]'),
        routeSummary: q('[data-ts-route-summary]'),
        stats: Object.fromEntries(qa('[data-ts-stat]').map((el) => [el.dataset.tsStat, el])),
        elevationRow: q('[data-ts-stat-row="elevation"]'),
        routeSource: q('[data-ts-route-source]'),
        changeInline: q('[data-ts-change-route-inline]'),
        productInputs: qa('[data-ts-product-input]'),
        panHint: q('[data-ts-pan-hint]'),
        autofit: q('[data-ts-autofit]'),
        span: q('[data-ts-span]'),
        routeCount: q('[data-ts-route-count]'),
        routeList: q('[data-ts-route-list]'),
        features: Object.fromEntries(qa('[data-ts-feature]').map((el) => [el.dataset.tsFeature, el])),
        featureNotes: Object.fromEntries(qa('[data-ts-feature-note]').map((el) => [el.dataset.tsFeatureNote, el])),
        generate: q('[data-ts-generate]'),
        sea: q('[data-ts-sea]'),
        seaWater: q('[data-ts-sea-water]'),
        dirty: q('[data-ts-dirty]'),
        snowField: q('[data-ts-snow-field]'),
        snowLine: q('[data-ts-snow-line]'),
        snowNumber: q('[data-ts-snow-number]'),
        snowOutput: q('[data-ts-snow-output]'),
        snowAuto: q('[data-ts-snow-auto]'),
        snowHint: q('[data-ts-snow-hint]'),
        snowBudget: q('[data-ts-snow-budget]'),
        buildingsStyle: q('[data-ts-buildings-style]'),
        buildingsStyleInputs: qa('[data-ts-buildings-style-input]'),
        buildingsStyleHint: q('[data-ts-buildings-style-hint]'),
        upgradeNotice: q('[data-ts-upgrade-notice]'),
        routeWidthInputs: qa('[data-ts-route-width-input]'),
        routeWidthHint: q('[data-ts-route-width-hint]'),
        sculpted: q('[data-ts-sculpted]'),
        backpiece: q('[data-ts-backpiece]'),
        zmult: q('[data-ts-zmult]'),
        zmultNumber: q('[data-ts-zmult-number]'),
        zmultOutput: q('[data-ts-zmult-output]'),
        zmultHint: q('[data-ts-zmult-hint]'),
        height: q('[data-ts-height]'),
        presets: qa('[data-ts-preset]'),
        colourRows: qa('[data-ts-colour-row]'),
        colourInputs: qa('[data-ts-colour]'),
        colourBudget: q('[data-ts-colour-budget]'),
        colourError: q('[data-ts-colour-error]'),
        status: q('[data-ts-status]'),
        incomplete: q('[data-ts-incomplete]'),
        warnings: q('[data-ts-warnings]'),
        toast: q('[data-ts-toast]'),
        toastText: q('[data-ts-toast-text]'),
        toastClose: q('[data-ts-toast-close]'),
        turnstile: q('[data-ts-turnstile]'),
        unsupported: q('[data-ts-unsupported]'),
        barTitle: q('[data-ts-bar-title]'),
        barPrice: q('[data-ts-bar-price]'),
        back: q('[data-ts-back]'),
        changeRoute: q('[data-ts-change-route]'),
        primary: q('[data-ts-primary]'),
        confirm: q('[data-ts-confirm]'),
        resume: q('[data-ts-resume]'),
        resumeThumb: q('[data-ts-resume-thumb]'),
        resumeName: q('[data-ts-resume-name]'),
        resumeContinue: q('[data-ts-resume-continue]'),
        resumeFresh: q('[data-ts-resume-fresh]'),
        colourCode: q('[data-ts-colour-code]'),
        codeCopy: q('[data-ts-code-copy]'),
        codePaste: q('[data-ts-code-paste]'),
        codeApply: q('[data-ts-code-apply]'),
      };
    }

    _readProducts() {
      const products = {};
      for (const input of this.els.productInputs) {
        const d = input.dataset;
        products[input.value] = {
          key: input.value,
          variantId: d.variantId,
          price: parseInt(d.price, 10) || 0,
          priceLabel: d.priceLabel || '',
          available: d.available === 'true',
          title: d.title || '',
          handle: d.handle || '',
          url: d.url || '',
          savings: parseInt(d.savings, 10) || 0,
        };
      }
      return products;
    }

    /* =====================================================================
     * Feature detection
     * ===================================================================*/

    /**
     * Cheap checks at load; creating a real WebGL context is slow (and wastes GPU memory on
     * a page that may never reach 3D), so that test waits for the first preview (_hasWebGL).
     */
    _isSupported() {
      return (
        (typeof WebGL2RenderingContext !== 'undefined' || typeof WebGLRenderingContext !== 'undefined') &&
        typeof Worker === 'function' &&
        typeof OffscreenCanvas !== 'undefined'
      );
    }

    _hasWebGL() {
      if (this._webglOk !== undefined) return this._webglOk;
      try {
        const c = document.createElement('canvas');
        const gl = c.getContext('webgl2') || c.getContext('webgl');
        this._webglOk = !!gl;
        gl?.getExtension('WEBGL_lose_context')?.loseContext();
      } catch (e) {
        this._webglOk = false;
      }
      return this._webglOk;
    }

    /* =====================================================================
     * Events
     * ===================================================================*/

    _bind() {
      this._abort = new AbortController();
      const opts = { signal: this._abort.signal };
      const on = (el, type, fn) => el && el.addEventListener(type, fn, opts);

      // --- steps ---
      this.els.tabs.forEach((tab) => {
        on(tab, 'click', () => this.goTo(tab.dataset.tsTab, true));
        on(tab, 'keydown', (e) => this._onTabKey(e));
      });

      // --- route ---
      on(this.els.file, 'change', (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = '';
        if (files.length) this._loadFiles(files);
      });
      on(this.els.stravaConnect, 'click', () => this._stravaConnect());
      on(this.els.upload, 'click', () => {
        if (!this._busy) this.els.file?.click();
      });
      on(this.els.sample, 'click', () => this._loadSample());
      on(this.els.changeInline, 'click', () => this._changeRoute());
      on(this.els.changeRoute, 'click', () => this._changeRoute());

      // Files may be dropped anywhere on the studio while the route entry is showing.
      const hasFiles = (e) => Array.from(e.dataTransfer?.types || []).includes('Files');
      on(this, 'dragover', (e) => {
        if (!hasFiles(e)) return;
        e.preventDefault();
        if (this._acceptingFiles()) this.classList.add('is-dragover');
        else if (e.dataTransfer) e.dataTransfer.dropEffect = 'none';
      });
      on(this, 'dragleave', (e) => {
        if (!this.contains(e.relatedTarget)) this.classList.remove('is-dragover');
      });
      on(this, 'drop', (e) => {
        if (!hasFiles(e)) return;
        e.preventDefault();
        this.classList.remove('is-dragover');
        if (this._acceptingFiles() && e.dataTransfer.files?.length) this._loadFiles(Array.from(e.dataTransfer.files));
      });

      // --- product ---
      this.els.productInputs.forEach((input) =>
        on(input, 'change', () => {
          if (input.checked) this._selectProduct(input.value);
        })
      );

      // --- map ---
      on(this.els.autofit, 'click', () => this._autoFit({ user: true }));
      on(this.els.generate, 'click', () => this._generate());
      on(this.els.seaWater, 'click', () => {
        this._onFeatureToggle('water', true);
        this.els.features.water?.focus();
      });
      on(this.els.routeList, 'click', (e) => {
        const btn = e.target.closest('[data-ts-remove-leg]');
        if (btn) this._removeLeg(parseInt(btn.dataset.tsRemoveLeg, 10));
      });
      Object.entries(this.els.features).forEach(([key, input]) =>
        on(input, 'change', () => this._onFeatureToggle(key, input.checked))
      );
      this.els.buildingsStyleInputs.forEach((input) =>
        on(input, 'change', () => {
          if (input.checked) this._onBuildingsStyle(input.value);
        })
      );
      on(this.els.snowLine, 'input', (e) => this._onSnowLineInput(parseFloat(e.target.value)));
      on(this.els.snowLine, 'change', (e) => this._onSnowLineChange(parseFloat(e.target.value)));
      on(this.els.snowAuto, 'click', () => this._onSnowAuto());
      // Typed snow line: commits on change (blur) or Enter; while typing, renders leave it alone.
      on(this.els.snowNumber, 'input', () => (this._snowTyping = true));
      on(this.els.snowNumber, 'change', () => this._commitSnowNumber());
      on(this.els.snowNumber, 'keydown', (e) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        this._commitSnowNumber();
      });
      on(this.els.snowNumber, 'blur', () => {
        this._snowTyping = false;
        this._renderSnowField();
      });
      this.els.viewButtons.forEach((btn) =>
        on(btn, 'click', () => {
          this.state.view = btn.dataset.tsView === '3d' ? '3d' : 'map';
          this._render();
        })
      );

      // --- customise ---
      on(this.els.backpiece, 'change', () => this._setBackpiece(this.els.backpiece.checked));
      on(this.els.zmult, 'input', (e) => {
        this._zAdjusted = null; // the slider never leaves the range: drop a typed-value notice
        this._renderZHint();
        this._onZMultInput(parseFloat(e.target.value));
      });
      // Typed height: commits on change (blur) or Enter; while typing, renders leave it alone.
      on(this.els.zmultNumber, 'input', () => (this._zTyping = true));
      on(this.els.zmultNumber, 'change', () => this._commitZNumber());
      on(this.els.zmultNumber, 'keydown', (e) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        this._commitZNumber();
      });
      on(this.els.zmultNumber, 'blur', () => {
        this._zTyping = false;
        this._syncZNumber();
      });
      this.els.presets.forEach((input) =>
        on(input, 'change', () => {
          if (input.checked) this._applyPreset(input.value);
        })
      );
      this.els.routeWidthInputs.forEach((input) =>
        on(input, 'change', () => {
          if (input.checked) this._onRouteWidth(input.value);
        })
      );
      on(this.els.sculpted, 'change', () => this._onSculpted(this.els.sculpted.checked));
      this.els.colourInputs.forEach((input) =>
        on(input, 'change', () => {
          if (!input.checked) return;
          this.state.colours[input.dataset.tsColour] = input.value.toUpperCase();
          this._applyColors();
          this._save();
        })
      );

      // --- resume (browser autosave) ---
      on(this.els.resumeContinue, 'click', () => this._resumeContinue());
      on(this.els.resumeFresh, 'click', () => this._resumeFresh());

      // --- colour code ---
      on(this.els.codeCopy, 'click', () => this._copyColourCode());
      on(this.els.codeApply, 'click', () => this._applyColourCode(this.els.codePaste?.value));
      on(this.els.codePaste, 'keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this._applyColourCode(this.els.codePaste.value);
        }
      });

      // --- bar ---
      on(this.els.back, 'click', () => {
        const i = STEPS.indexOf(this.state.step);
        if (i > 0) this.goTo(STEPS[i - 1], true);
      });
      on(this.els.primary, 'click', () => this._primary().run?.());
      on(this.els.toastClose, 'click', () => {
        this._showToast('');
        this.els.primary?.focus();
      });

      // --- confirm dialog ---
      if (this.els.confirm) {
        on(this.els.confirm, 'close', () => {
          if (this.els.confirm.returnValue === 'confirm') this._resetRoute();
        });
      }

      // --- stage size: keep the 2D route window and the map framing in step ---
      this._stageObserver = new ResizeObserver(() => this._onStageResize());
      this._stageObserver.observe(this.els.stage);

      window.addEventListener('pageshow', (e) => {
        // Returning from the cart via the back button restores this page from bfcache.
        if (e.persisted) this._render();
      }, opts);
    }

    _onTabKey(e) {
      const keys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
      if (!keys.includes(e.key)) return;
      e.preventDefault();
      const enabled = this.els.tabs.filter((t) => this._canEnter(t.dataset.tsTab));
      const current = enabled.indexOf(e.currentTarget);
      let next = current;
      if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = enabled.length - 1;
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (current - 1 + enabled.length) % enabled.length;
      else next = (current + 1) % enabled.length;
      const tab = enabled[next];
      if (!tab) return;
      this.goTo(tab.dataset.tsTab, false);
      tab.focus();
    }

    _acceptingFiles() {
      return !this._busy && this.state.step === 'route' && !this.state.route;
    }

    /* =====================================================================
     * Step machine
     * ===================================================================*/

    _canEnter(step) {
      const s = this.state;
      switch (step) {
        case 'route':
          return true;
        case 'product':
          return !!s.route;
        case 'map':
          return !!s.route && !!s.product;
        case 'customise':
          return !!s.route && !!s.product && !!this._built && !this._stale;
        default:
          return false;
      }
    }

    _stepDone(step) {
      switch (step) {
        case 'route':
          return !!this.state.route;
        case 'product':
          return !!this.state.product;
        case 'map':
          return !!this._built && !this._stale && !this._updateFailed;
        default:
          return false;
      }
    }

    goTo(step, focus = false) {
      if (!STEPS.includes(step) || !this._canEnter(step) || this._busy) return;
      if (this.state.step === step) {
        if (focus) this._focusPane(step);
        return;
      }
      this.state.step = step;
      // The restore notice (§E.3) has done its job once the updated design reaches Customise.
      if (step === 'customise') this._upgradeNotice = false;
      // A hint from the previous step is stale once the shopper moves on; errors stay (in the
      // status line: the toast by the button belongs to the step it was raised on).
      if (this.els.status && !this.els.status.hasAttribute('data-state')) this.setStatus('');
      this._showToast('');
      if (step === 'map' && !this._fit) this._autoFit({ user: false });
      if (step === 'map') {
        // A basemap that failed once (background tab, flaky network) gets one more try when
        // the shopper actually reaches the Map step, instead of a canvas for the whole visit.
        if (this._mapFailed && !this._mapRetried) {
          this._mapRetried = true;
          this._mapFailed = false;
        }
        this._ensureMap();
        this._schedulePrefetch();
        this._scheduleElevationPrefetch();
      }
      // §7 #3: the preview is settled on Customise: build the export for the cart in the background.
      if (step === 'customise') this._schedulePrebuild();
      this._render();
      this._save();
      this._emit('ttv:studio_step', { step });
      if (focus) this._focusPane(step);
    }

    _focusPane(step) {
      const pane = this.els.panes.find((p) => p.dataset.tsPane === step);
      if (!pane) return;
      const target = Array.from(pane.querySelectorAll('[data-ts-focus]')).find((el) => !el.closest('[hidden]'));
      if (this.els.panel) this.els.panel.scrollTop = 0;
      target?.focus({ preventScroll: true });
      if (window.matchMedia('(max-width: 989px)').matches) {
        const top = this.els.panel.getBoundingClientRect().top;
        const stageBottom = this.els.stage.getBoundingClientRect().bottom;
        if (top < stageBottom - 8 || top > window.innerHeight * 0.9) {
          const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          window.scrollTo({ top: Math.max(0, this.getBoundingClientRect().top + window.scrollY - this._headerOffset()), behavior: reduce ? 'auto' : 'smooth' });
        }
      }
    }

    _headerOffset() {
      const v = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--vst-header-group-h'));
      return Number.isFinite(v) ? v : 0;
    }

    /* =====================================================================
     * Rendering
     * ===================================================================*/

    _render() {
      const s = this.state;
      this.dataset.step = s.step;
      this.toggleAttribute('data-has-route', !!s.route);
      this.setAttribute('aria-busy', this._busy ? 'true' : 'false');

      // steps
      for (const tab of this.els.tabs) {
        const key = tab.dataset.tsTab;
        const selected = key === s.step;
        const enabled = this._canEnter(key);
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
        tab.setAttribute('aria-disabled', String(!enabled || (!!this._busy && !selected)));
        tab.classList.toggle('is-done', this._stepDone(key) && !selected);
      }
      for (const pane of this.els.panes) pane.hidden = pane.dataset.tsPane !== s.step;

      this._renderRoutePane();
      this._renderProductPane();
      this._renderMapPane();
      this._renderCustomisePane();
      this._renderBar();
      this._renderStage();
      this._renderIncomplete();
      this._emitPurchaseState();
    }

    _renderRoutePane() {
      const s = this.state;
      const picking = !!this._stravaPicking;
      if (this.els.entry) this.els.entry.hidden = !!s.route || picking;
      if (this.els.stravaPicker) this.els.stravaPicker.hidden = !picking || !!s.route;
      if (this.els.routeSummary) this.els.routeSummary.hidden = !s.route;
      if (this.els.file) this.els.file.disabled = !!this._busy;
      if (this.els.upload) this.els.upload.disabled = !!this._busy;
      if (this.els.sample) this.els.sample.disabled = !!this._busy;
      if (this.els.stravaConnect) this.els.stravaConnect.disabled = !!this._busy || !this._stravaReady;
      if (this.els.changeInline) this.els.changeInline.disabled = !!this._busy;
      if (!s.route) return;

      const { parsed, legs, source } = s.route;
      const st = this.els.stats;
      if (st.name) st.name.textContent = this._routeName() || '—';
      if (st.distance) st.distance.textContent = this._m('unit_km', { n: parsed.stats.distanceKm.toFixed(1) });
      if (st.span) {
        const b = parsed.bounds;
        const w = haversineKm(b.latMin, b.lonMin, b.latMin, b.lonMax);
        const h = haversineKm(b.latMin, b.lonMin, b.latMax, b.lonMin);
        st.span.textContent = this._m('span_dims', { w: w.toFixed(1), h: h.toFixed(1) });
      }
      if (st.points) st.points.textContent = parsed.stats.pointCount.toLocaleString();
      const grid = this._elevation;
      if (this.els.elevationRow) this.els.elevationRow.hidden = !grid && !this._buildMeta;
      if (st.elevation) {
        const m = this._buildMeta;
        if (m) st.elevation.textContent = `${Math.round(m.elevationMinM)}–${Math.round(m.elevationMaxM)} m`;
        else if (grid) st.elevation.textContent = `${Math.round(grid.stats.min)}–${Math.round(grid.stats.max)} m`;
      }
      if (this.els.routeSource) {
        const label =
          source === 'sample'
            ? this.els.routeSource.dataset.sample
            : source === 'strava'
            ? this.els.routeSource.dataset.strava
            : this.els.routeSource.dataset.gpx;
        this.els.routeSource.textContent = label || '';
        this.els.routeSource.hidden = !label;
      }
      void legs;
    }

    _renderProductPane() {
      for (const input of this.els.productInputs) {
        const p = this.products[input.value];
        input.checked = this.state.product === input.value;
        input.disabled = !p?.available || !!this._busy;
        input.closest('[data-ts-product-card]')?.classList.toggle('is-selected', input.checked);
      }
    }

    _renderMapPane() {
      const s = this.state;
      const fit = this._fit;
      const span = fit ? this._spanKm(fit) : null;

      if (this.els.panHint) this.els.panHint.hidden = !this._mapApi;
      if (this.els.autofit) {
        this.els.autofit.hidden = !this._mapApi;
        this.els.autofit.disabled = !!this._busy || this._fitMode === 'auto';
      }
      if (this.els.span) {
        this.els.span.textContent = span != null ? this.t.span.replace('[km]', formatKm(span)) : '';
      }

      // routes list
      if (this.els.routeList && s.route) {
        const legs = s.route.legs || [];
        if (this.els.routeCount) this.els.routeCount.textContent = `(${legs.length}/3)`;
        const html = legs
          .map((leg, i) => {
            const meta = [this._m('unit_km', { n: leg.distanceKm.toFixed(1) })];
            if (Number.isFinite(leg.gainM)) meta.push(this._m('unit_gain', { n: Math.round(leg.gainM).toLocaleString() }));
            const remove =
              legs.length > 1
                ? `<button type="button" class="ts__leg-remove" data-ts-remove-leg="${i}" aria-label="${escapeHtml(this.t.removeLeg.replace('[name]', leg.name))}"${this._busy ? ' disabled' : ''}>×</button>`
                : '';
            // Strava's brand rules: link back to Strava data with "View on Strava".
            const view = leg.stravaId
              ? ` · <a class="ts__leg-view" href="https://www.strava.com/activities/${encodeURIComponent(leg.stravaId)}" target="_blank" rel="noopener">${escapeHtml(this.t.strava?.view || 'View on Strava')}</a>`
              : '';
            return `<li class="ts__leg"><span class="ts__leg-swatch" aria-hidden="true"></span><span class="ts__leg-text"><span class="ts__leg-name">${escapeHtml(leg.name)}</span><span class="ts__leg-meta">${meta.join(' · ')}${view}</span></span>${remove}</li>`;
          })
          .join('');
        if (this.els.routeList.innerHTML !== html) this.els.routeList.innerHTML = html;
      }

      // features
      const buildingsBlocked = this._buildingsBlocked(span);
      const tooMany = this._buildingsTooMany();
      for (const [key, input] of Object.entries(this.els.features)) {
        input.checked = !!s.features[key];
        input.disabled = !!this._busy || (key === 'buildings' && (buildingsBlocked || tooMany) && !s.features.buildings);
      }
      const note = this.els.featureNotes.buildings;
      if (note) note.textContent = tooMany ? this._m('buildings_too_many') : buildingsBlocked ? this.t.buildingsOver : this.t.buildingsLimit;
      this._renderBuildingsStyle(span);
      if (this.els.upgradeNotice) this.els.upgradeNotice.hidden = !this._upgradeNotice;
      this._renderSnowField();

      // generate — a Customise rebuild that failed (_updateFailed) needs regenerating like a stale preview
      const check = this._canGenerate();
      const needsGen = !this._built || this._stale || this._updateFailed;
      // A current preview built without a map feature whose lookup ran out of time (§7 #4): the
      // shopper can go on (Next), or try that lookup again here.
      const retryLeftOut = !needsGen && this._leftOut().length > 0;
      if (this.els.generate) {
        this.els.generate.textContent = this._built ? this.t.regenerate : this.t.generate;
        this.els.generate.disabled = !!this._busy || !check.ok || !(needsGen || retryLeftOut);
        this.els.generate.hidden = !(needsGen || retryLeftOut);
      }
      if (this.els.dirty) this.els.dirty.hidden = !(this._built && needsGen);
      if (this.els.sea) {
        this.els.sea.hidden = !(this._seaDetected && !s.features.water);
        if (this.els.seaWater) this.els.seaWater.disabled = !!this._busy;
      }
    }

    _renderCustomisePane() {
      const s = this.state;
      if (this.els.backpiece) {
        this.els.backpiece.checked = !!s.backpiece;
        this.els.backpiece.disabled = !!this._busy && !!this._adding;
      }
      if (this.els.zmult) this.els.zmult.disabled = !!this._busy || !this._built;
      if (this.els.zmultNumber) {
        // Locked with the slider — except under the shopper's own hand during a height/snow
        // REBUILD, so a keyboard user keeps focus after Enter (the typed value is queued; see
        // _zOwed). Never during Generate or Add to cart.
        const num = this.els.zmultNumber;
        const focused = document.activeElement === num;
        num.disabled = !this._built || (!!this._busy && !(focused && this._rebuilding));
        this._syncZNumber();
      }
      this.els.presets.forEach((input) => {
        input.checked = input.value === s.preset;
        input.disabled = !!this._busy;
      });
      // Route width and Sculpted rebuild in place like the height slider: they stay usable during
      // that rebuild (the change is queued, and keyboard focus is kept), never during Generate or
      // Add to cart.
      const optsLocked = !!this._busy && !this._rebuilding;
      for (const input of this.els.routeWidthInputs) {
        input.checked = input.value === s.routeWidth;
        input.disabled = optsLocked;
      }
      if (this.els.sculpted) {
        this.els.sculpted.checked = !!s.sculpted;
        this.els.sculpted.disabled = optsLocked;
      }
      this._renderRouteWidthHint();
      for (const row of this.els.colourRows) {
        const key = row.dataset.tsColourRow;
        // Snow that the current build did not print has no colour to choose (_snowNotPrinted).
        row.hidden = !(key === 'terrain' || key === 'route' || (s.features[key] && !(key === 'snow' && this._snowNotPrinted())));
      }
      if (this.els.height) {
        const m = this._buildMeta;
        this.els.height.textContent = m ? this.t.heightReadout.replace('[mm]', m.maxZmm.toFixed(1)) : '';
      }
      if (this.els.codeApply) this.els.codeApply.disabled = !!this._busy;
      this._updateColourBudget({ silent: true });
    }

    _renderBar() {
      const s = this.state;
      const product = this.products[s.product];
      if (this.els.barTitle) this.els.barTitle.textContent = product ? product.title : this.t.studio;
      if (this.els.barPrice) this.els.barPrice.textContent = product ? product.priceLabel : this.els.barPrice.dataset.fromLabel || '';
      if (this.els.back) {
        this.els.back.hidden = s.step === 'route';
        this.els.back.disabled = !!this._busy;
      }
      if (this.els.changeRoute) {
        this.els.changeRoute.hidden = !s.route;
        this.els.changeRoute.disabled = !!this._busy;
      }
      const primary = this._primary();
      if (this.els.primary) {
        if (this.els.primary.textContent !== primary.label) this.els.primary.textContent = primary.label;
        this.els.primary.disabled = !!primary.disabled;
        this.els.primary.setAttribute('aria-busy', this._busy ? 'true' : 'false');
        this.els.primary.dataset.action = primary.action || '';
      }
    }

    _primary() {
      const s = this.state;
      const t = this.t;
      if (this._adding) return { label: t.adding, disabled: true, action: 'adding' };
      switch (s.step) {
        case 'route':
          return { label: t.next, disabled: !s.route || this._busy, action: 'next', run: () => this.goTo('product', true) };
        case 'product':
          return { label: t.next, disabled: !s.product || this._busy, action: 'next', run: () => this.goTo('map', true) };
        case 'map':
          if (!this._built || this._stale || this._updateFailed) {
            return {
              label: this._built ? t.regenerate : t.generate,
              disabled: this._busy || !this._canGenerate().ok,
              action: 'generate',
              run: () => this._generate(),
            };
          }
          return { label: t.next, disabled: this._busy, action: 'next', run: () => this.goTo('customise', true) };
        case 'customise': {
          const product = this.products[s.product];
          if (this._model && this._addedModelKey === this._model.key) {
            return { label: t.viewCart, disabled: false, action: 'cart', run: () => window.location.assign(this.config.cartUrl) };
          }
          if (this._updateFailed && !this._busy) {
            // The last slider/preset rebuild failed: offer the retry where the shopper is.
            return { label: t.regenerate, disabled: !this._canGenerate().ok, action: 'generate', run: () => this._retryPending() };
          }
          if (!product || !product.available) return { label: t.soldOut, disabled: true, action: 'unavailable' };
          if (!this.config.workerUrl) return { label: t.notConfigured, disabled: true, action: 'unconfigured' };
          // Phase 0 #7: an incomplete preview is never carted. While the background retries run,
          // Add to cart stays disabled; once they are over, Update preview asks again here.
          if (this._built && this._leftOut().length && !this._busy && !this._leftOutRetrying()) {
            return { label: t.regenerate, disabled: !this._canGenerate().ok, action: 'generate', run: () => this._retryPending() };
          }
          return { label: t.add, disabled: this._busy || !this.purchaseState.ready, action: 'add', run: () => this._addToCart() };
        }
        default:
          return { label: t.next, disabled: true };
      }
    }

    _stageMode() {
      const s = this.state;
      if (!s.route) return 'entry';
      if (s.step === 'customise' && this._built) return '3d';
      if (s.step === 'map' && this._built && s.view === '3d') return '3d';
      return this._mapApi && !this._mapFailed ? 'map' : 'canvas';
    }

    _renderStage() {
      const mode = this._stageMode();
      const entering = mode !== this._lastStageMode;
      this._lastStageMode = mode;
      this.dataset.stage = mode;
      if (this._mapApi) {
        const interactive = this.state.step === 'map' && !this._busy;
        if (interactive !== this._mapInteractive) {
          this._mapInteractive = interactive;
          this._mapApi.setInteractive(interactive);
        }
      }
      if (this.els.entryArt) this.els.entryArt.hidden = mode !== 'entry';
      if (this.els.routeCanvas) this.els.routeCanvas.hidden = mode !== 'canvas';
      if (this.els.map) this.els.map.hidden = mode !== 'map';
      if (this.els.canvas) this.els.canvas.hidden = mode !== '3d';
      if (this.els.stageToggle) {
        this.els.stageToggle.hidden = !(this.state.step === 'map' && this._built);
        this.els.viewButtons.forEach((btn) => {
          const pressed = (btn.dataset.tsView === '3d') === (mode === '3d');
          btn.setAttribute('aria-pressed', String(pressed));
          btn.disabled = !!this._busy;
        });
      }
      if (mode === 'canvas') this._drawRouteCanvas();
      if (mode === 'map' && entering) this._mapApi?.resize?.();
      if (mode === '3d') this._preview?.setBackpiece?.(!!this.state.backpiece);
    }

    _emitPurchaseState() {
      const ps = this.purchaseState;
      const key = JSON.stringify(ps);
      if (key === this._lastPurchaseState) return;
      this._lastPurchaseState = key;
      this._emit('ttv:purchase-state', ps);
    }

    get purchaseState() {
      return {
        busy: !!this._busy,
        started: !!this.state?.route,
        ready: !!(
          this._built &&
          !this._designDirty &&
          this._built.meta?.audit?.ok &&
          this._colourBudget().valid &&
          this.products?.[this.state?.product] &&
          // Phase 0 #7: a preview missing a map feature the shopper chose cannot be ordered.
          !this._leftOut().length
        ),
        available: !!this.products?.[this.state?.product]?.available,
        configured: !!this.config?.workerUrl,
      };
    }

    /** v12 name: anything that would make the uploaded model differ from the preview. */
    get _designDirty() {
      return !!(this._stale || this._pending || (this._built && !this._built.meta?.audit?.ok));
    }

    /* =====================================================================
     * Route: GPX, sample, (Strava in strava-client.js)
     * ===================================================================*/

    _gpxModule() {
      if (!this._gpxModulePromise) this._gpxModulePromise = import(this.config.gpxModuleUrl);
      return this._gpxModulePromise;
    }

    _elevationModule() {
      if (!this._elevationModulePromise) this._elevationModulePromise = import(this.config.elevationModuleUrl);
      return this._elevationModulePromise;
    }

    /** The geometry engine on the main thread: only for the snow pre-check (winterSnowAltitude, SNOW). */
    _geometryModule() {
      if (!this._geometryModulePromise) this._geometryModulePromise = import(this.config.geometryModuleUrl);
      return this._geometryModulePromise;
    }

    async _loadFiles(files) {
      if (this._busy) return;
      const gpx = files.filter((f) => /\.gpx$/i.test(f.name || ''));
      if (gpx.length !== files.length) {
        this.setStatus(this._m('gpx_extension'), 'error');
        return;
      }
      if (files.length > 3) {
        this.setStatus(this._m('gpx_too_many'), 'error');
        return;
      }
      if (files.reduce((n, f) => n + f.size, 0) > this.config.maxGpxBytes) {
        this.setStatus(
          files.length === 1
            ? this._m('gpx_too_large')
            : this._m('gpx_too_many'),
          'error'
        );
        return;
      }
      const loadId = ++this._loadId;
      this.setStatus(files.length === 1 ? this._m('reading_file', { name: files[0].name }) : this._m('reading_files'));
      let sources;
      try {
        sources = await Promise.all(files.map(async (f) => ({ name: f.name, text: await f.text() })));
      } catch (e) {
        if (loadId === this._loadId) this.setStatus(this._m('gpx_read_failed'), 'error');
        return;
      }
      if (loadId !== this._loadId) return;
      await this._acceptSources(sources, 'gpx', { loadId });
    }

    async _loadSample() {
      if (this._busy || !this.config.sampleUrl) return;
      const loadId = ++this._loadId;
      this.setStatus(this._m('sample_loading'));
      try {
        const res = await fetch(this.config.sampleUrl, { cache: 'force-cache' });
        if (!res.ok) throw new Error(`sample ${res.status}`);
        const text = await res.text();
        if (loadId !== this._loadId) return;
        // The sample is coastal: Water keeps the sea flat instead of modelling the sea floor.
        if (!this.state.features.water) {
          this.state.features.water = true;
          this.state.preset = 'custom';
          this._applyColors({ quiet: true });
        }
        await this._acceptSources([{ name: `${this._m('sample_name')}.gpx`, text }], 'sample', { loadId });
      } catch (err) {
        console.error('[terrain-studio] sample failed', err);
        if (loadId === this._loadId) this.setStatus(this._m('sample_failed'), 'error');
      }
    }

    /**
     * Validate and adopt one to three GPX texts as the route. Strava legs arrive here too,
     * already converted to GPX, so every source takes the same parser path.
     * @param {{name:string,text:string,meta?:object}[]} sources
     */
    async _acceptSources(sources, source, opts = {}) {
      const loadId = opts.loadId ?? ++this._loadId;
      let mod;
      try {
        mod = await this._gpxModule();
      } catch (err) {
        console.error('[terrain-studio] GPX module failed', err);
        if (!opts.silent) this.setStatus(this._m('reader_failed'), 'error');
        return false;
      }
      if (loadId !== this._loadId) return false;

      let text;
      let parsed;
      let legs;
      try {
        const plain = sources.map(({ name, text: t }) => ({ name, text: t }));
        let event;
        if (typeof mod.parseGpxEvent === 'function') {
          // §7 #13: every file goes through the XML parser once, and the joined event is read
          // from those documents (the same points, text and stats as parsing it again).
          event = mod.parseGpxEvent(plain);
        } else {
          const one = sources.map((src) => mod.parseGpxText(src.text));
          const joined = sources.length === 1 ? sources[0].text : mod.combineGpxTexts(plain);
          event = { legs: one, text: joined, parsed: sources.length === 1 ? one[0] : mod.parseGpxText(joined) };
        }
        legs = event.legs.map((p, i) => {
          const src = sources[i];
          return {
            name: src.meta?.name || p.stats.name || stripExt(src.name) || this._m('leg_name_fallback'),
            distanceKm: Number.isFinite(src.meta?.distanceKm) ? src.meta.distanceKm : p.stats.distanceKm,
            gainM: Number.isFinite(src.meta?.gainM) ? src.meta.gainM : elevationGain(p.points),
            stravaId: src.meta?.stravaId || null,
          };
        });
        text = event.text;
        parsed = event.parsed;
        if (sources.length > 1) {
          // The parser names a combined file 'Multi-sport event' (engine text); shoppers see the locale's.
          if (!parsed.stats.name || parsed.stats.name === 'Multi-sport event') parsed.stats.name = this._m('multi_sport_name');
        }
      } catch (err) {
        if (loadId !== this._loadId) return false;
        if (err && err.name === 'GpxError') this.setStatus(err.message, 'error');
        else {
          console.error('[terrain-studio] GPX parse failed', err);
          this.setStatus(
            sources.length > 1
              ? this._m('gpx_combine_failed')
              : this._m('gpx_parse_failed'),
            'error'
          );
        }
        return false;
      }
      if (loadId !== this._loadId) return false;

      // A route too large for one tile is no longer refused here (§7 #5, #6): auto-fit frames a
      // 150 km section of it, measured exactly as the framing check measures (the tile span), and
      // the shopper pans the map to choose the section. So nothing is accepted and then blocked.
      this._resetDesign();
      // A route the shopper has just given (not a restore or a removed leg) takes the current cover.
      if (!opts.silent) this.state.routeVersion = ROUTE_VERSION;
      this.state.route = {
        source,
        sources: sources.map(({ name, text: t, meta }) => ({ name, text: t, meta: meta || null })),
        legs,
        gpxText: text,
        parsed,
      };
      this._fitMode = 'auto';
      this._showWarnings(this._routeWarnings());
      await this._autoFit({ user: false, silent: true });
      if (loadId !== this._loadId) return false;
      if (this._mapApi) this._mapApi.setRoute(parsed.points, this.state.colours.route);
      else this._ensureMap();

      if (!opts.silent) {
        // A new route while "Continue your last design" is offered means starting fresh.
        if (this._resumeData) {
          this._resumeData = null;
          if (this.els.resume) this.els.resume.hidden = true;
          this._emit('ttv:studio_resume', { accepted: false });
        }
        this.state.step = 'product';
        this._emit('ttv:studio_route', { source });
        const name = this._routeName() || this._m('route_fallback');
        const section = this._clampedSection();
        this.setStatus(
          section
            ? this._m('route_loaded_section', { name, km: SPAN_HARD_MAX_KM, pct: section.pct })
            : this._m('route_loaded', { name })
        );
      }
      this._render();
      this._save();
      if (!opts.silent) this._focusPane('product');
      return true;
    }

    _routeName() {
      const r = this.state.route;
      if (!r) return null;
      if (r.legs?.length > 1) return r.legs.map((l) => l.name).join(' + ');
      return r.legs?.[0]?.name || r.parsed?.stats?.name || null;
    }

    _routeWarnings() {
      const r = this.state.route;
      if (!r) return [];
      // The studio reports the TILE span itself (framing can differ from the route bbox).
      return (r.parsed.warnings || []).filter((w) => !/spans about/i.test(w));
    }

    async _removeLeg(index) {
      const r = this.state.route;
      if (this._busy || !r || r.sources.length < 2 || !r.sources[index]) return;
      const sources = r.sources.filter((_, i) => i !== index);
      const step = this.state.step;
      const ok = await this._acceptSources(sources, r.source, { silent: true });
      if (ok) {
        this.state.step = this._canEnter(step) ? step : 'map';
        this.setStatus(this._m('route_updated'));
        this._render();
        this._save();
      }
    }

    _changeRoute() {
      if (this._busy || !this.state.route) return;
      if (this._built) {
        if (this.els.confirm && typeof this.els.confirm.showModal === 'function') {
          this.els.confirm.returnValue = '';
          this.els.confirm.showModal();
          return;
        }
        if (!window.confirm(this.t.confirmChange)) return;
      }
      this._resetRoute();
    }

    _resetRoute() {
      this._loadId++;
      this._resetDesign();
      this.state.route = null;
      this.state.step = 'route';
      // A new route starts with the Buildings style preselected by its span again, and the
      // current route cover.
      this.state.buildingsMode = null;
      this.state.buildingsModeSource = 'auto';
      this.state.routeVersion = ROUTE_VERSION;
      // A new route may be coastal again: the automatic Water (§7 #7) is offered afresh.
      this._waterUserOff = false;
      this._upgradeNotice = false;
      this._stravaPicking = false;
      this._showWarnings([]);
      this.setStatus('');
      this._clearSaved();
      this._clearAutosave();
      this._save();
      this._render();
      this._focusPane('route');
    }

    /** Everything derived from the route/framing; product and colour choices survive. */
    _resetDesign() {
      clearTimeout(this._zTimer);
      this._elevation = null;
      this._elevationKey = null;
      this._mapFeatures = null;
      this._mapRevision = (this._mapRevision || 0) + 1;
      this._geometryCache = new Map();
      this._built = null;
      this._buildMeta = null;
      this._model = null;
      this._addedModelKey = null;
      this._stale = false;
      this._pending = false;
      this._updateFailed = false;
      this._fit = null;
      this._fitMode = 'auto';
      this._fitClamped = null;
      this._seaDetected = false;
      this._lastBuildExport = false;
      this._lastBuildFast = false;
      // The last good build belongs to this route and framing (§7 #1).
      this._lastGood = null;
      this._exportTrapKeys = null;
      this._autoWaterKey = null;
      clearTimeout(this._prefetchTimer);
      this._cancelPrefetch();
      // A new route (or none): its framings' elevation and any prebuilt export are not this design's.
      this._cancelElevationPrefetch();
      this._cancelPrebuild('route');
      this.state.view = 'map';
      this._mapApi?.setRoute?.(null);
    }

    /* =====================================================================
     * Strava — vestige-strava worker via strava-client.js. The browser holds only an opaque
     * session id; activities become GPX text and take the normal route path.
     * ===================================================================*/

    async _stravaModule() {
      if (!this._stravaModPromise) this._stravaModPromise = import(this.config.stravaModuleUrl);
      const mod = await this._stravaModPromise;
      this._stravaLib = mod;
      return mod;
    }

    /**
     * Connect with Strava is rendered with its box reserved (invisible, disabled) and revealed only
     * when the worker says it is fully configured; otherwise it collapses. The lede and the Strava
     * privacy line follow the same signal, so the page never promises Strava without the button.
     */
    async _initStrava() {
      const c = this.config;
      const btn = this.els.stravaConnect;
      if (!c.stravaWorkerUrl || !c.stravaModuleUrl) {
        if (btn) btn.hidden = true;
        return;
      }
      try {
        const mod = await this._stravaModule();
        this._strava ||= mod.createStravaClient(c.stravaWorkerUrl);
        const h = await this._strava.health();
        this._stravaReady = !!(h && h.configured);
      } catch (e) {
        this._stravaReady = false;
      }
      if (btn) {
        btn.hidden = !this._stravaReady;
        btn.classList.remove('ts__strava--pending');
        btn.disabled = !this._stravaReady || !!this._busy;
        if (this._stravaReady) {
          btn.removeAttribute('aria-hidden');
          btn.removeAttribute('tabindex');
        }
      }
      // The lede must only promise Strava when the button is actually there.
      const lede = this.querySelector('[data-ts-lede]');
      if (lede) {
        const text = this._stravaReady ? lede.dataset.withStrava : lede.dataset.withoutStrava;
        if (text && lede.textContent !== text) lede.textContent = text;
      }
      // …and so must the privacy sentence about Strava.
      const privacy = this.querySelector('[data-ts-privacy]');
      if (privacy) privacy.hidden = !this._stravaReady;
    }

    _stravaSid() {
      try {
        return sessionStorage.getItem(SID_KEY);
      } catch (e) {
        return null;
      }
    }

    _setStravaSid(sid) {
      try {
        if (sid) sessionStorage.setItem(SID_KEY, sid);
        else sessionStorage.removeItem(SID_KEY);
      } catch (e) {
        /* storage unavailable */
      }
    }

    /**
     * Read and strip #strava=<sid> / #strava_error=<code> from the URL (called synchronously in
     * connectedCallback, before the asynchronous restore). The value is consumed by _afterRestore.
     */
    _takeStravaFragment() {
      const hash = window.location.hash || '';
      const ok = hash.match(/^#strava=([A-Za-z0-9_-]{40,64})$/);
      const bad = hash.match(/^#strava_error=([a-z_]+)$/);
      if (!ok && !bad) return null;
      try {
        history.replaceState(history.state, '', window.location.pathname + window.location.search);
      } catch (e) {
        /* the value is still consumed below; only the URL keeps the fragment */
      }
      return ok ? { sid: ok[1] } : { error: bad[1] };
    }

    /** After restore: act on the return from Strava taken from the URL in connectedCallback. */
    async _afterRestore() {
      const ret = this._stravaReturn;
      this._stravaReturn = null;
      await (this._stravaInit || this._initStrava());
      if (ret && ret.sid) {
        this._setStravaSid(ret.sid);
        this._emit('ttv:strava_connect', { status: 'ok' });
        if (!this.state.route) {
          this.state.step = 'route';
          await this._stravaOpenPicker();
        }
      } else if (ret && ret.error) {
        this._emit('ttv:strava_connect', { status: ret.error });
        this.setStatus(this._stravaError({ code: ret.error }), 'error');
      }
    }

    async _stravaConnect() {
      if (this._busy) return;
      if (!this._strava) await (this._stravaInit || this._initStrava());
      if (!this._strava) return;
      if (this._stravaSid()) return this._stravaOpenPicker({ fromConnect: true });
      this._stravaRedirect();
    }

    _stravaRedirect() {
      // The design survives the round trip through Strava (sessionStorage).
      this._saveNow();
      const back = window.location.origin + window.location.pathname + window.location.search;
      window.location.assign(this._strava.authorizeUrl(back));
    }

    /**
     * @param {{fromConnect?: boolean}} [opts] fromConnect: the shopper pressed Connect. A sid left in
     *   this tab from an earlier visit may have expired on the worker; then go straight to Strava
     *   instead of showing "expired" and asking for a second click. The return path (_afterRestore)
     *   never redirects, so a freshly minted sid that fails cannot loop.
     */
    async _stravaOpenPicker({ fromConnect = false } = {}) {
      const sid = this._stravaSid();
      if (!sid || !this._strava) return;
      this._stravaPicking = true;
      this._stravaState = { rows: [], page: 0, done: false, filter: 'all', query: '', multi: false, selected: [], me: null, loading: false, busy: false };
      this._buildStravaPicker();
      this._render();
      this._focusPane('route');
      try {
        const [me] = await Promise.all([this._strava.me(sid), this._stravaLoadPage()]);
        if (this._stravaState) this._stravaState.me = me;
      } catch (err) {
        if (fromConnect && err && err.code === 'session') {
          this._setStravaSid(null);
          this._stravaPicking = false;
          this._stravaState = null;
          this._stravaRedirect();
          return;
        }
        this._stravaFail(err);
        return;
      }
      this._renderStravaPicker();
    }

    async _stravaLoadPage() {
      const st = this._stravaState;
      const sid = this._stravaSid();
      if (!st || st.loading || st.done || !sid) return;
      st.loading = true;
      this._renderStravaPicker();
      try {
        const rows = await this._strava.activities(sid, st.page + 1, 30);
        if (this._stravaState !== st) return;
        st.page += 1;
        const seen = new Set(st.rows.map((r) => String(r.id)));
        st.rows.push(...(rows || []).filter((r) => !seen.has(String(r.id))));
        if (!rows || !rows.length) st.done = true;
      } finally {
        st.loading = false;
        this._renderStravaPicker();
      }
    }

    /** The picker's fixed frame (built once per opening; the list re-renders in place). */
    _buildStravaPicker() {
      const box = this.els.stravaPicker;
      if (!box) return;
      const t = this.t.strava || {};
      const chips = ['all', 'run', 'ride', 'hike', 'swim', 'other']
        .map((k) => `<button type="button" class="ts__chip" data-ts-filter="${k}" aria-pressed="${k === 'all'}">${escapeHtml(t[k] || k)}</button>`)
        .join('');
      box.innerHTML =
        `<div class="ts__picker-head"><h2 class="ts__h2" tabindex="-1" data-ts-focus data-ts-strava-who></h2>` +
        `<button type="button" class="ts__link" data-ts-strava-disconnect>${escapeHtml(t.disconnect || 'Disconnect')}</button></div>` +
        `<img class="ts__powered" src="${escapeHtml(this.dataset.stravaPoweredUrl || '')}" width="148" height="15" alt="${escapeHtml(t.powered_alt || 'Powered by Strava')}">` +
        `<div class="ts__chips" role="group" aria-label="${escapeHtml(t.filter_label || 'Filter activities')}">${chips}</div>` +
        `<input type="search" class="ts__search" data-ts-strava-search placeholder="${escapeHtml(t.search || 'Search')}" aria-label="${escapeHtml(t.search || 'Search')}">` +
        `<label class="ts__check"><input type="checkbox" data-ts-strava-multi> <span>${escapeHtml(t.multi || '')}</span></label>` +
        // Not a live region: re-rendering the list would make screen readers read every row
        // again on each filter or selection. A short hidden status announces the count instead.
        `<ul class="ts__acts" data-ts-strava-list></ul>` +
        `<p class="visually-hidden" role="status" data-ts-strava-count></p>` +
        `<button type="button" class="ts__chip-btn ts__more" data-ts-strava-more>${escapeHtml(t.load_more || 'Load more')}</button>` +
        `<button type="button" class="button button--primary ts__use" data-ts-strava-use disabled>${escapeHtml(t.use || 'Use this route')}</button>` +
        `<button type="button" class="ts__link" data-ts-strava-back>${escapeHtml(t.back || 'Back')}</button>`;
      if (!box._tsBound) {
        box._tsBound = true;
        box.addEventListener('click', (e) => this._onStravaClick(e));
        box.addEventListener('input', (e) => {
          if (e.target.matches('[data-ts-strava-search]') && this._stravaState) {
            this._stravaState.query = e.target.value.trim().toLowerCase();
            this._renderStravaList();
          }
        });
        box.addEventListener('change', (e) => this._onStravaChange(e));
      }
    }

    _onStravaClick(e) {
      const st = this._stravaState;
      const chip = e.target.closest('[data-ts-filter]');
      if (chip && st) {
        st.filter = chip.dataset.tsFilter;
        this._renderStravaPicker();
        return;
      }
      if (e.target.closest('[data-ts-strava-more]')) this._stravaLoadPage().catch((err) => this._stravaFail(err));
      else if (e.target.closest('[data-ts-strava-use]')) this._stravaUse();
      else if (e.target.closest('[data-ts-strava-back]')) {
        this._stravaPicking = false;
        this._render();
        this._focusPane('route');
      } else if (e.target.closest('[data-ts-strava-disconnect]')) this._stravaDisconnect();
    }

    _onStravaChange(e) {
      const st = this._stravaState;
      if (!st) return;
      if (e.target.matches('[data-ts-strava-multi]')) {
        st.multi = e.target.checked;
        if (!st.multi) st.selected = st.selected.slice(0, 1);
        this._renderStravaList();
        return;
      }
      const input = e.target.closest('[data-ts-activity]');
      if (!input) return;
      const id = input.value;
      if (!st.multi) st.selected = input.checked ? [id] : [];
      else if (input.checked) {
        if (st.selected.length >= 3) {
          input.checked = false;
          this.setStatus(this.t.strava?.max_legs || 'Up to three legs.', 'error');
          return;
        }
        st.selected.push(id);
      } else st.selected = st.selected.filter((x) => x !== id);
      this._renderStravaList();
    }

    _renderStravaPicker() {
      const box = this.els.stravaPicker;
      const st = this._stravaState;
      if (!box || !st || !box.firstChild) return;
      const t = this.t.strava || {};
      const who = box.querySelector('[data-ts-strava-who]');
      if (who) who.textContent = st.me ? (t.connected_as || 'Connected as [name]').replace('[name]', st.me.firstname || 'you') : t.loading || '…';
      box.querySelectorAll('[data-ts-filter]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.tsFilter === st.filter)));
      this._renderStravaList();
    }

    _renderStravaList() {
      const box = this.els.stravaPicker;
      const st = this._stravaState;
      const lib = this._stravaLib;
      if (!box || !st || !lib) return;
      const t = this.t.strava || {};
      const list = box.querySelector('[data-ts-strava-list]');
      const rows = st.rows.filter(
        (r) => (st.filter === 'all' || lib.sportGroup(r.sport_type) === st.filter) && (!st.query || String(r.name || '').toLowerCase().includes(st.query))
      );
      const type = st.multi ? 'checkbox' : 'radio';
      const fmtDate = (iso) => {
        const d = new Date(iso);
        return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { timeZone: 'UTC', day: 'numeric', month: 'short', year: 'numeric' });
      };
      const sport = (s) => String(s || '').replace(/([a-z])([A-Z])/g, '$1 $2');
      const count = box.querySelector('[data-ts-strava-count]');
      if (count) {
        const announce = st.loading && !rows.length ? t.loading || 'Loading…' : (t.count || '[count] activities shown').replace('[count]', String(rows.length));
        if (count.textContent !== announce) count.textContent = announce;
      }
      list.innerHTML = rows.length
        ? rows
            .map((r) => {
              const id = String(r.id);
              const checked = st.selected.includes(id);
              const meta = [sport(r.sport_type), fmtDate(r.start_date_local || r.start_date), this._m('unit_km', { n: (r.distance / 1000).toFixed(1) })];
              if (Number.isFinite(r.total_elevation_gain)) meta.push(this._m('unit_gain', { n: Math.round(r.total_elevation_gain) }));
              return (
                `<li class="ts__act${r.has_gps ? '' : ' is-disabled'}${checked ? ' is-selected' : ''}">` +
                `<label class="ts__act-main"><input type="${type}" name="ts-strava-${this.config.sectionId}" value="${escapeHtml(id)}" data-ts-activity${checked ? ' checked' : ''}${r.has_gps && !st.busy ? '' : ' disabled'}>` +
                `<canvas class="ts__act-thumb" width="64" height="64" data-ts-thumb="${escapeHtml(id)}" aria-hidden="true"></canvas>` +
                `<span class="ts__act-text"><span class="ts__act-name">${escapeHtml(r.name || 'Activity')}</span>` +
                `<span class="ts__act-meta">${escapeHtml(meta.join(' · '))}${r.has_gps ? '' : ` · ${escapeHtml(t.no_gps || 'No GPS')}`}</span></span></label>` +
                `<a class="ts__act-view" href="https://www.strava.com/activities/${encodeURIComponent(id)}" target="_blank" rel="noopener">${escapeHtml(t.view || 'View on Strava')}</a></li>`
              );
            })
            .join('')
        : `<li class="ts__act-empty">${escapeHtml(st.loading ? t.loading || 'Loading…' : t.empty || '')}</li>`;
      const colour = this.state.colours.route || '#C12E1F';
      list.querySelectorAll('[data-ts-thumb]').forEach((cv) => {
        const r = st.rows.find((x) => String(x.id) === cv.dataset.tsThumb);
        if (r) lib.drawRouteThumb(cv, lib.decodePolyline(r.summary_polyline), colour);
      });
      const more = box.querySelector('[data-ts-strava-more]');
      if (more) {
        more.hidden = st.done;
        more.disabled = st.loading || st.busy;
        more.textContent = st.loading && st.rows.length ? t.loading || 'Loading…' : t.load_more || 'Load more';
      }
      const use = box.querySelector('[data-ts-strava-use]');
      if (use) {
        use.disabled = !st.selected.length || st.busy || this._busy;
        use.textContent = st.selected.length > 1 ? (t.use_many || 'Use these [count] legs').replace('[count]', String(st.selected.length)) : t.use || 'Use this route';
        use.setAttribute('aria-busy', st.busy ? 'true' : 'false');
      }
    }

    async _stravaUse() {
      const st = this._stravaState;
      const sid = this._stravaSid();
      if (!st || !st.selected.length || st.busy || this._busy || !sid) return;
      const lib = this._stravaLib;
      st.busy = true;
      this._renderStravaList();
      this.setStatus(this.t.strava?.fetching || 'Fetching your route…');
      try {
        const sources = [];
        for (const id of st.selected) {
          // One at a time: kind to Strava's rate limits, and the order of legs is kept.
          const route = await this._strava.route(sid, id);
          const row = st.rows.find((r) => String(r.id) === String(id)) || {};
          const name = route.name || row.name || 'Strava activity';
          sources.push({
            name: `${name}.gpx`,
            text: lib.routeToGpx({ ...route, name, sport_type: route.sport_type || row.sport_type, start_date: route.start_date || row.start_date }),
            meta: {
              name,
              distanceKm: Number(route.distance ?? row.distance) / 1000,
              gainM: Number(route.total_elevation_gain ?? row.total_elevation_gain),
              stravaId: String(id),
            },
          });
        }
        const ok = await this._acceptSources(sources, 'strava');
        if (ok) this._stravaPicking = false;
      } catch (err) {
        this._stravaFail(err);
      } finally {
        if (this._stravaState === st) st.busy = false;
        this._renderStravaList();
        this._render();
      }
    }

    async _stravaDisconnect() {
      const sid = this._stravaSid();
      this._setStravaSid(null);
      this._stravaPicking = false;
      this._stravaState = null;
      this._render();
      this._focusPane('route');
      if (sid && this._strava) {
        try {
          await this._strava.disconnect(sid);
        } catch (e) {
          /* the local session is gone either way */
        }
      }
    }

    _stravaFail(err) {
      if (err && err.code === 'session') {
        this._setStravaSid(null);
        this._stravaPicking = false;
        this._stravaState = null;
      }
      this.setStatus(this._stravaError(err), 'error');
      this._render();
      this._renderStravaPicker();
    }

    _stravaError(err) {
      const t = this.t.strava || {};
      switch (err && err.code) {
        case 'denied':
          return t.err_denied;
        case 'scope':
          return t.err_scope;
        case 'unavailable':
          return t.err_unavailable;
        case 'session':
          return t.err_session;
        case 'rate_limited':
          return t.err_rate;
        case 'no_gps':
          return t.err_nogps;
        case 'network':
          return t.err_network;
        default:
          return t.err_failed;
      }
    }

    /* =====================================================================
     * Product
     * ===================================================================*/

    _productKeyFor(value) {
      const v = String(value || '').toLowerCase();
      if (!v) return null;
      for (const [key, p] of Object.entries(this.products)) {
        if (v === key || v === p.handle) return key;
      }
      return null;
    }

    _selectProduct(key) {
      const p = this.products[key];
      if (!p || !p.available || this._busy) {
        this._render();
        return;
      }
      if (this.state.product === key) return;
      this.state.product = key;
      this._emit('ttv:studio_product', { handle: p.handle });
      this._render();
      this._save();
    }

    /* =====================================================================
     * Framing (auto-fit here; map framing in terrain-map.js)
     * ===================================================================*/

    async _autoFit({ user = false, silent = false } = {}) {
      const r = this.state.route;
      if (!r) return;
      const mod = await this._elevationModule();
      if (this.state.route !== r) return;
      let fit = mod.makeFitTransform(mod.padBounds(r.parsed.bounds, 0.08), this.config.sideMm);
      // §7 #5 / #6: auto-fit never frames more than a tile can print. A route whose box needs a
      // wider tile gets the largest printable one (150 km, a hair under so the framing check
      // passes), placed where it covers the most of the route (a loop or an L-shaped route can
      // miss its own bounding-box centre); the shopper pans to choose another section. Routes
      // that fit are framed exactly as before.
      const span = this._spanKm(fit);
      this._fitClamped = null;
      if (span > SPAN_HARD_MAX_KM) {
        fit = this._bestSectionFit(fit);
        this._fitClamped = { key: fitKey(fit), coverage: this._coverage(fit) };
      }
      // Re-framing is shown on the map, not on a preview that no longer matches.
      if (user) this.state.view = 'map';
      this._setFit(fit, 'auto', { fromMap: false, silent });
      if (this._mapApi) this._mapApi.showFit(fit);
      if (user) {
        const section = this._clampedSection();
        this.setStatus(section ? this._m('route_section', { km: SPAN_HARD_MAX_KM, pct: section.pct }) : this._m('route_framed'));
        this._render();
      }
    }

    /**
     * The 150 km framing (a hair under, so the framing check passes) that holds the most route
     * points. Candidate centres: the route box centre and up to 240 route points evenly along the
     * file; coverage is counted on up to 3,000 evenly spaced points. Ties keep the candidate
     * nearest the box centre, so the choice is deterministic.
     */
    _bestSectionFit(boxFit) {
      const pts = this.state.route.parsed.points;
      const R = this.config.sideMm;
      const a = (R * Math.sqrt(3)) / 2;
      const merc = (list) => list.map((p) => [lonToMercX(p.lon), latToMercY(p.lat)]);
      const every = (n) => Math.max(1, Math.ceil(pts.length / n));
      const sample = merc(pts.filter((_, i) => i % every(3000) === 0));
      const candidates = [[boxFit.mcx, boxFit.mcy], ...merc(pts.filter((_, i) => i % every(240) === 0))];
      const kFor = (latMid) => {
        const metresPerMercUnit = 2 * Math.PI * EARTH_R * Math.cos((latMid * Math.PI) / 180);
        return (2 * R * metresPerMercUnit) / (1000 * SPAN_HARD_MAX_KM * (1 - 1e-6));
      };
      let best = null;
      for (const [mcx, mcy] of candidates) {
        const latMid = mercYToLat(mcy);
        const k = kFor(latMid);
        let inside = 0;
        for (const [mx, my] of sample) {
          const x = (mx - mcx) * k;
          const ay = Math.abs((my - mcy) * k);
          if (ay <= a && Math.sqrt(3) * Math.abs(x) + ay <= Math.sqrt(3) * R) inside++;
        }
        const d = Math.hypot(mcx - boxFit.mcx, mcy - boxFit.mcy);
        if (!best || inside > best.inside || (inside === best.inside && d < best.d)) best = { inside, d, mcx, mcy, latMid, k };
      }
      return fitFromParams({ k: best.k, mcx: best.mcx, mcy: best.mcy, latMid: best.latMid });
    }

    /**
     * Does any of the route lie inside this framing? A point inside, or (for a sparse file) a
     * segment crossing the hexagon, sampled every 0.5 mm of print. The engine builds the route
     * only where it is inside the tile, so a framing without any is refused at Generate.
     */
    _routeInTile(fit) {
      const pts = this.state.route?.parsed?.points;
      if (!fit || !pts?.length) return true;
      const key = fitKey(fit);
      const memo = this._routeInTileMemo;
      if (memo && memo.key === key && memo.pts === pts) return memo.value;
      const value = this._coverage(fit) > 0 || this._routeCrossesTile(fit, pts);
      this._routeInTileMemo = { key, pts, value };
      return value;
    }

    _routeCrossesTile(fit, pts) {
      const R = this.config.sideMm;
      const a = (R * Math.sqrt(3)) / 2;
      const inHex = (x, y) => Math.abs(y) <= a && Math.sqrt(3) * Math.abs(x) + Math.abs(y) <= Math.sqrt(3) * R;
      let prev = null;
      for (const p of pts) {
        const q = [(lonToMercX(p.lon) - fit.mcx) * fit.k, -(latToMercY(p.lat) - fit.mcy) * fit.k];
        const off = !prev || Math.max(prev[0], q[0]) < -R || Math.min(prev[0], q[0]) > R || Math.max(prev[1], q[1]) < -a || Math.min(prev[1], q[1]) > a;
        if (!off && !p.breakBefore) {
          const n = Math.min(4000, Math.ceil(Math.hypot(q[0] - prev[0], q[1] - prev[1]) / 0.5));
          for (let i = 1; i < n; i++) {
            if (inHex(prev[0] + ((q[0] - prev[0]) * i) / n, prev[1] + ((q[1] - prev[1]) * i) / n)) return true;
          }
        }
        prev = q;
      }
      return false;
    }

    /**
     * The auto-fit is showing a 150 km section of a larger route: the share of its points inside
     * the hexagon, as a whole percentage (never rounded up to 100). Null when the whole route fits
     * or the shopper has framed the map themselves.
     */
    _clampedSection() {
      const c = this._fitClamped;
      if (!c || !this._fit || this._fitMode !== 'auto' || c.key !== fitKey(this._fit) || c.coverage >= 0.995) return null;
      return { pct: Math.floor(c.coverage * 100) };
    }

    /**
     * Adopt a framing. `mode` is 'auto' (auto-fit) or 'user' (panned/zoomed map).
     * A framing change after a preview marks it stale (needs regenerating).
     */
    _setFit(fit, mode, { silent = false } = {}) {
      const prev = this._fit;
      this._fit = fit;
      this._fitMode = mode;
      if (prev && this._built && fitKey(prev) !== fitKey(fit)) {
        this._stale = true;
        this._invalidateModel();
      }
      // Buildings cannot be offered beyond the span limit.
      const span = this._spanKm(fit);
      if (this._buildingsBlocked(span) && this.state.features.buildings) {
        this.state.features.buildings = false;
        if (this._built) this._stale = true;
        if (!silent) this.setStatus(this._m('buildings_span', { km: this.config.buildingsBlocksMaxSpanKm }));
      }
      this._showWarnings(this._framingWarnings());
      if (!silent) {
        this._render();
        this._save();
      }
      this._schedulePrefetch();
      this._scheduleElevationPrefetch();
    }

    _spanKm(fit) {
      if (!fit) return null;
      const metresPerMercUnit = 2 * Math.PI * EARTH_R * Math.cos((fit.latMid * Math.PI) / 180);
      return ((2 * this.config.sideMm) / fit.k) * metresPerMercUnit / 1000;
    }

    _coverage(fit) {
      const pts = this.state.route?.parsed?.points;
      if (!fit || !pts?.length) return 1;
      const R = this.config.sideMm;
      const a = (R * Math.sqrt(3)) / 2;
      let inside = 0;
      for (const p of pts) {
        const x = (lonToMercX(p.lon) - fit.mcx) * fit.k;
        const y = -(latToMercY(p.lat) - fit.mcy) * fit.k;
        const ay = Math.abs(y);
        if (ay <= a && Math.sqrt(3) * Math.abs(x) + ay <= Math.sqrt(3) * R) inside++;
      }
      return inside / pts.length;
    }

    _framingWarnings() {
      const warnings = [...this._routeWarnings()];
      const fit = this._fit;
      if (!fit) return warnings;
      const span = this._spanKm(fit);
      if (span > SPAN_HARD_MAX_KM) {
        warnings.push(this._m('span_too_large', { km: Math.round(span), max: SPAN_HARD_MAX_KM }));
      } else if (span > SPAN_WARN_KM) {
        warnings.push(this._m('span_large', { km: Math.round(span) }));
      } else if (span < SPAN_MIN_KM) {
        warnings.push(this._m('span_small', { min: SPAN_MIN_KM }));
      }
      const section = this._clampedSection();
      if (!this._routeInTile(fit)) warnings.push(this._m('route_not_in_tile'));
      else if (section) warnings.push(this._m('route_section', { km: SPAN_HARD_MAX_KM, pct: section.pct }));
      else if (this._coverage(fit) < COVERAGE_WARN) warnings.push(this._m('route_outside'));
      return warnings;
    }

    _canGenerate() {
      const s = this.state;
      if (!s.route) return { ok: false, reason: this._m('need_route') };
      if (!s.product) return { ok: false, reason: this._m('need_product') };
      const fit = this._fit;
      if (!fit) return { ok: false, reason: this._m('need_frame') };
      const span = this._spanKm(fit);
      if (span > SPAN_HARD_MAX_KM) return { ok: false, reason: this._m('zoom_in', { max: SPAN_HARD_MAX_KM }) };
      if (span < SPAN_MIN_KM) return { ok: false, reason: this._m('zoom_out', { min: SPAN_MIN_KM }) };
      // A tile with none of the route on it is not a route tile: never built, never carted.
      if (!this._routeInTile(fit)) return { ok: false, reason: this._m('route_not_in_tile') };
      return { ok: true };
    }

    _hexPx(w, h) {
      return HEX_WINDOW * Math.min(w, h);
    }

    _onStageResize() {
      const mode = this._stageMode();
      if (mode === 'canvas') this._drawRouteCanvas();
      this._mapApi?.onStageResize?.();
    }

    /**
     * Lazy-load the MapLibre framing view once a route exists. Until the basemap style is
     * ready (or if it never loads) the stage keeps the 2D canvas, and Auto-fit still works.
     */
    async _ensureMap() {
      if (this._mapApi || this._mapLoading || this._mapFailed || !this.state.route) return;
      const c = this.config;
      if (!c.mapModuleUrl || !c.maplibreJsUrl || !c.basemapStyleUrl) return;
      this._mapLoading = true;
      try {
        const mod = await import(c.mapModuleUrl);
        const api = await mod.createTerrainMap({
          host: this.els.map,
          styleUrl: c.basemapStyleUrl,
          maplibreJsUrl: c.maplibreJsUrl,
          maplibreCssUrl: c.maplibreCssUrl,
          elevationModuleUrl: c.elevationModuleUrl,
          sideMm: c.sideMm,
          hexWindow: HEX_WINDOW,
          magnets: c.magnets,
          magnetDiamMm: c.magnetDiamMm,
          routeColour: this.state.colours.route,
          label: this.els.map?.dataset.label,
          onFit: (fit, info) => this._onMapFit(fit, info),
          onLateError: (err) => console.warn('[terrain-studio] map error', err),
        });
        if (!this.isConnected) {
          api.dispose();
          return;
        }
        this._mapApi = api;
        api.setRoute(this.state.route ? this.state.route.parsed.points : null, this.state.colours.route);
        this._mapInteractive = null;
        this._lastStageMode = null;
      } catch (err) {
        console.warn('[terrain-studio] map unavailable, using the plain view', err);
        this._mapFailed = true;
        this._mapApi = null;
        // No coordinates: only the fact that framing runs without a basemap.
        this._emit('ttv:studio_basemap', { ok: false });
      } finally {
        this._mapLoading = false;
        this._render();
        // The map mounts hidden-then-shown: frame it once it has a size.
        if (this._mapApi && this._fit) this._mapApi.showFit(this._fit);
      }
    }

    /**
     * A settled shopper pan/zoom on the map — or a framing the map had to clamp (zoom range), in
     * which case what the hexagon shows is adopted so Generate builds exactly that.
     */
    _onMapFit(fit, { user, clamped } = {}) {
      if (!user || !fit || !Number.isFinite(fit.k) || fit.k <= 0 || this._busy) {
        // Snap the map back to the working fit while busy — but never answer a clamped report
        // with another showFit, which would only be clamped and reported again.
        if (this._busy && this._fit && !clamped) this._mapApi?.showFit(this._fit);
        return;
      }
      // A clamped auto-fit is still the auto-fit: keep the mode so Auto-fit reads as done.
      this._setFit(fit, clamped ? this._fitMode || 'auto' : 'user');
    }

    /** 2D route window: the plain-canvas framing view (also the basemap-outage fallback). */
    _drawRouteCanvas() {
      const cv = this.els.routeCanvas;
      const stage = this.els.stage;
      if (!cv || !stage) return;
      const w = stage.clientWidth;
      const h = stage.clientHeight;
      if (!w || !h) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      if (cv.width !== Math.round(w * dpr) || cv.height !== Math.round(h * dpr)) {
        cv.width = Math.round(w * dpr);
        cv.height = Math.round(h * dpr);
      }
      const ctx = cv.getContext('2d');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);
      const css = getComputedStyle(this);
      const fg = css.getPropertyValue('--ts-fg-rgb').trim() || '27,42,74';
      const routeHex = this.state.colours.route || '#C12E1F';

      const cx = w / 2;
      const cy = h / 2;
      const P = this._hexPx(w, h);
      const hexPath = () => {
        ctx.beginPath();
        for (let i = 0; i < 6; i++) {
          const a = (i * Math.PI) / 3;
          const x = cx + P * Math.cos(a);
          const y = cy + P * Math.sin(a);
          if (i) ctx.lineTo(x, y);
          else ctx.moveTo(x, y);
        }
        ctx.closePath();
      };

      // faint survey grid
      ctx.strokeStyle = `rgba(${fg},0.06)`;
      ctx.lineWidth = 1;
      const step = Math.max(24, Math.round(P / 6));
      ctx.beginPath();
      for (let x = (cx % step); x < w; x += step) {
        ctx.moveTo(x + 0.5, 0);
        ctx.lineTo(x + 0.5, h);
      }
      for (let y = (cy % step); y < h; y += step) {
        ctx.moveTo(0, y + 0.5);
        ctx.lineTo(w, y + 0.5);
      }
      ctx.stroke();

      // route
      const fit = this._fit;
      const route = this.state.route;
      if (fit && route) {
        const s = P / this.config.sideMm;
        ctx.save();
        ctx.lineJoin = 'round';
        ctx.lineCap = 'round';
        ctx.strokeStyle = routeHex;
        ctx.lineWidth = Math.max(2, P / 90);
        ctx.beginPath();
        let first = null;
        route.parsed.points.forEach((p, i) => {
          const x = cx + (lonToMercX(p.lon) - fit.mcx) * fit.k * s;
          const y = cy + (latToMercY(p.lat) - fit.mcy) * fit.k * s;
          if (i === 0 || p.breakBefore) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
          if (!first) first = [x, y];
        });
        ctx.stroke();
        if (first) {
          ctx.fillStyle = routeHex;
          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.arc(first[0], first[1], Math.max(4, P / 45), 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
        }
        ctx.restore();
      }

      // dimmed mask outside the hexagon, then the outline
      ctx.save();
      ctx.beginPath();
      ctx.rect(0, 0, w, h);
      hexPath();
      ctx.fillStyle = `rgba(${fg},0.12)`;
      ctx.fill('evenodd');
      ctx.restore();
      hexPath();
      ctx.strokeStyle = `rgba(${fg},0.85)`;
      ctx.lineWidth = 2;
      ctx.stroke();

      // magnet pockets (context for "fits the backpiece")
      const s2 = P / this.config.sideMm;
      ctx.strokeStyle = `rgba(${fg},0.35)`;
      ctx.lineWidth = 1;
      for (const [mx, my] of this.config.magnets) {
        ctx.beginPath();
        ctx.arc(cx + mx * s2, cy - my * s2, (this.config.magnetDiamMm / 2) * s2, 0, Math.PI * 2);
        ctx.stroke();
      }
    }

    /* =====================================================================
     * Map features (OpenStreetMap: water, forest, roads, buildings) — v12 logic
     * ===================================================================*/

    _onFeatureToggle(key, on) {
      if (this._busy) {
        this._render();
        return;
      }
      if (key === 'buildings' && on && ((this._fit && this._buildingsBlocked(this._spanKm(this._fit))) || this._buildingsTooMany())) {
        this._render();
        return;
      }
      // The shopper's own "no Water" is never overridden by the coastal default (§7 #7).
      if (key === 'water' && !on && this.state.features.water) this._waterUserOff = true;
      this.state.features[key] = !!on;
      this.state.preset = 'custom';
      if (this._built) this._stale = true;
      // Snow as a fifth colour: say so under the switch (cleared by _renderSnowBudget at <= 4).
      if (key === 'snow') this._snowBudgetHint = !!on && this._colourBudget().count > 4;
      this._invalidateModel();
      this._applyColors({ quiet: true });
      this._render();
      this._save();
      if (on) this._schedulePrefetch();
    }

    /* ---------- map cells (Phase 1) ---------- */

    /** The worker that serves the map cells, or null while they are off. */
    _cellsBase() {
      const c = this.config;
      return c.cellsEnabled ? c.cellsUrl || c.workerUrl || null : null;
    }

    /** Fetch the cell manifest at studio start (kept 1 h in memory and sessionStorage), so the first lookup has it. */
    _warmCells() {
      const base = this._cellsBase();
      if (!base || !this.config.waterModuleUrl) return;
      import(this.config.waterModuleUrl)
        .then((water) => typeof water.loadCellManifest === 'function' && water.loadCellManifest(base))
        .catch(() => {});
    }

    /* ---------- map lookup started early (§7 #4) ---------- */

    /**
     * On the Map step, once the framing settles with a map feature on, start that feature's lookup
     * in the background so Generate usually finds it cached. Debounced (a pan is many settles) and
     * one prefetch at a time; the same per-kind queries Generate makes, for the same bounds.
     */
    _schedulePrefetch() {
      clearTimeout(this._prefetchTimer);
      // A prefetch for a framing the shopper has left is only competing with the next lookup for
      // the map providers (and their shared 429 cooldown): stop it now.
      this._cancelPrefetch(this._fit ? fitKey(this._fit) : null);
      if (this.state.step !== 'map' || !this._fit || !this._prefetchKinds().length) return;
      this._prefetchTimer = setTimeout(() => this._prefetchMap(), PREFETCH_DELAY_MS);
    }

    /**
     * Abort the running map prefetch unless it is for `keepKey` (a fit key) or a Generate lookup
     * has already joined it. Its requests stop at the network; nothing is cached from them.
     */
    _cancelPrefetch(keepKey = null) {
      const p = this._prefetch;
      if (!p || p.adopted || (keepKey && p.key === keepKey)) return;
      p.controller.abort();
      this._prefetch = null;
    }

    /**
     * Generate is about to look up this framing: a prefetch for it is joined (never aborted
     * later), and one for any other framing is stopped first.
     */
    _adoptPrefetch(key) {
      this._cancelPrefetch(key);
      if (this._prefetch && this._prefetch.key === key) this._prefetch.adopted = true;
    }

    /** The kinds a Generate would look up now, less glaciers (after the snow check) and Blocks (unless cells are on). */
    _prefetchKinds() {
      const f = this.state.features;
      const mode = f.buildings ? this._buildingsMode() : null;
      const kinds = [];
      if (f.water || f.buildings) kinds.push('water');
      if (f.forest) kinds.push('forest');
      if (f.roads || mode === 'blocks') kinds.push('roads');
      // Blocks buildings only while the cells are on: water-data then warms them from the cells, never live.
      if (mode === 'individual' || (mode === 'blocks' && this._cellsBase())) kinds.push('buildings');
      return kinds;
    }

    async _prefetchMap() {
      const fit = this._fit;
      if (this._busy || this.state.step !== 'map' || !fit || !this.config.waterModuleUrl) return;
      if (!this._routeInTile(fit)) return; // Generate would not look this framing up either
      const span = this._spanKm(fit);
      if (!(span >= SPAN_MIN_KM && span <= SPAN_HARD_MAX_KM)) return;
      const kinds = this._prefetchKinds();
      if (!kinds.length) return;
      const key = fitKey(fit);
      this._cancelPrefetch(key);
      if (this._prefetch) {
        // Still warming this framing (the kinds may have grown): once more when it is done.
        this._prefetchAgain = true;
        return;
      }
      // A visit that keeps reframing stops prefetching after PREFETCH_MAX framings.
      if ((this._prefetchCount || 0) >= PREFETCH_MAX) return;
      this._prefetchCount = (this._prefetchCount || 0) + 1;
      const job = { key, controller: new AbortController(), adopted: false };
      this._prefetch = job;
      try {
        const [elev, water] = await Promise.all([this._elevationModule(), import(this.config.waterModuleUrl)]);
        if (typeof water.prefetchMapFeatures !== 'function' || job.controller.signal.aborted) return;
        // Exactly the bounds Generate queries: the elevation raster's padded hexagon footprint.
        const bounds = elev.padBounds(elev.hexFootprintBounds(fit, this.config.sideMm), 0.01);
        await water.prefetchMapFeatures(bounds, {
          want: kinds,
          proxyUrl: this.config.workerUrl,
          cells: this._cellsBase(),
          timeoutMs: MAP_ATTEMPT_MS,
          buildingsMode: kinds.includes('buildings') ? (this._buildingsMode() === 'blocks' ? 'blocks' : 'individual') : null,
          signal: job.controller.signal,
        });
      } catch (e) {
        /* a prefetch only warms the cache; Generate reports its own lookup */
      } finally {
        if (this._prefetch === job) this._prefetch = null;
        if (this._prefetchAgain) {
          this._prefetchAgain = false;
          this._schedulePrefetch();
        }
      }
    }

    /* ---------- elevation fetched early (§7 #8) ---------- */

    /**
     * On the Map step, once a framing settles, fetch its elevation raster in the background: the
     * same footprint and options Generate uses, so Generate usually starts with the tiles in hand.
     * Debounced (a pan is many settles), one framing in flight at a time (a newer framing aborts the
     * older fetch), and the last ELEVATION_PREFETCH_KEEP rasters kept by fit key. The geometry
     * worker is started alongside, so its modules and WASM are loaded before the first build.
     */
    _scheduleElevationPrefetch() {
      clearTimeout(this._elevPrefetchTimer);
      if (this.state.step !== 'map' || !this._fit) return;
      this._elevPrefetchTimer = setTimeout(() => this._prefetchElevation(), ELEVATION_PREFETCH_DELAY_MS);
    }

    _prefetchElevation() {
      const fit = this._fit;
      if (!fit || this.state.step !== 'map' || this._busy || !this.config.tileUrlTemplate) return;
      if (!this._canGenerate().ok) return; // Generate would not fetch this framing either
      const key = fitKey(fit);
      if (this._elevation && this._elevationKey === key) return; // Generate already has it
      this._elevPrefetches ||= new Map();
      const have = this._elevPrefetches.get(key);
      if (have && !have.error) return; // on its way, or in hand
      this._cancelElevationPrefetch({ inFlightOnly: true });
      const controller = new AbortController();
      const rec = { key, controller, grid: null, error: null, t0: performance.now(), ms: null, listener: null };
      rec.promise = (async () => {
        const mod = await this._elevationModule();
        return mod.fetchElevationGrid(mod.hexFootprintBounds(fit, this.config.sideMm), {
          urlTemplate: this.config.tileUrlTemplate,
          proxyUrl: this.config.workerUrl,
          padFrac: 0.01,
          signal: controller.signal,
          onProgress: (done, total) => rec.listener?.(done, total),
        });
      })();
      rec.promise.then(
        (grid) => {
          rec.grid = grid;
          rec.ms = Math.round(performance.now() - rec.t0);
          // Keep the newest rasters only (each is up to 30 tiles, about 8 MB).
          const done = [...this._elevPrefetches.values()].filter((r) => r.grid);
          for (const old of done.slice(0, Math.max(0, done.length - ELEVATION_PREFETCH_KEEP))) this._elevPrefetches.delete(old.key);
        },
        (err) => {
          rec.error = err;
          if (this._elevPrefetches?.get(key) === rec) this._elevPrefetches.delete(key);
        }
      );
      this._elevPrefetches.set(key, rec);
      this._warmWorker();
    }

    /** The prefetched raster for this fit key: in hand, or awaited (with Generate's progress). Null if none. */
    async _takeElevationPrefetch(key, onProgress) {
      clearTimeout(this._elevPrefetchTimer);
      const rec = this._elevPrefetches?.get(key);
      if (!rec || rec.error) return null;
      if (rec.grid) return rec.grid;
      rec.listener = onProgress;
      try {
        return await rec.promise;
      } catch (e) {
        return null; // aborted or failed: Generate fetches it itself, and reports its own error
      } finally {
        rec.listener = null;
      }
    }

    /** Stop elevation prefetches: the ones in flight, or (route change, disconnect) everything kept too. */
    _cancelElevationPrefetch({ inFlightOnly = false } = {}) {
      clearTimeout(this._elevPrefetchTimer);
      const recs = this._elevPrefetches;
      if (!recs) return;
      for (const [k, rec] of recs) {
        if (rec.grid && inFlightOnly) continue;
        if (!rec.grid) rec.controller.abort();
        recs.delete(k);
      }
    }

    /** Start the geometry worker (modules and WASM) ahead of the first build; a no-op once it runs. */
    _warmWorker() {
      if (this._worker || !this.config.workerModuleUrl) return;
      this._ensureWorker();
      // The ready reply carries the engine's feature cap: the first Generate of a coastal tile then
      // builds once, with Water, instead of learning the cap from a first build without it.
      this._callWorker('init', this._workerModules)
        .then((ready) => this._noteEngineCaps(ready))
        .catch(() => {
          /* a failed warm-up retires this worker; the first build starts a fresh one */
        });
    }

    /* ---------- Buildings style (ROUTE-AND-BUILDINGS-DESIGN §D.4, §E.1) ---------- */

    /** Buildings are not offered at all beyond the Blocks span limit (5 km). */
    _buildingsBlocked(span) {
      return span != null && span > this.config.buildingsBlocksMaxSpanKm;
    }

    /** This framing's lookup counted more buildings than Blocks can print (water-data preflight). */
    _buildingsTooMany() {
      return !!this._fit && this._buildingsTooManyKey != null && this._buildingsTooManyKey === fitKey(this._fit);
    }

    /**
     * The Buildings style a build uses at this span: the shopper's pick, else by span (Individual
     * up to 2 km, Blocks above). Individual is never used above 2 km, whatever was picked.
     */
    _buildingsMode(span = this._fit ? this._spanKm(this._fit) : null) {
      const s = this.state;
      const individualOk = span == null || span <= this.config.buildingsIndividualMaxSpanKm;
      if (s.buildingsModeSource === 'user' && BUILDINGS_MODES.includes(s.buildingsMode)) {
        return s.buildingsMode === 'individual' && !individualOk ? 'blocks' : s.buildingsMode;
      }
      return individualOk ? 'individual' : 'blocks';
    }

    /** 'user' when the style in use is the shopper's own pick, else 'auto' (preselected by span). */
    _buildingsModeSource(span) {
      const s = this.state;
      return s.buildingsModeSource === 'user' && s.buildingsMode === this._buildingsMode(span) ? 'user' : 'auto';
    }

    /** The Individual / Blocks choice under the Buildings switch, shown only while it is on. */
    _renderBuildingsStyle(span) {
      const el = this.els;
      if (!el.buildingsStyle) return;
      const s = this.state;
      el.buildingsStyle.hidden = !(s.features.buildings && !this._buildingsBlocked(span));
      const mode = this._buildingsMode(span);
      const individualOk = span == null || span <= this.config.buildingsIndividualMaxSpanKm;
      for (const input of el.buildingsStyleInputs) {
        input.checked = input.value === mode;
        input.disabled = !!this._busy || (input.value === 'individual' && !individualOk);
      }
      const km = formatKm(this.config.buildingsIndividualMaxSpanKm);
      // Over 2 km: why Individual is disabled. Otherwise the line matches the style in use.
      const hint = !individualOk
        ? this.t.buildingsIndividualOver.split('[km]').join(km)
        : mode === 'blocks'
          ? this.t.buildingsStyleBlocks
          : this.t.buildingsStyleIndividual;
      if (el.buildingsStyleHint && el.buildingsStyleHint.textContent !== hint) el.buildingsStyleHint.textContent = hint;
    }

    _onBuildingsStyle(mode) {
      if (!BUILDINGS_MODES.includes(mode)) return;
      if (this._busy) {
        this._render();
        return;
      }
      const span = this._fit ? this._spanKm(this._fit) : null;
      if (mode === 'individual' && span != null && span > this.config.buildingsIndividualMaxSpanKm) {
        this._render();
        return;
      }
      const before = this._buildingsMode(span);
      this.state.buildingsMode = mode;
      this.state.buildingsModeSource = 'user';
      if (before !== mode) {
        // A different style is a different geometry (and Blocks needs the road lookup): regenerate.
        if (this._built && this.state.features.buildings) this._stale = true;
        this._invalidateModel();
      }
      this._render();
      this._save();
      this._schedulePrefetch();
    }

    /* ---------- Route width and Sculpted surface (§B.2, §C.4, §E.1) ---------- */

    _routeWidthOpts() {
      return ROUTE_WIDTHS[this.state.routeWidth] || ROUTE_WIDTHS[DEFAULT_ROUTE_WIDTH];
    }

    _onRouteWidth(width) {
      if (!ROUTE_WIDTHS[width] || width === this.state.routeWidth) return;
      this.state.routeWidth = width;
      this._onDesignOption();
    }

    _onSculpted(on) {
      if (!!on === !!this.state.sculpted) return;
      this.state.sculpted = !!on;
      this._onDesignOption();
    }

    /**
     * A Customise option that changes the geometry: rebuild in place at the current height (the
     * height slider's path, without its timer). During a rebuild the change is queued; a stale
     * preview waits for Generate.
     */
    _onDesignOption() {
      this._save();
      this._invalidateModel();
      if (this._busy && !this._rebuilding) {
        // Generate / Add to cart is running (the controls are normally disabled then).
        if (this._built) this._stale = true;
        this._render();
        return;
      }
      if (this._built && !this._stale && this._elevation && this._fit) {
        this._pending = true;
        this._updateFailed = false; // the change itself is the retry
        this._render();
        this._emitPurchaseState();
        if (this._busy) {
          this._zQueued = true;
          this._optsQueued = true;
        } else this._rebuildForZ();
      } else {
        this._render();
      }
    }

    /**
     * The switchback hint (§B.2): some legs merge at this width and Fine would keep at least half
     * of that length apart. Only from a build at the chosen width; never at Fine itself.
     */
    _renderRouteWidthHint(meta = this._buildMeta) {
      const el = this.els.routeWidthHint;
      if (!el) return;
      const s = this.state;
      const wantMm = 2 * this._routeWidthOpts().radiusMm;
      let text = '';
      if (
        meta &&
        s.routeWidth !== 'fine' &&
        !this._pending &&
        Number.isFinite(meta.routeWidthMm) &&
        Math.abs(meta.routeWidthMm - wantMm) < 1e-6 &&
        Number.isFinite(meta.routeMergedLengthMm) &&
        Number.isFinite(meta.routeMergedLengthFineMm) &&
        meta.routeMergedLengthMm > SWITCHBACK_MIN_MM &&
        meta.routeMergedLengthFineMm <= SWITCHBACK_FINE_RATIO * meta.routeMergedLengthMm
      ) {
        text = this.t.routeSwitchback;
      }
      // A live region: unchanged text is not rewritten.
      if (el.textContent !== text) el.textContent = text;
    }

    /* ---------- snow line (SNOW-DESIGN §1, §5) ---------- */

    /** A snow-line string from locales (vestige_studio.map.snow_line_*) with [m] / [pct] filled in. */
    _snowText(key, vars) {
      let text = (this.t.snow && this.t.snow[key]) || '';
      if (vars) for (const [name, value] of Object.entries(vars)) text = text.split(`[${name}]`).join(String(value));
      return text;
    }

    /** "1,850 m" — the studio's number formatting (toLocaleString) plus the locale's unit string. */
    _snowMetres(m) {
      return this._snowText('m', { m: Math.round(m).toLocaleString() });
    }

    /** The build's snow-line range [lo, hi] in metres, or null when the build did not report one. */
    _snowRange(meta = this._buildMeta) {
      const r = meta && meta.snowRangeM;
      return Array.isArray(r) && r.length === 2 && Number.isFinite(r[0]) && Number.isFinite(r[1]) && r[1] > r[0] ? r : null;
    }

    /**
     * Reflect state + the last build's meta into the Snow line field. A build meta without the
     * snow fields (an engine before SNOW-DESIGN) reads as "not offered": the slider stays disabled.
     */
    _renderSnowField() {
      const el = this.els;
      if (!el.snowField) return;
      const s = this.state;
      const meta = this._buildMeta;
      el.snowField.hidden = !(this.config.showSnow && s.features.snow);
      const range = this._snowRange(meta);
      const flat = !!(meta && meta.snowOffered === false);
      const offered = !!(range && meta.snowOffered === true);
      const edited = !!this._snowEdited;
      const auto = s.snowLineM == null;
      const slider = el.snowLine;
      if (slider) {
        const set = (k, v) => {
          if (slider[k] !== v) slider[k] = v;
        };
        if (range) {
          set('min', String(range[0]));
          set('max', String(range[1]));
          set('step', '10');
          if (!edited && Number.isFinite(meta.snowLineM)) set('value', String(Math.round(meta.snowLineM)));
        } else if (Number.isFinite(s.snowLineM)) {
          // No build range yet (e.g. after a reload): centre the thumb on the stored line rather
          // than leaving the HTML default under a readout that says something else.
          set('min', String(s.snowLineM - 500));
          set('max', String(s.snowLineM + 500));
          set('step', '10');
          set('value', String(s.snowLineM));
        }
        // Like the feature switches, locked while busy — except under the shopper's own hand
        // during a snow-line/height REBUILD, so a keyboard user keeps focus (the move is queued).
        // Never during Generate or Add to cart: a move there would not reach the built tile.
        const focused = document.activeElement === slider;
        slider.disabled = !offered || (!!this._busy && !(focused && this._rebuilding));
      }
      // Auto mode after a build reads "1,850 m · Auto" (§1.1); before a build just "Auto".
      const built = !edited && offered && Number.isFinite(meta.snowLineM);
      let readout;
      if (auto) readout = built ? `${this._snowMetres(meta.snowLineM)} · ${this._snowText('auto')}` : this._snowText('auto');
      else if (built) readout = this._snowMetres(meta.snowLineM);
      else readout = this._snowMetres(s.snowLineM);
      // The typed field: the build's range, the readout's number (empty = "Auto" placeholder),
      // disabled with the slider (same focus exception). Left alone while the shopper types.
      const num = el.snowNumber;
      if (num) {
        if (range) {
          if (num.min !== String(range[0])) num.min = String(range[0]);
          if (num.max !== String(range[1])) num.max = String(range[1]);
        }
        const numFocused = document.activeElement === num;
        num.disabled = !offered || (!!this._busy && !(numFocused && this._rebuilding));
        if (!(this._snowTyping && numFocused)) {
          const line = built ? meta.snowLineM : s.snowLineM;
          const text = (auto && !built) || !Number.isFinite(line) ? '' : String(Math.round(line));
          if (num.value !== text) num.value = text;
        }
      }
      if (el.snowOutput && el.snowOutput.textContent !== readout) el.snowOutput.textContent = readout;
      if (slider) slider.setAttribute('aria-valuetext', readout);
      if (el.snowAuto) {
        el.snowAuto.hidden = auto;
        el.snowAuto.disabled = !!this._busy;
      }
      let hint;
      // Branch order (SNOW-DESIGN §5): climate → flat → none → generate → moved → cover.
      if (flat && meta.snowReason === 'climate' && Number.isFinite(meta.snowClimateLineM)) {
        // Below the latitude's snow line (§3.4): the slider stays disabled.
        hint = this._snowText('climate', { m: Math.round(meta.snowClimateLineM).toLocaleString() });
      } else if (flat) {
        const relief = Number.isFinite(meta.elevationMaxM) && Number.isFinite(meta.elevationMinM) ? meta.elevationMaxM - meta.elevationMinM : 0;
        hint = this._snowText('flat', { m: Math.max(0, Math.round(relief)).toLocaleString() });
      } else if (!offered || (edited && (!this._built || this._stale))) {
        hint = this._snowText('generate');
      } else if (!meta.snow && meta.snowReason === 'none' && Number.isFinite(meta.snowLineM)) {
        // Offered and wanted, but nothing printable survived the cull at this line: the preview
        // IS current, so say so and point the slider the right way (it stays enabled).
        hint = this._snowText('none', { m: Math.round(meta.snowLineM).toLocaleString() });
      } else if (!meta.snow) {
        hint = this._snowText('generate');
      } else if (meta.snowLineClamped && !auto) {
        hint = this._snowText('moved', { m: Math.round(meta.snowLineM).toLocaleString() });
      } else if (Number.isFinite(meta.snowAreaFrac) && Number.isFinite(meta.snowLineM)) {
        hint = this._snowText('cover', {
          m: Math.round(meta.snowLineM).toLocaleString(),
          pct: Math.round(Math.max(0, Math.min(1, meta.snowAreaFrac)) * 100),
        });
      } else {
        hint = '';
      }
      if (el.snowHint) {
        if (el.snowHint.textContent !== hint) el.snowHint.textContent = hint;
        el.snowHint.hidden = !hint;
      }
    }

    /** Dragging: state and readout only (the geometry follows on release — _onSnowLineChange). */
    _onSnowLineInput(value) {
      if (!Number.isFinite(value)) return;
      const s = this.state;
      s.snowLineM = Math.round(value);
      s.snowLineUser = true;
      s.snowPctLegacy = null;
      this._snowEdited = true;
      this._renderSnowField();
      this._save();
    }

    /**
     * Released: a built, current tile rebuilds at the new line straight away (like the height
     * slider, without its 260 ms timer); otherwise the preview is marked stale for Generate.
     */
    _onSnowLineChange(value) {
      if (!Number.isFinite(value)) return;
      this._onSnowLineInput(value);
      if (this._busy && !this._rebuilding) {
        // Generate / Add to cart is running (the slider is normally disabled then): that build
        // read the older line, so the tile it produces must not count as current (_generate
        // also compares the requested line when its build lands).
        this._stale = true;
        this._render();
        return;
      }
      if (this._built && !this._stale && !this._updateFailed && this._elevation && this._fit) {
        this._pending = true;
        this._invalidateModel();
        this._renderBar();
        this._emitPurchaseState();
        if (this._busy) {
          this._zQueued = true;
          this._snowQueued = true;
        } else this._rebuildForZ();
      } else {
        if (this._built) this._stale = true;
        this._invalidateModel();
        this._render();
      }
    }

    /**
     * Typed snow line (change / Enter): clamp into the build's range, snap to 10 m, write back to
     * the field and the slider, then take the slider's release path (_onSnowLineChange: a
     * current preview rebuilds, a stale one waits for Generate).
     */
    _commitSnowNumber() {
      const num = this.els.snowNumber;
      const slider = this.els.snowLine;
      if (!num || !slider || num.disabled) return;
      // Nothing typed since the last render (Enter on the shown line): no change, no rebuild.
      if (!this._snowTyping) {
        this._renderSnowField();
        return;
      }
      this._snowTyping = false;
      const raw = parseFloat(num.value);
      const range = this._snowRange();
      if (!Number.isFinite(raw) || !range) {
        this._renderSnowField();
        return;
      }
      const lo = Math.ceil(range[0] / 10) * 10;
      const hi = Math.max(lo, Math.floor(range[1] / 10) * 10);
      const v = Math.min(hi, Math.max(lo, Math.round(raw / 10) * 10));
      num.value = String(v);
      slider.value = String(v);
      if (v === this.state.snowLineM) {
        this._renderSnowField();
        return;
      }
      this._onSnowLineChange(v);
    }

    /** Auto chip: back to the engine's default line; the next Generate recomputes it. */
    _onSnowAuto() {
      if (this._busy) return;
      this._snowTyping = false;
      const s = this.state;
      s.snowLineM = null;
      s.snowLineUser = false;
      s.snowPctLegacy = null;
      this._snowEdited = true;
      if (this._built) this._stale = true;
      this._invalidateModel();
      this._render();
      this._save();
      // The chip hides in auto mode: keep focus in the field.
      const target = this.els.snowLine && !this.els.snowLine.disabled ? this.els.snowLine : this.els.features.snow;
      target?.focus();
    }

    /** A saved snowPct from before the snow-line slider, or null when it was only the default. */
    _legacySnowPct(saved) {
      const pct = saved && saved.snowPct;
      if (!Number.isFinite(pct)) return null;
      // No shopper control ever wrote snowPct: a value equal to a shipped default of the retired
      // snow_pct section setting (55 in the schema, 60 in page.design.json) was the default, so it
      // becomes auto.
      if (LEGACY_SNOW_PCT_DEFAULTS.includes(pct)) return null;
      return Math.min(98, Math.max(5, pct));
    }

    /** Restore the snow line from a saved design (session save or device autosave). */
    _restoreSnow(saved) {
      const s = this.state;
      if (Number.isFinite(saved.snowLineM)) {
        s.snowLineM = Math.round(saved.snowLineM);
        s.snowLineUser = true;
        s.snowPctLegacy = null;
      } else {
        s.snowLineM = null;
        s.snowLineUser = false;
        s.snowPctLegacy = this._legacySnowPct(saved);
      }
    }

    /** The snow part of a saved design: snowLineM / snowLineUser, plus an unconverted legacy snowPct. */
    _savedSnow() {
      const s = this.state;
      const out = { snowLineM: s.snowLineM, snowLineUser: !!s.snowLineUser };
      if (s.snowPctLegacy != null) out.snowPct = s.snowPctLegacy;
      return out;
    }

    /** Route width, Sculpted and the Buildings style of a saved design (§E.1, §E.3). */
    /**
     * The shopper's own "no Water" (§7 #7) and the footprint the automatic coastal Water already
     * ran for. Saved with the design so a reload, the Strava round trip or a resumed visit never
     * switches Water back on against the shopper's choice.
     */
    _savedWaterChoice() {
      return { waterUserOff: !!this._waterUserOff, autoWaterKey: this._autoWaterKey || null };
    }

    /** Restore them; called after the route is accepted (which resets the per-route state). */
    _restoreWaterChoice(saved) {
      this._waterUserOff = saved?.waterUserOff === true;
      this._autoWaterKey = typeof saved?.autoWaterKey === 'string' ? saved.autoWaterKey : null;
    }

    _savedDesignOptions() {
      const s = this.state;
      return {
        routeWidth: s.routeWidth,
        sculpted: !!s.sculpted,
        routeVersion: s.routeVersion,
        buildingsMode: s.buildingsModeSource === 'user' ? s.buildingsMode : null,
        buildingsModeSource: s.buildingsModeSource === 'user' ? 'user' : 'auto',
      };
    }

    /**
     * Restore them. A design saved before these options existed takes the new defaults (Standard,
     * Softened, style by span); returns true so the caller can show the one-line notice (§E.3).
     */
    _restoreDesignOptions(saved) {
      const s = this.state;
      s.routeWidth = ROUTE_WIDTHS[saved.routeWidth] ? saved.routeWidth : DEFAULT_ROUTE_WIDTH;
      s.sculpted = saved.sculpted === true;
      // A design saved with routeVersion 2 (or 4) keeps its route cover; one saved before
      // routeVersion was stored, with 3 or with anything else takes the current one, and is announced (below).
      s.routeVersion = ROUTE_VERSIONS_RESTORABLE.includes(saved.routeVersion) ? saved.routeVersion : ROUTE_VERSION;
      if (saved.buildingsModeSource === 'user' && BUILDINGS_MODES.includes(saved.buildingsMode)) {
        s.buildingsMode = saved.buildingsMode;
        s.buildingsModeSource = 'user';
      } else {
        s.buildingsMode = null;
        s.buildingsModeSource = 'auto';
      }
      // §E.3: a design saved before the route options (no routeWidth), or with a route cover that is
      // not kept (none, 3 or an unknown value: it now builds as v4), takes the new defaults with the notice.
      return !('routeWidth' in saved) || !ROUTE_VERSIONS_RESTORABLE.includes(saved.routeVersion);
    }

    /**
     * After a restore: show the notice (§E.3) only for a design saved before these options, which
     * takes the new defaults. A design saved by this version has nothing to announce: an explicit
     * Individual pick on a framing over 2 km was already built as Blocks when it was saved, and
     * designs from before this change never carry a 'user' Buildings style.
     */
    _noticeRestoredDesign(predates) {
      this._upgradeNotice = !!(this.state.route && predates);
    }

    /**
     * Fetch the real lakes / reservoirs / rivers, woodland, roads and buildings for this
     * tile's footprint. Cached per generated area; re-queried only when a feature is
     * turned on that wasn't fetched yet. Snow needs only the glacier outlines (SNOW-DESIGN §2.8),
     * and only while this footprint may be offered snow.
     * @returns {Promise<boolean|undefined>} false if the user should not expect the feature
     */
    async _withMapData({ budgetMs = MAP_BUDGET_MS, quiet = false, lateWait = true } = {}) {
      const f = this.state.features;
      const wantWater = !!f.water;
      const wantForest = !!f.forest;
      const wantRoads = !!f.roads;
      const wantBuildings = !!f.buildings;
      // Blocks always cuts the printable roads out of the city blocks (§D.4 step 3), so it needs
      // the road lookup whether or not the Roads layer is on.
      const buildingsMode = wantBuildings ? this._buildingsMode() : null;
      // Before the first build of a footprint, decide from its elevation whether it can be offered
      // snow at all, so a climate-guarded or flat tile never asks for glaciers (§2.8).
      if (this.config.showSnow && f.snow && this._elevation && !this._buildMetaKnown()) await this._precheckSnowOffer();
      const wantGlacier = this._wantGlaciers();
      if (!wantWater && !wantForest && !wantRoads && !wantBuildings && !wantGlacier) return;
      if (!this._elevation) return false;

      const want = [];
      if (wantWater) want.push('water');
      if (wantForest) want.push('forest');
      if (wantRoads || buildingsMode === 'blocks') want.push('roads');
      if (wantBuildings) {
        want.push('buildings');
        if (!want.includes('water')) want.push('water');
      }
      // Last, so a glacier lookup never delays the shopper's other features in the batch order.
      if (wantGlacier) want.push('glacier');
      const got = (this._mapFeatures && this._mapFeatures._got) || [];
      if (want.every((w) => got.includes(w))) return this._checkBuildingsCap(buildingsMode);
      const requested = [...new Set([...got, ...want])];

      // While late map data is being added automatically, its own line stays up ("Adding forest…").
      if (!this._lateAdding) {
        this.setStatus(this._m('features_lookup'));
        this._setStageNote(this._m('features_lookup'));
      }
      const slow = this._slowNote(this._m('features_lookup_slow'), LOOKUP_SLOW_MS);
      const lookupKey = this._elevationKey;
      // Phase 0 #4: during Generate the bar moves per kind received, and creeps in between.
      const bar = this._lookupRange ? this._progressRun(this._lookupRange, budgetMs) : null;
      let received = 0;
      // A prefetch of this framing is joined; one of an older framing stops competing with it.
      this._adoptPrefetch(this._elevationKey);
      try {
        const mod = await import(this.config.waterModuleUrl);
        const feats = await mod.fetchWaterFeatures(this._elevation.paddedBounds, {
          // §7 #4: one 18 s budget for the whole lookup, and keep what arrived (failedKinds). A
          // kind still loading at the deadline finishes in the background into the cache.
          timeoutMs: MAP_ATTEMPT_MS,
          budgetMs,
          partial: true,
          proxyUrl: this.config.workerUrl,
          cells: this._cellsBase(),
          want: requested,
          // The buildings cap (Individual 2,000, Blocks 12,000) and, for Blocks, the direct-path
          // preflight and long retry when the normal lookup fails. The Individual cap applies
          // only when Individual is the style being built: buildings already fetched for Blocks
          // on this framing are re-requested (from the cache) with Buildings off, and must not
          // then fail every later lookup. _checkBuildingsCap enforces 2,000 for Individual.
          buildingsMode: buildingsMode === 'individual' ? 'individual' : requested.includes('buildings') ? 'blocks' : null,
          onProgress: (message, code) => {
            const text = code === 'buildings_slow' ? this._m('buildings_slow') || message : message;
            if (this._lateAdding && code !== 'buildings_slow') return;
            this.setStatus(text);
            if (code === 'buildings_slow') this._setStageNote(text);
          },
          // A kind that missed the budget and lands afterwards (in the cache): _onLateMapData.
          onLateSettled: (kinds) => this._onLateMapData(kinds, lookupKey),
          onKindSettled: () => bar && bar.step(++received / requested.length),
        });
        // A failed glacier lookup never blocks the build (glaciers only add to Snow); leaving it
        // out of _got lets the next Generate try again. The same for a kind that missed the
        // budget. Buildings are built with the water around them (and Blocks with the roads that
        // cut them), so they wait for those too.
        const lost = new Set(Array.isArray(feats.failedKinds) ? feats.failedKinds : []);
        if (requested.includes('buildings') && (lost.has('water') || (buildingsMode === 'blocks' && lost.has('roads')))) lost.add('buildings');
        if (feats.glacierFailed) lost.add('glacier');
        feats._got = requested.filter((w) => !lost.has(w));
        feats._leftOut = [...lost].filter((w) => w !== 'glacier');
        feats._glacierFailed = !!feats.glacierFailed;
        if (lost.size) console.warn('[terrain-studio] map lookup left out', [...lost]);
        this._mapFeatures = feats;
        this._mapRevision = (this._mapRevision || 0) + 1;
        if (wantWater && !lost.has('water') && feats.waterCount === 0) this.setStatus(this._m('no_water'));
        else if (wantForest && !lost.has('forest') && feats.forestCount === 0) this.setStatus(this._m('no_forest'));
        return this._checkBuildingsCap(buildingsMode) === false ? false : true;
      } catch (err) {
        console.warn('[terrain-studio] map data failed', err);
        if (err && err.code === 'buildings_too_many') return this._onTooManyBuildings(err.buildingsMode);
        // Phase 0 #1: nothing arrived inside the budget, but answers are still on their way. Wait
        // for them ("Still looking for map details…") and build with what lands, without a click.
        if (!quiet && lateWait && err && Array.isArray(err.lateEach) && lookupKey === this._elevationKey) {
          slow();
          // Phase 0 #4: during Generate the bar runs on, evenly, through the whole wait; the lookup
          // that takes what landed, and the build, carry on from where it stops (_progressSpan).
          if (bar) bar.stop();
          const lateBar = this._lookupRange ? this._progressRun([0, PROGRESS_LATE_TOP], LATE_WAIT_MS, { linear: true }) : null;
          let landed;
          try {
            landed = await this._awaitLateMapData(err.lateEach);
          } finally {
            if (lateBar) lateBar.stop();
          }
          if (landed.length && lookupKey === this._elevationKey) {
            // The lookup's own hook (onLateSettled) reported them too: this lookup takes them instead.
            this._lateMapData = null;
            return this._withMapData({ budgetMs: LATE_RETRY_BUDGET_MS, lateWait: false });
          }
        }
        // quiet: a lookup only for the automatic Water; the caller drops it and builds, so no error.
        if (!quiet) this.setStatus(err && err.name === 'WaterError' ? err.message : this._m('map_data_failed'), 'error');
        return false;
      } finally {
        slow();
        if (bar) bar.stop();
      }
    }

    /**
     * Phase 0 #1: show "Still looking for map details…" until every late answer has settled or
     * LATE_WAIT_MS has passed (the lookup threw at its deadline, so this is measured from it).
     * Returns the kinds that landed by then (they are in the cache).
     */
    async _awaitLateMapData(lateEach) {
      const text = this._m('features_still_looking');
      if (text) {
        this.setStatus(text);
        this._setStageNote(text);
      }
      const landed = [];
      let timer;
      const timeout = new Promise((resolve) => {
        timer = setTimeout(resolve, LATE_WAIT_MS);
      });
      const each = lateEach.map((p) => Promise.resolve(p).then((kind) => kind && landed.push(kind), () => {}));
      try {
        await Promise.race([Promise.all(each), timeout]);
      } finally {
        clearTimeout(timer);
      }
      this._emit('ttv:studio_late_wait', { landed: [...landed] });
      return [...landed];
    }

    /**
     * Map data that missed the lookup budget has landed (water-data onLateSettled; it is in the
     * cache now). The founder, 1 Oct 2026: on the first Generate, forest that arrives after the
     * 18 s budget must be added to the preview by itself, not wait for Update preview.
     */
    _onLateMapData(kinds, key) {
      if (!Array.isArray(kinds) || !kinds.length || key !== this._elevationKey) return;
      this._lateMapData = { key, kinds };
      this._maybeAddLateFeatures();
    }

    /** What the shopper can change between a Generate and late map data landing. */
    _lateAddSignature() {
      const s = this.state;
      return JSON.stringify({
        features: s.features, preset: s.preset, colours: s.colours, zMult: s.zMult, routeWidth: s.routeWidth,
        sculpted: s.sculpted, routeVersion: s.routeVersion, snowLineM: s.snowLineM, snowLineUser: s.snowLineUser,
        snowPctLegacy: s.snowPctLegacy, buildingsMode: s.buildingsMode, buildingsModeSource: s.buildingsModeSource,
        product: s.product, backpiece: s.backpiece, fit: this._fit ? fitKey(this._fit) : null,
      });
    }

    /**
     * Rebuild with the late map data, once, only when the preview on screen is the one the
     * shopper's Generate made and nothing has changed since (design, framing, route, step to the
     * cart). Otherwise the manual Update preview stays the way to add it. A Generate still running
     * when the data lands picks it up as it ends.
     */
    _maybeAddLateFeatures() {
      const late = this._lateMapData;
      if (!late || this._busy) return;
      this._lateMapData = null;
      const g = this._lateGenerate;
      if (!g || g.key !== late.key || this._elevationKey !== late.key || this.state.route !== g.route) return;
      if (this._built !== g.built || this._stale || this._pending || this._updateFailed) return;
      if (this._adding || this._exporting || this._addedModelKey) return;
      if (this._lateAddSignature() !== g.signature) return;
      const leftOut = this._leftOut();
      if (!leftOut.length) return;
      // Only data the preview is missing starts a rebuild (not a kind it already has).
      const lost = (this._mapFeatures && this._mapFeatures._leftOut) || [];
      if (!late.kinds.some((k) => lost.includes(k))) return;
      this._lateGenerate = null;
      this._emit('ttv:studio_late_features', { kinds: late.kinds, adding: leftOut });
      this._generate({ lateAdd: leftOut });
    }

    /**
     * The map features the shopper has on whose lookup ran out of time for this footprint (§7 #4):
     * the current build was made without them. [] when every lookup arrived.
     */
    _leftOut() {
      const mf = this._mapFeatures;
      if (!mf || !Array.isArray(mf._leftOut) || !mf._leftOut.length) return [];
      const f = this.state.features;
      const got = mf._got || [];
      // Water may have been looked up only for Buildings; then it is Buildings that is missing.
      return FEATURE_ORDER.filter((k) => mf._leftOut.includes(k) && f[k] && !got.includes(k));
    }

    /**
     * Individual keeps its 2,000 cap even when this footprint's buildings were fetched for Blocks
     * (whose cap is 12,000). Returns false (with the message showing) when the cap is exceeded.
     */
    _checkBuildingsCap(mode) {
      if (mode !== 'individual') return undefined;
      const n = (this._mapFeatures && this._mapFeatures.buildings && this._mapFeatures.buildings.length) || 0;
      return n > INDIVIDUAL_BUILDINGS_CAP ? this._onTooManyBuildings('individual') : undefined;
    }

    /**
     * Too many buildings (§D.4). Blocks: this framing cannot print buildings at all, so Buildings
     * is switched off and disabled until the framing changes. Individual: point to Blocks.
     */
    _onTooManyBuildings(mode) {
      if (mode === 'blocks') {
        this._buildingsTooManyKey = this._fit ? fitKey(this._fit) : null;
        this.state.features.buildings = false;
        this.state.preset = 'custom';
        this._applyColors({ quiet: true });
        this._save();
        this.setStatus(this._m('buildings_too_many'), 'error');
      } else {
        this.setStatus(this._m('buildings_individual_too_many'), 'error');
      }
      this._render();
      return false;
    }

    async _fitLandmarkHeight() {
      if (!this.state.features.buildings || !this._mapFeatures || !this._fit) return true;
      const mod = await this._elevationModule();
      for (let attempt = 0; attempt < 3; attempt++) {
        const fit = this._fit;
        const local = (this._mapFeatures.buildings || []).filter(
          (b) =>
            !b.isBridge &&
            b.polygons.some((r) =>
              r.some((p) => {
                const x = (mod.lonToMercX(p.lon) - fit.mcx) * fit.k;
                const y = -(mod.latToMercY(p.lat) - fit.mcy) * fit.k;
                return Math.hypot(x, y) <= this.config.sideMm;
              })
            )
        );
        const height = Math.max(0, ...local.map((b) => b.heightM * fit.vertScaleMmPerM));
        const available = this.config.maxHeightMm - this.config.baseMm - 4;
        if (height <= available) return true;
        // Expand the whole geographic scene, preserving XYZ proportions. Do not
        // flatten individual landmarks or sample beyond the fetched raster.
        const ratio = (available / height) * 0.95;
        const next = fitFromParams({ ...fit, k: fit.k * ratio, vertScaleMmPerM: fit.vertScaleMmPerM * ratio });
        this.setStatus(this._m('widening'));
        const grid = await mod.fetchElevationGrid(mod.hexFootprintBounds(next, this.config.sideMm), {
          urlTemplate: this.config.tileUrlTemplate,
          proxyUrl: this.config.workerUrl,
          padFrac: 0.01,
        });
        this._fit = next;
        this._fitMode = 'user';
        this._elevation = grid;
        this._elevationKey = fitKey(next);
        this._mapFeatures = null;
        this._mapRevision = (this._mapRevision || 0) + 1;
        this._invalidateModel();
        this._mapApi?.showFit(next);
        if ((await this._withMapData()) === false) return false;
      }
      throw withMsg(this._m('buildings_too_tall'));
    }

    /* =====================================================================
     * Generate + geometry worker — v12 pipeline
     * ===================================================================*/

    /**
     * @param {{lateAdd?: string[]}} [opts] lateAdd: an automatic rebuild that adds map features
     *   whose lookup missed the budget (_maybeAddLateFeatures); it shows "Adding forest…" and
     *   never schedules another.
     */
    async _generate(opts = {}) {
      if (this._busy) return;
      const lateAdd = Array.isArray(opts.lateAdd) && opts.lateAdd.length ? opts.lateAdd : null;
      // A new Generate supersedes the one whose left-out features were waiting to be added.
      this._lateGenerate = null;
      // ...and the background retry of what that one left out (#7): it is scheduled again at the
      // end. A Generate the shopper asked for starts the retry count again.
      clearTimeout(this._leftOutTimer);
      this._leftOutTimer = null;
      if (!lateAdd) this._leftOutRetry = null;
      const check = this._canGenerate();
      if (!check.ok) {
        this.setStatus(check.reason, 'error');
        return;
      }
      if (!this._hasWebGL()) {
        if (this.els.unsupported) this.els.unsupported.hidden = false;
        this.setStatus(this._m('no_webgl'), 'error');
        return;
      }
      const t0 = performance.now();
      let ok = false;
      // What this Generate asks for, to say what could not be built if it fails (§7 #1).
      const attempt = this._designSnapshot();
      let autoWater = false;
      this._setBusy(true);
      this._showWarnings(this._framingWarnings());
      this.setProgress(0.03);
      // Phase 0 #5: on a long wait, bring the stage's status chip into view.
      const revealCancel = this._revealStageNoteLater();
      this._lateAdding = lateAdd ? this._m('features_adding', { features: this._featureWords(lateAdd) }) : null;
      this.setStatus(this._lateAdding || this._m('preparing_tile'));
      this._setStageNote(this._lateAdding || this._m('preparing_tile'));
      try {
        const mod = await this._elevationModule();
        const fit = this._fit;
        const key = fitKey(fit);
        if (!this._elevation || this._elevationKey !== key) {
          // New framing ⇒ new footprint: elevation, map features and geometry are all stale.
          this._elevation = null;
          this._mapFeatures = null;
          this._mapRevision = (this._mapRevision || 0) + 1;
          this._geometryCache = new Map();
          // Fit FIRST, then fetch tiles for the whole hexagon footprint (never the route bbox).
          const area = mod.hexFootprintBounds(fit, this.config.sideMm);
          this.setStatus(this._m('elevation'));
          this._setStageNote(this._m('elevation'));
          const onProgress = (done, total) => {
            this.setStatus(this._m('elevation_progress', { done, total }));
            this.setProgress(PROGRESS_ELEVATION[0] + (PROGRESS_ELEVATION[1] - PROGRESS_ELEVATION[0]) * (done / total));
          };
          // §7 #8: the Map step usually fetched this framing's raster already (or is fetching it).
          const t0Elevation = performance.now();
          let grid = await this._takeElevationPrefetch(key, onProgress);
          const prefetched = !!grid;
          if (!grid) {
            this._cancelElevationPrefetch({ inFlightOnly: true }); // an older framing's fetch is no longer wanted
            grid = await mod.fetchElevationGrid(area, {
              urlTemplate: this.config.tileUrlTemplate,
              proxyUrl: this.config.workerUrl,
              padFrac: 0.01,
              onProgress,
            });
          }
          this._elevation = grid;
          this._elevationKey = key;
          this._elevationTiming = { key, prefetched, waitedMs: Math.round(performance.now() - t0Elevation) };
        }
        // §7 #7: a footprint whose ground inside the hexagon dips below sea level gets Water before
        // its lookup, once the engine is known to cap map-feature complexity (_noteEngineCaps).
        this._autoWaterPending = false;
        autoWater = !!this._engineFeatureCap && this._autoWaterForCoast(this._hexMinElevation());
        this.setProgress(PROGRESS_ELEVATION[1]);

        let built;
        let reqSnowLine;
        for (let pass = 0; pass < 2; pass++) {
          this._lookupRange = PROGRESS_LOOKUP;
          // Only the automatic Water to look up: a shorter wait, and no error if it fails (dropped below).
          const f0 = this.state.features;
          const autoOnly = autoWater && !!this._autoWaterPending && !f0.forest && !f0.roads && !f0.buildings && !this._wantGlaciers();
          const mapped = await this._withMapData(autoOnly ? { budgetMs: AUTO_WATER_BUDGET_MS, quiet: true } : undefined);
          if (mapped === false && autoWater && this._autoWaterPending) {
            // The automatic coastal Water is the studio's choice, not the shopper's: a lookup that
            // fails for it never costs the preview. Water goes back off, unannounced, and the tile
            // is built as the shopper set it (the first pass's tile when there was one).
            this._undoAutoWater();
            this._noteAutoWater('off-lookup');
            autoWater = false;
            if (pass === 1) break;
            // Nothing else to look up: build now. A lookup that also carried the shopper's own
            // features failed for them too, and is reported as before.
            const f = this.state.features;
            if (!f.forest && !f.roads && !f.buildings && !this._wantGlaciers()) {
              pass--;
              continue;
            }
          }
          if (mapped === false || (await this._fitLandmarkHeight()) === false) {
            // The Water lookup for the second pass failed: keep the first pass's tile (its message stays).
            if (pass === 1 && this._restoreLastGood()) ok = true;
            return;
          }
          // Low ground alone does not make a coast: an inland basin below sea level (Kati
          // Thanda–Lake Eyre, the Dead Sea, Death Valley) has no mapped sea. Only a lookup that
          // found the ocean keeps the automatic Water; otherwise it goes back off, unannounced.
          if (autoWater && this._autoWaterPending && !this._seaMapped()) {
            this._undoAutoWater();
            this._noteAutoWater('off-unmapped');
            autoWater = false;
            if (pass === 1) break; // the first pass's tile is this design again
          }
          this._autoWaterPending = false;
          this._lookupRange = null;

          this.setStatus(this._lateAdding || this._m('building'));
          this._setStageNote(this._lateAdding || this._m('building'));
          this.setProgress(PROGRESS_BUILD[0]);
          reqSnowLine = this._snowLineReq();
          const request = this._designSnapshot();
          // Phase 0 #4: the bar and the stage text follow the engine's stages (not while "Adding
          // forest…"), from where the bar is (past PROGRESS_BUILD[0] after a late wait).
          built = await this._buildWithNote({ progress: this._progressSpan(PROGRESS_BUILD), stages: !this._lateAdding });
          this._noteEngineCaps(built.meta);
          if (built.meta.audit.ok) this._rememberGood(request, built);
          // The first build of a visit is how the studio learns the engine's cap: a coastal tile
          // found then gets Water and one more build, within this same Generate. A sea flattened
          // inside the hexagon (seaFlatten) reads 0 m in the build, so the raster's own low point decides.
          if (pass === 0 && !autoWater && built.meta.audit.ok && this._autoWaterForCoast(this._seaFlattenedInHex(built.meta) > 0 ? this._hexMinElevation() : built.meta.elevationMinM)) {
            autoWater = true;
            continue;
          }
          break;
        }
        this.setProgress(0.97);

        // The first preview of a visit loads the 3D viewer: the bar keeps moving meanwhile.
        const viewerBar = this._progressRun([0.97, 1], 4000);
        const preview = await this._ensurePreview().finally(() => viewerBar.stop());
        preview.setTerrain(built);
        this._previewBuilt = built;
        preview.setColors(this._currentColors());
        this._built = built;
        // A snow line moved while this build ran is not in it: the tile is stale, not current.
        this._stale = !built.meta.audit.ok || this._snowLineReq() !== reqSnowLine;
        this._pending = false;
        this._updateFailed = false;
        this._model = null;
        this._addedModelKey = null;
        this._applyBuildMeta(built.meta);
        this._updateDevPanel(built);

        if (!built.meta.audit.ok) {
          this.setStatus(this._m('geometry_error'), 'error');
        } else {
          ok = true;
          this.state.view = '3d';
          // A small picture of this design for "Continue your last design".
          try {
            this._thumb = preview.snapshot ? preview.snapshot({ width: 256 }) : null;
          } catch (e) {
            this._thumb = null;
          }
          const grid = this._elevation;
          const lowDetail = grid.lowDetail && grid.z < (mod.ZMAX || 14);
          const notes = [];
          if (autoWater && this.state.features.water && built.meta.water) notes.push(this._m('sea_water_on'));
          // §7 #4: name what the lookup could not bring in time (the build went ahead without it).
          const leftOut = this._leftOut();
          if (leftOut.length) notes.push(this._m('features_left_out', { features: this._featureWords(leftOut, true) }));
          // Phase 0 #6: glacier outlines that did not arrive are named too, not left out silently.
          if (this.state.features.snow && built.meta.snowOffered && this._mapFeatures && this._mapFeatures._glacierFailed) notes.push(this._m('glaciers_unavailable'));
          // ...and add it by itself if it lands while this preview is unchanged. An automatic
          // rebuild schedules this again only for what is still missing (each one adds a kind, and
          // the background retries are bounded, #7), so it cannot loop.
          if (leftOut.length) {
            this._lateGenerate = { key: this._elevationKey, route: this.state.route, built, signature: this._lateAddSignature() };
          }
          this.setStatus(
            [
              this._m(lowDetail ? 'preview_ready_low' : 'preview_ready', {
                min: Math.round(built.meta.elevationMinM),
                max: Math.round(built.meta.elevationMaxM),
                mm: built.meta.maxZmm.toFixed(1),
              }),
              ...notes,
            ].join(' ')
          );
        }
      } catch (err) {
        if (err && err.geometryError) {
          // A worker/engine failure (a WASM trap included): shopper copy, never the engine's text,
          // and the last good preview comes back (§7 #1).
          this._onGeometryFailure(err, attempt);
        } else if (err && (err.name === 'ElevationError' || err.name === 'GeometryError' || err.userMessage)) {
          // ElevationError / GeometryError carry shopper-readable text; anything else is a bug.
          this.setStatus(err.userMessage || err.message, 'error');
        } else {
          console.error('[terrain-studio] generate failed', err);
          this.setStatus(this._m('build_failed'), 'error');
        }
      } finally {
        // Phase 0 #8: every outcome (success, failure, watchdog, late rebuild) ends here, so the
        // bar, the stage chip, the busy state and with it every button are reset on all of them.
        revealCancel();
        this._lookupRange = null;
        this._lateAdding = null;
        this._setStageNote(null);
        this.setProgress(null);
        this._setBusy(false);
        this._emit('ttv:studio_preview', {
          ok,
          spanKm: this._fit ? Math.round(this._spanKm(this._fit) * 10) / 10 : null,
          features: ALL_FEATURES.filter((k) => this.state.features[k]),
          ms: Math.round(performance.now() - t0),
          ...(lateAdd ? { lateAdd } : {}),
        });
        this._save();
        // Late map data that landed while this Generate ran.
        if (this._lateMapData) this._maybeAddLateFeatures();
        // Anything still left out is looked up again in the background (#7).
        if (!this._busy) this._scheduleLeftOutRetry();
        this._render();
      }
    }

    /* ---------- incomplete previews (Phase 0 #7) ---------- */

    /**
     * A preview built without a map feature the shopper chose is incomplete: the stage says so
     * ("Incomplete: retrying forest"), Add to cart waits, and the missing lookup is asked again in
     * the background, LEFT_OUT_RETRIES times LEFT_OUT_RETRY_MS apart (a request still running is
     * joined, not repeated). What lands is added like late map data (_onLateMapData).
     */
    _scheduleLeftOutRetry() {
      clearTimeout(this._leftOutTimer);
      this._leftOutTimer = null;
      const key = this._elevationKey;
      if (!this._built || !key || !this._leftOut().length) {
        this._leftOutRetry = null;
        return;
      }
      let r = this._leftOutRetry;
      if (!r || r.key !== key) r = this._leftOutRetry = { key, tries: 0, done: false };
      if (r.tries >= LEFT_OUT_RETRIES) r.done = true;
      if (!r.done) this._leftOutTimer = setTimeout(() => this._retryLeftOutNow(r), LEFT_OUT_RETRY_MS);
    }

    /** True while the background retries for this footprint's left-out features are still running. */
    _leftOutRetrying() {
      const r = this._leftOutRetry;
      return !!(r && r.key === this._elevationKey && !r.done);
    }

    async _retryLeftOutNow(r) {
      clearTimeout(this._leftOutTimer);
      this._leftOutTimer = null;
      if (this._leftOutRetry !== r || r.done || r.running || r.key !== this._elevationKey || !this._elevation) return;
      if (this._busy) {
        // A build or the cart is running: look again once it has ended.
        this._leftOutTimer = setTimeout(() => this._retryLeftOutNow(r), 2000);
        return;
      }
      const want = ((this._mapFeatures && this._mapFeatures._leftOut) || []).filter((k) => k !== 'glacier');
      if (!want.length || !this._leftOut().length) {
        this._scheduleLeftOutRetry();
        this._render();
        return;
      }
      r.tries += 1;
      r.running = true;
      const style = want.includes('buildings') ? this._buildingsMode() : null;
      let arrived = [];
      try {
        const mod = await import(this.config.waterModuleUrl);
        const feats = await mod.fetchWaterFeatures(this._elevation.paddedBounds, {
          timeoutMs: MAP_ATTEMPT_MS,
          budgetMs: MAP_BUDGET_MS,
          partial: true,
          proxyUrl: this.config.workerUrl,
          cells: this._cellsBase(),
          want,
          buildingsMode: style === 'individual' ? 'individual' : style ? 'blocks' : null,
          onLateSettled: (kinds) => this._onLateMapData(kinds, r.key),
        });
        const failed = new Set(Array.isArray(feats.failedKinds) ? feats.failedKinds : []);
        arrived = want.filter((k) => !failed.has(k));
      } catch (err) {
        // Nothing arrived yet; answers still on their way are added by onLateSettled.
        console.warn('[terrain-studio] background map retry', err);
      } finally {
        r.running = false;
      }
      if (this._leftOutRetry !== r) return;
      if (arrived.length) this._onLateMapData(arrived, r.key);
      if (!this._busy) {
        // It landed but was not added (the design changed since): Update preview adds it.
        if (arrived.length) r.done = true;
        this._scheduleLeftOutRetry();
        this._render();
      }
    }

    /** The stage badge while the preview lacks a map feature the shopper chose. */
    _renderIncomplete() {
      const el = this.els.incomplete;
      if (!el) return;
      const onStage = this.state.step === 'map' || this.state.step === 'customise';
      const kinds = this._built && onStage ? this._leftOut() : [];
      const vars = { features: this._featureWords(kinds) };
      const text = !kinds.length ? '' : this._leftOutRetrying() ? this._m('incomplete_retrying', vars) : this._m('incomplete_left_out', vars);
      if (el.textContent !== text) el.textContent = text;
      el.hidden = !text;
    }

    /* ---------- recovery from geometry failures (§7 #1) ---------- */

    /** Everything a build depends on, as the shopper has it now (restorable by _restoreLastGood). */
    _designSnapshot() {
      const s = this.state;
      return {
        features: { ...s.features },
        preset: s.preset,
        zMult: s.zMult,
        routeWidth: s.routeWidth,
        sculpted: s.sculpted,
        routeVersion: s.routeVersion,
        snowLineM: s.snowLineM,
        snowLineUser: s.snowLineUser,
        snowPctLegacy: s.snowPctLegacy,
        buildingsMode: s.buildingsMode,
        buildingsModeSource: s.buildingsModeSource,
        fit: this._fit,
        fitMode: this._fitMode,
        fitClamped: this._fitClamped,
        elevation: this._elevation,
        elevationKey: this._elevationKey,
        mapFeatures: this._mapFeatures,
      };
    }

    /** A build that passed the audit, with the design that asked for it: what a failure falls back to. */
    _rememberGood(request, built) {
      this._lastGood = {
        ...request,
        built,
        previewBuilt: built,
        lastBuildExport: !!this._lastBuildExport,
        lastBuildFast: !!this._lastBuildFast,
      };
    }

    /**
     * Put the last good design back: its options, framing, map data, preview and model, so it is
     * current (not stale) and can be added to the cart. False when there is none.
     */
    _restoreLastGood() {
      const g = this._lastGood;
      if (!g || !g.built || !g.fit || !g.elevation) return false;
      const s = this.state;
      s.features = { ...g.features };
      s.preset = g.preset;
      s.zMult = g.zMult;
      s.routeWidth = g.routeWidth;
      s.sculpted = g.sculpted;
      s.routeVersion = g.routeVersion;
      s.snowLineM = g.snowLineM;
      s.snowLineUser = g.snowLineUser;
      s.snowPctLegacy = g.snowPctLegacy;
      s.buildingsMode = g.buildingsMode;
      s.buildingsModeSource = g.buildingsModeSource;
      const refit = fitKey(this._fit) !== fitKey(g.fit);
      this._fit = g.fit;
      this._fitMode = g.fitMode;
      this._fitClamped = g.fitClamped;
      this._elevation = g.elevation;
      this._elevationKey = g.elevationKey;
      this._mapFeatures = g.mapFeatures;
      // A fresh revision (never an old number): later builds must not hit cache entries made
      // from the failed attempt's map data.
      this._mapRevision = (this._mapRevision || 0) + 1;
      this._geometryCache = new Map();
      this._built = g.built;
      this._stale = false;
      this._pending = false;
      this._updateFailed = false;
      clearTimeout(this._zTimer);
      this._zQueued = false;
      this._snowQueued = false;
      this._optsQueued = false;
      this._lastBuildExport = g.lastBuildExport;
      this._lastBuildFast = g.lastBuildFast;
      this._invalidateModel();
      if (g.previewBuilt && this._previewBuilt !== g.previewBuilt) {
        // The good build may never have been shown (a failure in the same Generate): show it now.
        const shown = g.previewBuilt;
        this._previewBuilt = shown;
        this._ensurePreview()
          .then((preview) => {
            if (this._previewBuilt !== shown) return;
            preview.setTerrain(shown);
            preview.setColors(this._currentColors());
          })
          .catch((e) => console.warn('[terrain-studio] preview unavailable', e));
      }
      this._applyBuildMeta(g.built.meta);
      if (refit) this._mapApi?.showFit(g.fit);
      this._syncInputsFromState();
      this._applyColors({ quiet: true });
      this._save();
      return true;
    }

    /**
     * A build the worker could not finish. The raw engine text ("null function", "memory access
     * out of bounds") goes to the console only; the shopper reads what to try, and the last good
     * preview is restored so the design they had stays ready for the cart. The copy follows the
     * kind of failure (workerError): only a WASM trap is "too much map detail"; a worker that did
     * not load (a network fault) or was replaced mid-job gets the plain retry copy; the engine's
     * own refusal (for example a building too tall for the tile) is shown as the engine words it.
     */
    _onGeometryFailure(err, attempt) {
      console.error('[terrain-studio] geometry build failed', err);
      const good = this._lastGood;
      const asked = FEATURE_ORDER.filter((k) => attempt && attempt.features[k]);
      const kind = err && err.watchdog ? 'watchdog' : err && err.engineTrap ? 'trap' : err && (err.workerLoad || err.workerRetired) ? 'retry' : 'engine';
      const engineText = kind === 'engine' && err && err.engineWords && err.message ? err.message : '';
      let text;
      if (good && this._restoreLastGood()) {
        const added = asked.filter((k) => !good.features[k]);
        if (kind === 'watchdog') text = this._m('build_timeout_kept');
        else if (kind === 'retry') text = this._m('build_failed_kept');
        else if (kind === 'engine') text = engineText || this._m('build_failed_kept');
        else if (added.length) text = this._m('detail_kept_feature', { feature: this._featureLabel(added[0]) });
        else if (asked.length) text = this._m('detail_kept_area');
        else text = this._m('build_failed_kept');
      } else {
        text =
          kind === 'watchdog'
            ? this._m('build_timeout')
            : kind === 'retry' || (kind === 'trap' && !asked.length)
            ? this._m('build_failed')
            : kind === 'engine'
              ? engineText || this._m('build_failed')
              : asked[0] === 'forest'
                ? this._m('too_much_detail')
                : this._m('too_much_detail_feature', { feature: this._featureLabel(asked[0]) });
        // Nothing to fall back to: the preview on screen (if any) is not this design; offer the retry.
        if (this._built) {
          this._pending = true;
          this._updateFailed = true;
        }
      }
      this.setStatus(text, 'error');
      this._render();
      this._emit('ttv:studio_build_failed', { kept: !!good, features: asked, ...(kind === 'watchdog' ? { watchdog: true } : {}) });
    }

    /** "Forest": the switch label the shopper sees for a map feature. */
    _featureLabel(key) {
      const el = this.querySelector(`[data-ts-feature-row="${key}"] .ts__switch-label`);
      const text = el && el.textContent.trim();
      return text || key.charAt(0).toUpperCase() + key.slice(1);
    }

    /** "forest and water" (or "Forest and Water" with capitals): feature names for a sentence. */
    _featureWords(keys, capitals = false) {
      const words = keys.map((k) => (capitals ? this._featureLabel(k) : this._featureLabel(k).toLocaleLowerCase()));
      if (words.length < 2) return words.join('');
      return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
    }

    /** Show `text` in the status line and on the stage once a step has run `ms`; returns a cancel. */
    _slowNote(text, ms) {
      if (!text) return () => {};
      const timer = setTimeout(() => {
        this.setStatus(text);
        this._setStageNote(text);
      }, ms);
      return () => clearTimeout(timer);
    }

    /** The map features this build will print (on, and looked up for this footprint). */
    _builtFeatureKinds() {
      const f = this.state.features;
      const got = (this._mapFeatures && this._mapFeatures._got) || [];
      return FEATURE_ORDER.filter((k) => f[k] && got.includes(k));
    }

    /**
     * _buildGeometry, with the progressive note for a build that carries map features (§7 #12).
     * opts.progress [from, to] (Phase 0 #4): the bar moves through that range while the engine
     * works; with opts.stages the status and the stage chip name its stage (terrain, then each
     * layer) instead of the slow note. The engine is one call in the worker, so the stage is
     * paced by this tile's expected build time (_buildEstimateMs), not reported by the engine.
     */
    async _buildWithNote(opts = {}) {
      const { progress, stages, ...buildOpts } = opts;
      const kinds = this._builtFeatureKinds();
      const text = (progress && stages) || !kinds.length
        ? ''
        : opts.export
          ? this._m('rendering_slow')
          : this._m('building_features_slow', { features: this._featureWords(kinds) });
      const cancel = this._slowNote(text, BUILD_SLOW_MS);
      const ticker = progress ? this._buildTicker(progress, kinds, opts) : null;
      const t0 = performance.now();
      try {
        const built = await this._buildGeometry(buildOpts);
        const ms = performance.now() - t0;
        // A cache hit says nothing about how long a build takes. Against the reference desktop's
        // quickest build of as many layers, it is also how fast this device builds now
        // (_deviceSlowness; a build with map features can only overstate it).
        if (!buildOpts.export && !buildOpts.fast && ms > 500) {
          this._lastBuildMs = { ms, layers: kinds.length };
          const ref = (TERRAIN_BUILD_REF_MS + BUILD_ESTIMATE_PER_LAYER_MS * kinds.length) * (this._buildIsHeavy(buildOpts) ? 3 : 1);
          this._buildSlowness = ms / ref;
        }
        return built;
      } finally {
        cancel();
        if (ticker) ticker.stop();
      }
    }

    /** Phase 0 #4: pace the bar (and, with opts.stages, the stage text) through a build. */
    _buildTicker([from, to], kinds, opts) {
      const names = ['terrain', ...BUILD_STAGE_ORDER.filter((k) => kinds.includes(k))];
      const est = this._buildEstimateMs(kinds, opts);
      const t0 = performance.now();
      let shown = null;
      const tick = () => {
        const t = (performance.now() - t0) / est;
        this.setProgress(from + (to - from) * Math.min(0.97, 1 - Math.exp(-2.3 * t)));
        if (!opts.stages) return;
        const stage = names[Math.min(names.length - 1, Math.floor(t * names.length))];
        if (stage === shown) return;
        shown = stage;
        const text = stage === 'terrain' ? this._m('building_terrain') : this._m('building_layer', { feature: this._featureWords([stage]) });
        if (!text) return;
        this.setStatus(text);
        this._setStageNote(text);
      };
      tick();
      const timer = setInterval(tick, 250);
      return { stop: () => clearInterval(timer) };
    }

    /** How long this build should take: the last one with as many layers, else a first guess. */
    _buildEstimateMs(kinds, opts = {}) {
      const last = this._lastBuildMs;
      if (last && last.layers === kinds.length && !opts.export) return Math.max(2000, last.ms);
      return (BUILD_ESTIMATE_MS + BUILD_ESTIMATE_PER_LAYER_MS * kinds.length) * (this._buildIsHeavy(opts) ? 3 : 1);
    }

    /**
     * Phase 0 #3: a build the watchdog gives BUILD_WATCHDOG_HEAVY_MS, by the studio's own size
     * measures: the export grid, a tile wider than SPAN_WARN_KM (the "detail will be limited"
     * scale), Blocks buildings, or more map features to build than the Individual buildings cap.
     */
    _buildIsHeavy(opts = {}) {
      if (opts.export) return true;
      if (this._fit && this._spanKm(this._fit) > SPAN_WARN_KM) return true;
      const f = this.state.features;
      if (f.buildings && this._buildingsMode() === 'blocks') return true;
      const mf = this._mapFeatures;
      if (!mf) return false;
      const n =
        (f.buildings ? (mf.buildings || []).length : 0) +
        (f.water ? mf.waterCount || 0 : 0) +
        (f.forest ? mf.forestCount || 0 : 0) +
        (f.roads ? mf.roadsCount || 0 : 0);
      return n > INDIVIDUAL_BUILDINGS_CAP;
    }

    /**
     * Phase 0 #3: how long this build may run before the watchdog stops it. The 90 s / 180 s
     * limits hold on the reference desktop; a slower or busier device gets them times its
     * slowness, and no build gets less than WATCHDOG_ESTIMATE_X times its expected length (an
     * export, 1.5 times the preview build of the same layers). An export that already ran out of
     * time in this visit gets twice as long, then four times. config.buildWatchdogMs (tests only)
     * replaces the limit; config.buildWatchdogScale (tests only) scales it.
     */
    _buildWatchdogMs(opts = {}) {
      if (this.config.buildWatchdogMs > 0) return this.config.buildWatchdogMs;
      const kinds = this._builtFeatureKinds();
      const last = this._lastBuildMs;
      const expected = Math.max(this._buildEstimateMs(kinds, opts), opts.export && last && last.layers === kinds.length ? 1.5 * last.ms : 0);
      const base = this._buildIsHeavy(opts) ? BUILD_WATCHDOG_HEAVY_MS : BUILD_WATCHDOG_MS;
      const again = opts.export ? 2 ** Math.min(2, this._exportTimeouts || 0) : 1;
      const ms = Math.max(base * this._deviceSlowness(), WATCHDOG_ESTIMATE_X * expected) * again;
      return this.config.buildWatchdogScale > 0 ? ms * this.config.buildWatchdogScale : ms;
    }

    /**
     * Phase 0 #3: how much slower than the reference desktop this device builds now (1 to
     * DEVICE_SLOWNESS_MAX): the larger of the last preview build here (_buildWithNote) and a
     * short timing loop on the page (at most every CPU_CAL_EVERY_MS: builds while dragging come
     * often). A phone's page and worker run at about one speed; a busy desktop can slow the
     * worker alone, which the measured build shows.
     */
    _deviceSlowness() {
      const now = performance.now();
      if (!this._cpuCal || now - this._cpuCal.at > CPU_CAL_EVERY_MS) {
        let ms = Infinity;
        let sum = 0;
        for (let k = 0; k < 2; k++) {
          const t0 = performance.now();
          for (let i = 0; i < CPU_CAL_LOOPS; i++) sum += Math.sqrt(i);
          ms = Math.min(ms, performance.now() - t0);
        }
        this._cpuCal = { at: now, ms, sum }; // sum is kept, so the loop is not optimised away
      }
      return Math.min(DEVICE_SLOWNESS_MAX, Math.max(1, this._buildSlowness || 0, this._cpuCal.ms / CPU_CAL_REF_MS));
    }

    /**
     * Phase 0 #3: stop a build that has not reported in time (_buildWatchdogMs). The watchdog's
     * error settles the build first (geometryError + watchdog: _onGeometryFailure restores the
     * last good preview; Add to cart asks the shopper to try again), then its worker is replaced,
     * since the engine may still be running (`stop`, by default the worker this build was posted to).
     */
    _watchBuild(posted, opts, stop) {
      const ms = this._buildWatchdogMs(opts);
      const worker = this._worker;
      const jobs = this._workerJobs;
      let timer;
      const watchdog = new Promise((_, reject) => {
        timer = setTimeout(() => {
          const err = new Error(`Geometry build did not finish within ${Math.round(ms / 1000)} s.`);
          err.geometryError = true;
          err.watchdog = true;
          console.warn('[terrain-studio] build watchdog', ms);
          reject(err);
          if (stop) stop();
          else if (worker) this._retireWorker(worker, jobs, true);
        }, ms);
      });
      return Promise.race([posted, watchdog]).finally(() => clearTimeout(timer));
    }

    /* ---------- coastal tiles (§7 #7) ---------- */

    /**
     * §7 #7 waits for an engine that caps map-feature complexity before Manifold (§7 #2, built
     * separately), so Water on a coastal tile cannot send the shopper into a crash. Learnt from any
     * build's meta; until an engine reports the cap, Water stays the shopper's choice.
     */
    _noteEngineCaps(meta) {
      if (!meta || this._engineFeatureCap) return;
      const cap = meta.featureCap ?? meta.featureBudget ?? meta.featureComplexity;
      if (cap != null && cap !== false) this._engineFeatureCap = true;
    }

    /** The lowest elevation (m) of the raster inside the hexagon, or NaN before a raster exists. */
    _hexMinElevation() {
      const g = this._elevation;
      const fit = this._fit;
      if (!g || !g.data || !fit) return NaN;
      const R = this.config.sideMm;
      const a = (R * Math.sqrt(3)) / 2;
      const step = Math.max(1, Math.round(Math.sqrt((g.width * g.height) / 250000)));
      let min = Infinity;
      for (let j = 0; j < g.height; j += step) {
        const ay = Math.abs((g.originMerY + j * g.merPerPx - fit.mcy) * fit.k);
        if (ay > a) continue;
        const xMax = R - ay / Math.sqrt(3);
        for (let i = 0; i < g.width; i += step) {
          if (Math.abs((g.originMerX + i * g.merPerPx - fit.mcx) * fit.k) > xMax) continue;
          const e = g.data[j * g.width + i];
          if (e < min) min = e;
        }
      }
      return Number.isFinite(min) ? min : NaN;
    }

    /**
     * Turn Water on for a coastal footprint (its ground reaches SEA_FLOOR_M or lower), as the
     * sample route does, so the sea prints flat instead of as a tall block of sea floor. Only with
     * the section setting on, an engine that caps feature complexity, Water off and not switched
     * off by the shopper, once per footprint, within the four-colour budget, and only for a tile up
     * to config.autoWaterCoastMaxKm across (section auto_water_coast_max_km). A wider coastal tile,
     * or one with Water off, is built with its sea flattened to 0 m in the worker (seaFlatten).
     */
    _autoWaterForCoast(minM) {
      const s = this.state;
      if (!this.config.autoWaterCoast || !this._engineFeatureCap || s.features.water || this._waterUserOff) return false;
      // Wider than the section's span: no Water; the worker flattens the sea instead (seaFlatten).
      if (this._autoWaterSpanOver()) return false;
      if (!Number.isFinite(minM) || minM > SEA_FLOOR_M || !this._elevationKey || this._autoWaterKey === this._elevationKey) return false;
      this._autoWaterKey = this._elevationKey;
      const preset = s.preset;
      s.features.water = true;
      if (!this._colourBudget().valid) {
        s.features.water = false;
        this._noteAutoWater('off-colours');
        return false;
      }
      s.preset = 'custom';
      // Provisional until the Water lookup confirms mapped sea (_seaMapped, in _generate).
      this._autoWaterPending = { preset };
      this._applyColors({ quiet: true });
      this._render();
      this._save();
      return true;
    }

    /** The Water lookup for this footprint found mapped ocean (a coastline), not just low ground. */
    _seaMapped() {
      const mf = this._mapFeatures;
      return !!(mf && (mf._got || []).includes('water') && Array.isArray(mf.oceanPolygons) && mf.oceanPolygons.length > 0);
    }

    /** This tile is wider than the span the section turns Water on for (auto_water_coast_max_km). */
    _autoWaterSpanOver() {
      const span = this._fit ? this._spanKm(this._fit) : null;
      return Number.isFinite(span) && span > this.config.autoWaterCoastMaxKm;
    }

    /** Why the automatic coastal Water did not stay on for this footprint (meta.autoWater). */
    _noteAutoWater(v) {
      this._autoWaterNote = { key: this._elevationKey || null, v };
    }

    /**
     * Which coastal path this build took, for the studio and tests (meta.autoWater): 'on' (Water
     * on, automatic or the shopper's), 'off-span' (wider than auto_water_coast_max_km), 'off-setting'
     * (auto_water_coast off), 'off-user' (the shopper switched Water off), or, when the automatic
     * Water was tried and dropped, 'off-unmapped' (no mapped sea), 'off-lookup' (lookup failed) or
     * 'off-colours' (four-colour budget). null for a tile that is not coastal.
     */
    _autoWaterStatus(meta) {
      const hexMin = this._hexMinElevation();
      const coastal = (Number.isFinite(hexMin) && hexMin <= SEA_FLOOR_M) || this._seaFlattenedInHex(meta) > 0;
      if (!coastal) return null;
      if (this.state.features.water) return 'on';
      if (!this.config.autoWaterCoast) return 'off-setting';
      if (this._waterUserOff) return 'off-user';
      if (this._autoWaterSpanOver()) return 'off-span';
      const n = this._autoWaterNote;
      return n && n.key === (this._elevationKey || null) ? n.v : 'off';
    }

    /**
     * Sea cells the worker flattened INSIDE the hexagon (meta.seaFlattenedInHex). Sea only in the
     * raster's overhang is not flattened and does not make a tile coastal. 0 for a legacy build.
     */
    _seaFlattenedInHex(meta) {
      if (!meta) return 0;
      const n = Number.isFinite(meta.seaFlattenedInHex) ? meta.seaFlattenedInHex : meta.seaFlattened;
      return Number.isFinite(n) ? n : 0;
    }

    /** Put Water and the preset back as they were before the automatic coastal Water. */
    _undoAutoWater() {
      const prev = this._autoWaterPending;
      this._autoWaterPending = false;
      if (!prev) return;
      this.state.features.water = false;
      this.state.preset = prev.preset;
      this._applyColors({ quiet: true });
      this._render();
      this._save();
    }

    _ensureWorker() {
      if (!this._worker) {
        const worker = new Worker(this.config.workerModuleUrl, { type: 'module' });
        const jobs = new Map();
        this._worker = worker;
        this._workerJobs = jobs;
        this._workerSeq = 0;
        // hand the worker freshly-versioned module URLs so it never imports a stale build
        this._workerModules = {
          geometry: this.config.geometryModuleUrl,
          elevation: this.config.elevationModuleUrl,
          solid: this.config.solidModuleUrl,
          kernel: this.config.kernelModuleUrl,
          wasm: this.config.wasmUrl,
        };
        worker.onmessage = (e) => {
          const { id, type, error, ...rest } = e.data || {};
          const job = jobs.get(id);
          if (!job) return;
          jobs.delete(id);
          if (type === 'error') {
            // §7 #1: a WASM trap is caught inside the worker and posted as an ordinary error, and
            // the same (now broken) Manifold instance would fail every later build in ~60 ms. A
            // worker that reported a failure is never reused: the next build starts a fresh one.
            this._retireWorker(worker, jobs, true);
            job.reject(workerError(error, rest));
          } else {
            job.resolve(rest);
          }
        };
        worker.onerror = (e) => {
          console.error('[terrain-studio] worker error', e);
          // A worker that failed to load or threw outside a build: the generic failure, as before.
          this._retireWorker(worker, jobs, false);
        };
      }
      return this._worker;
    }

    /** Terminate a worker and fail anything still waiting on it; the next call starts a new one. */
    _retireWorker(worker, jobs, geometryError) {
      for (const [, job] of jobs) {
        const err = new Error('Geometry worker failed.');
        if (geometryError) {
          // Not this job's failure: it was waiting on a worker that is being replaced.
          err.geometryError = true;
          err.workerRetired = true;
        }
        job.reject(err);
      }
      jobs.clear();
      worker.terminate();
      if (this._worker === worker) {
        this._worker = null;
        this._workerJobs = null;
      }
    }

    _callWorker(type, payload) {
      const w = this._ensureWorker();
      const jobs = this._workerJobs;
      const id = ++this._workerSeq;
      return new Promise((resolve, reject) => {
        jobs.set(id, { resolve, reject });
        w.postMessage({ id, type, payload });
      });
    }

    /**
     * Build (or reuse) the tile for the current design. opts: fast (coarse grid while dragging),
     * export (the 0.4 mm grid for the cart), prebuild (§7 #3: the background export on Customise —
     * it neither fills the cache nor changes which build `_built` is, see _runPrebuild).
     *
     * A foreground export whose design matches the prebuilt one takes it (or waits for it when it
     * is still building). Any other foreground build needs the worker now, so an unfinished
     * background build is cancelled first (its worker is replaced; see _cancelPrebuild).
     */
    _buildGeometry(opts = {}) {
      const { geometryKey, payload } = this._geometryRequest(opts);
      const settle = (built) => {
        if (!opts.prebuild) {
          this._lastBuildFast = !!opts.fast;
          this._lastBuildExport = !!opts.export;
        }
        return built;
      };
      this._geometryCache ||= new Map();
      if (this._geometryCache.has(geometryKey)) {
        const cached = this._geometryCache.get(geometryKey);
        this._geometryCache.delete(geometryKey);
        this._geometryCache.set(geometryKey, cached);
        return Promise.resolve(settle(cached));
      }
      if (!opts.prebuild) {
        const pb = this._prebuild;
        if (opts.export && pb && pb.geometryKey === geometryKey && !pb.error) {
          if (pb.built) return Promise.resolve(settle(pb.built));
          // Awaited with the same watchdog; on timeout the prebuild and its worker are dropped.
          if (pb.buildPromise) return this._watchBuild(pb.buildPromise, opts, () => this._cancelPrebuild('watchdog')).then(settle);
        }
        this._yieldPrebuildWorker();
      }
      this._ensureWorker(); // sets this._workerModules before the payload reads it
      const posted = this._callWorker('build', payload());
      if (opts.prebuild && typeof opts.onPosted === 'function') opts.onPosted(this._worker);
      // Phase 0 #3: a foreground build has a watchdog (a background prebuild gives way anyway).
      return (opts.prebuild ? posted : this._watchBuild(posted, opts)).then((built) => {
        if (opts.prebuild) return built;
        const size = (b) =>
          b.positions.byteLength + b.indices.byteLength + (b.normals?.byteLength || 0) +
          (b.preview?.positions.byteLength || 0) + (b.preview?.indices.byteLength || 0);
        // 20 MB on phones that report 4 GB or less (Phase 0 #3), 64 MB elsewhere.
        const cacheLimit = typeof navigator !== 'undefined' && navigator.deviceMemory && navigator.deviceMemory <= 4 ? 20 : 64;
        if (size(built) <= cacheLimit * 1024 * 1024) {
          this._geometryCache.set(geometryKey, built);
          const total = () => [...this._geometryCache.values()].reduce((n, b) => n + size(b), 0);
          while (this._geometryCache.size > 2 || total() > cacheLimit * 1024 * 1024) {
            this._geometryCache.delete(this._geometryCache.keys().next().value);
          }
        }
        return settle(built);
      });
    }

    /**
     * What a build of the current design would send: its cache key, and the worker payload (built
     * on demand, so asking for the key alone costs nothing). The payload is exactly what
     * _buildGeometry has always posted.
     */
    _geometryRequest(opts = {}) {
      const grid = this._elevation;
      const fit = this._fit;
      const c = this.config;
      const f = this.state.features;
      // Coarser while dragging, the finer export grid for the cart, else the preview grid.
      const gridMm = opts.fast ? Math.max(c.gridMm, 0.7) : opts.export ? c.exportGridMm : c.gridMm;
      const pts = this.state.route ? this.state.route.parsed.points : [];
      // Which map features are both toggled on AND have data fetched for this area.
      const mf = this._mapFeatures;
      const got = (mf && mf._got) || [];
      const wantWater = !!(f.water && mf && got.includes('water'));
      const wantForest = !!(f.forest && mf && got.includes('forest') && mf.forestPolygons && mf.forestPolygons.length);
      const wantRoads = !!(f.roads && mf && got.includes('roads'));
      const wantBuildings = !!(f.buildings && mf && got.includes('buildings'));
      // §D.4 / §E.2: the Buildings style, and for Blocks the printable roads it is cut by (sent
      // even with the Roads layer off; the engine prints a Roads body only when geom.roads).
      const buildingsMode = wantBuildings ? this._buildingsMode(this._spanKm(fit)) : null;
      const sendRoadLines = wantRoads || !!(buildingsMode === 'blocks' && mf && got.includes('roads'));
      // §E.2: the design's route cover (v4 for new designs) at the chosen width (radius and offset in print mm) and the terrain
      // smoothing level the worker applies before sampling (1 Softened, 2 Sculpted).
      const routeWidth = this._routeWidthOpts();
      const routeVersion = ROUTE_VERSIONS_RESTORABLE.includes(this.state.routeVersion) ? this.state.routeVersion : ROUTE_VERSION;
      const terrainSmooth = this.state.sculpted ? 2 : 1;
      const wantSnow = !!(c.showSnow && f.snow);
      // OSM glacier outlines (SNOW-DESIGN §2.8), unioned into the snow cap by the engine.
      const wantGlacier = !!(wantSnow && mf && got.includes('glacier') && mf.glacierPolygons && mf.glacierPolygons.length);
      const zMult = Number.isFinite(this.state.zMult) ? this.state.zMult : c.defaultZMult;
      // Snow line (SNOW-DESIGN §6.3): absolute metres or null (auto); a legacy snowPct only
      // until its first build converts it. Natural snow for every new design.
      let snowLineM = Number.isFinite(this.state.snowLineM) ? this.state.snowLineM : null;
      // Export pins the previewed line (§1.4): in auto mode the 0.4 mm export samples other nodes
      // than the 0.5 mm preview, so its own default can land 10 m away. State stays null (auto).
      const pm = this._buildMeta;
      if (opts.export && wantSnow && snowLineM == null && !Number.isFinite(this.state.snowPctLegacy) && pm && pm.snowOffered && Number.isFinite(pm.snowLineM)) {
        snowLineM = Math.round(pm.snowLineM);
      }
      const snowPct = snowLineM == null && Number.isFinite(this.state.snowPctLegacy) ? this.state.snowPctLegacy : null;
      const snowNatural = true;
      const geometryKey = JSON.stringify([
        gridMm, zMult, wantWater, wantForest, wantRoads, wantBuildings, wantSnow,
        wantSnow ? snowLineM ?? 'auto' : null, wantSnow ? snowPct : null, wantSnow ? snowNatural : null,
        wantSnow ? c.snowClimateGuard : null, wantGlacier,
        this._mapRevision || 0, fitKey(fit),
        routeVersion, routeWidth.radiusMm, routeWidth.offsetMm, terrainSmooth, buildingsMode, sendRoadLines,
      ]);
      const payload = () => {
        // flat [lon, lat, lon, lat, …] so it clones cheaply into the worker
        const route = new Float64Array(pts.length * 2);
        for (let i = 0; i < pts.length; i++) {
          route[i * 2] = pts[i].lon;
          route[i * 2 + 1] = pts[i].lat;
        }
        return {
          route,
          routeBreaks: pts.flatMap((p, i) => (p.breakBefore ? [i] : [])),
          // Consumed by the worker (never reaches the engine); σ is resolved there and reported in
          // meta.terrainSmoothM / terrainSmoothSource, which _modelParams stores with the order.
          terrainSmooth,
          // Every new design: the raster's open sea is flattened to 0 m unless Water is built over
          // mapped sea (worker, meta.seaFlattened). A legacy design re-rendered without it is unchanged.
          seaFlatten: true,
          modules: this._workerModules || { geometry: c.geometryModuleUrl, elevation: c.elevationModuleUrl },
          elevation: {
            data: grid.data,
            width: grid.width,
            height: grid.height,
            originMerX: grid.originMerX,
            originMerY: grid.originMerY,
            merPerPx: grid.merPerPx,
          },
          fit: { k: fit.k, mcx: fit.mcx, mcy: fit.mcy, latMid: fit.latMid, vertScaleMmPerM: fit.vertScaleMmPerM },
          geom: {
            sideMm: c.sideMm,
            baseMm: c.baseMm,
            maxHeightMm: c.maxHeightMm,
            gridMm,
            zMult,
            pockets: c.magnets.map((m) => ({ x: m[0], y: m[1] })),
            pocketDiamMm: c.magnetDiamMm,
            pocketDepthMm: c.magnetDepthMm,
            roads: wantRoads,
            water: !!wantWater,
            waterSeaLevelM: null,
            forest: !!wantForest,
            snow: wantSnow,
            snowLineM,
            snowNatural,
            snowPct,
            // §3.4: offer snow only where it settles at this latitude (section snow_climate_guard).
            snowClimateGuard: c.snowClimateGuard,
            waterRaiseMm: c.waterRaiseMm,
            routeVersion,
            routeRadiusMm: routeWidth.radiusMm,
            routeOffsetMm: routeWidth.offsetMm,
            buildingsMode,
          },
          roads: sendRoadLines ? { lines: roadLinesForSpan(mf.roadLines || [], this._spanKm(fit)) } : null,
          buildings: wantBuildings ? mf.buildings || [] : [],
          // Also the Softened pass's sea mask. The worker detects a coastal tile from the raster's
          // own sea as well (§C.1, §C.3), so terrainSmoothSource and σ do not depend on whether a
          // Water lookup has run this session; these polygons only refine the mask.
          coast: mf?.oceanPolygons?.length ? { polygons: mf.oceanPolygons, holes: mf.oceanHoles || [] } : null,
          water: wantWater
            ? { polygons: mf.polygons, holes: mf.holes, lines: mf.lines, oceanPolygons: mf.oceanPolygons, oceanHoles: mf.oceanHoles, latMid: fit.latMid }
            : null,
          forest: wantForest ? { polygons: mf.forestPolygons, holes: mf.forestHoles, latMid: fit.latMid } : null,
          // lat/lon rings; the worker projects them (toModel) into the engine's snowFeatures.
          snow: wantGlacier ? { polygons: mf.glacierPolygons, holes: mf.glacierHoles || [] } : null,
        };
      };
      return { geometryKey, payload };
    }

    /** The snow line a build would request now: user metres, or null in auto mode. */
    _snowLineReq() {
      return Number.isFinite(this.state.snowLineM) ? this.state.snowLineM : null;
    }

    /**
     * Glacier outlines (SNOW-DESIGN §2.8) are looked up only with Snow on, and only while this
     * footprint may be offered snow: before its first build, or when that build said offered.
     * A footprint refused for climate or relief never costs a map query.
     */
    _wantGlaciers() {
      if (!this.config.showSnow || !this.state.features.snow) return false;
      if (this._buildMetaKnown()) return this._buildMeta.snowOffered === true;
      // No build of this footprint yet: the elevation pre-check, when it has run, decides.
      const pre = this._snowPrecheck;
      return !(pre && pre.key === this._elevationKey && pre.offered === false);
    }

    /** True when _buildMeta describes the footprint currently loaded. */
    _buildMetaKnown() {
      return !!this._buildMeta && this._buildMetaKey != null && this._buildMetaKey === this._elevationKey;
    }

    /**
     * Whether a footprint with no build yet can be offered snow, from its elevation grid alone
     * (SNOW-DESIGN §2.8 / §3.4). The grid covers more than the hexagon, so its maximum is never
     * below the engine's eMax and its relief never below the engine's: this can only say "no"
     * where the engine would also refuse (climate, or too flat). Never blocks the build: any
     * failure leaves the lookup on.
     */
    async _precheckSnowOffer() {
      const key = this._elevationKey;
      if (this._snowPrecheck && this._snowPrecheck.key === key) return this._snowPrecheck.offered;
      let offered = true;
      try {
        const stats = this._elevation && this._elevation.stats;
        const latMid = this._fit && this._fit.latMid;
        if (stats && Number.isFinite(stats.max) && Number.isFinite(stats.min)) {
          const geo = await this._geometryModule();
          const S = geo.SNOW || {};
          if (Number.isFinite(S.minReliefM) && stats.max - stats.min < S.minReliefM) offered = false;
          else if (this.config.snowClimateGuard && typeof geo.winterSnowAltitude === 'function' && Number.isFinite(latMid)) {
            const w = geo.winterSnowAltitude(Math.abs(latMid));
            const margin = Number.isFinite(S.snowOfferMarginM) ? S.snowOfferMarginM : 200;
            if (Number.isFinite(w) && stats.max < w - margin) offered = false;
          }
        }
      } catch (e) {
        offered = true;
      }
      this._snowPrecheck = { key, offered };
      return offered;
    }

    async _rebuildForZ() {
      if (this._busy || !this._elevation || !this._fit) return;
      this._rebuilding = true;
      this._setBusy(true);
      this.setProgress(0.05);
      this._updateFailed = false;
      // Status (live region) and the stage pill carry the same text, as the generate stages do.
      const note = this._m('tile_updating');
      this.setStatus(note);
      this._setStageNote(note);
      const attempt = this._designSnapshot();
      try {
        if ((await this._fitLandmarkHeight()) === false) {
          this._pending = true;
          this._updateFailed = true;
          return;
        }
        const request = this._designSnapshot();
        const built = await this._buildWithNote({ progress: [0.08, 0.96] });
        const preview = await this._ensurePreview();
        preview.setTerrain(built);
        this._previewBuilt = built;
        preview.setColors(this._currentColors());
        this._built = built;
        // A height typed while this build ran is still owed: stay pending so the queued
        // catch-up below (or the slider's own timer) rebuilds at the newer value.
        this._pending = this._zOwed(built.meta);
        this._noteEngineCaps(built.meta);
        this._applyBuildMeta(built.meta);
        this._updateDevPanel(built);
        if (!built.meta.audit.ok) throw new Error('Geometry validation failed');
        this._rememberGood(request, built);
        this.setStatus(this._m('tile_updated', { mm: built.meta.maxZmm.toFixed(1) }));
      } catch (err) {
        if (err && err.geometryError) {
          // §7 #1: the worker is already replaced; the last good tile comes back, ready for the cart.
          this._onGeometryFailure(err, attempt);
        } else {
          this._pending = true;
          this._updateFailed = true;
          this.setStatus(this._m('update_failed'), 'error');
          console.error('[terrain-studio] rebuild failed', err);
        }
      } finally {
        this._setStageNote(null);
        this.setProgress(null);
        // A snow-line move released during this rebuild is still owed (the build read the older line).
        if (this._snowQueued && this._built) this._pending = true;
        this._snowQueued = false;
        // Likewise a route width / Sculpted change made during it.
        if (this._optsQueued && this._built) this._pending = true;
        this._optsQueued = false;
        this._rebuilding = false;
        this._setBusy(false);
        // A slider move during the rebuild leaves the model one step behind: catch up.
        if (this._pending && this._built && this._zQueued) {
          this._zQueued = false;
          this._rebuildForZ();
        }
      }
    }

    /**
     * Retry after a failed Customise rebuild (worker error, or a map lookup that failed after a
     * preset change): fetch any missing map layers first, then rebuild at the current height.
     * Offered by the bar's Update preview on the Customise step (_primary).
     */
    async _retryPending() {
      if (this._busy || !this._built || !this._elevation || !this._fit) return;
      // The shopper asked: background retries of left-out features (#7) start again afterwards.
      clearTimeout(this._leftOutTimer);
      this._leftOutTimer = null;
      this._leftOutRetry = null;
      this._setBusy(true);
      let ok;
      try {
        ok = await this._withMapData();
      } finally {
        this._setBusy(false);
      }
      if (ok !== false) await this._rebuildForZ();
      this._scheduleLeftOutRetry();
      this._render();
    }

    _onZMultInput(value) {
      if (!Number.isFinite(value)) return;
      this.state.zMult = value;
      if (this.els.zmultOutput) this.els.zmultOutput.textContent = this._fmtZOut(value);
      this._syncZNumber(value);
      if (!this._elevation || !this._built) return;
      this._pending = true;
      this._updateFailed = false; // the move itself is the retry
      this._invalidateModel();
      this._renderBar();
      clearTimeout(this._zTimer);
      this._zTimer = setTimeout(() => {
        if (this._busy) this._zQueued = true;
        else this._rebuildForZ();
      }, 260);
      this._save();
    }

    /** "3.50×" — the height readout (two decimals, as a typed value or a computed cap needs). */
    _fmtZOut(v) {
      return `${Number(v).toFixed(2)}×`;
    }

    /**
     * True when the shopper changed the height after this build was requested (typed during a
     * rebuild): the build is one step behind and a catch-up rebuild is owed.
     */
    _zOwed(meta) {
      const want = this.state.zMult;
      return !!meta && Number.isFinite(meta.zMultRequested) && Number.isFinite(want) && Math.abs(meta.zMultRequested - want) > 1e-9;
    }

    /**
     * Mirror the height slider into the typed field: the same min/max (the build's computed cap)
     * and the value, two decimals. Left alone while the shopper is typing in it.
     */
    _syncZNumber(value) {
      const num = this.els.zmultNumber;
      if (!num) return;
      const slider = this.els.zmult;
      if (slider) {
        if (num.min !== slider.min) num.min = slider.min;
        if (num.max !== slider.max) num.max = slider.max;
      }
      if (this._zTyping && document.activeElement === num) return;
      const v = Number.isFinite(value) ? value : parseFloat(slider ? slider.value : this.state.zMult);
      if (!Number.isFinite(v)) return;
      const text = Number(v).toFixed(2);
      if (num.value !== text) num.value = text;
    }

    /**
     * Typed height (change / Enter): parse, clamp into the slider's [min, max] (max = the
     * computed cap), round to two decimals, write back to the field and the slider, then take the
     * slider's path (_onZMultInput: debounced rebuild). A value pulled into range says so.
     */
    _commitZNumber() {
      const num = this.els.zmultNumber;
      const slider = this.els.zmult;
      if (!num || !slider || num.disabled) return;
      // Nothing typed since the last sync (Enter on the shown value, or change after Enter): the
      // shown text may be a rounded cap, so re-committing it would read as out of range.
      if (!this._zTyping) {
        this._syncZNumber();
        return;
      }
      this._zTyping = false;
      const raw = parseFloat(num.value);
      if (!Number.isFinite(raw)) {
        this._syncZNumber(this.state.zMult);
        return;
      }
      const min = parseFloat(slider.min) || 0.1;
      const max = Math.max(min, parseFloat(slider.max) || 8);
      let v = Math.round(raw * 100) / 100;
      let which = null;
      // Two decimals must not round past the cap (the engine would clamp and say "Capped").
      if (v > max) {
        v = Math.floor(max * 100 + 1e-6) / 100;
        if (raw > max) which = 'max';
      } else if (v < min) {
        v = Math.ceil(min * 100 - 1e-6) / 100;
        which = 'min';
      }
      this._zAdjusted = which ? { which, x: v, max } : null;
      num.value = v.toFixed(2);
      slider.value = String(v);
      this._renderZHint();
      if (Math.abs(v - this.state.zMult) < 1e-9) {
        // Same height (e.g. Enter then blur): nothing to rebuild, just tidy the readout.
        if (this.els.zmultOutput) this.els.zmultOutput.textContent = this._fmtZOut(v);
        return;
      }
      this._onZMultInput(v);
    }

    /** The height hint: a typed value pulled into range, else the last build's cap / maximum. */
    _renderZHint(meta = this._buildMeta) {
      const el = this.els.zmultHint;
      if (!el) return;
      const adj = this._zAdjusted;
      let text;
      if (adj) {
        text = (adj.which === 'max' ? this.t.heightAdjustedMax : this.t.heightAdjustedMin).split('[x]').join(adj.x.toFixed(2));
      } else if (meta) {
        text = meta.zMultClamped
          ? this._m('height_capped', { x: meta.zMultUsed.toFixed(2), mm: this.config.maxHeightMm })
          : this._m('height_max', { x: meta.zMultMax.toFixed(2) });
      } else {
        text = '';
      }
      // Unchanged text is not rewritten: the hint is a live region.
      if (el.textContent !== text) el.textContent = text;
    }

    /** Reflect the build back into the UI: clamp the slider, warn if exaggeration was capped. */
    _applyBuildMeta(meta) {
      this._buildMeta = meta;
      // Which footprint this meta describes (the glacier lookup asks whether it offered snow).
      this._buildMetaKey = this._elevationKey;
      // A feature whose lookup ran out of time is named as left out, not as "nothing mapped".
      const leftOut = this._leftOut();
      // A layer the engine left out because the tile would be too complex to build (its feature
      // budget, meta.featuresDropped) is named as such, not as "nothing mapped".
      const tooDetailed = FEATURE_ORDER.filter((key) => this.state.features[key] && Array.isArray(meta.featuresDropped) && meta.featuresDropped.includes(key));
      const missing = ['water', 'forest', 'roads'].filter((key) => this.state.features[key] && !meta[key] && !leftOut.includes(key) && !tooDetailed.includes(key));
      // Terrarium carries bathymetry. New designs flatten the sea inside the hexagon to 0 m
      // (meta.seaFlattenedInHex), and the hint ("The sea is shown flat…") shows only then: a pit
      // below −10 m that is not open sea is neither flat nor sea. A legacy build (no seaFlatten)
      // keeps the old trigger, ground below −10 m, where the sea floor is modelled as terrain.
      this._seaDetected = !this.state.features.water && (meta.seaFlatten === true
        ? this._seaFlattenedInHex(meta) > 0
        : Number.isFinite(meta.elevationMinM) && meta.elevationMinM < SEA_FLOOR_M);
      // Which coastal path ran (tests and the dev panel). meta is this build's own object.
      meta.autoWater = this._autoWaterStatus(meta);
      // Glaciers are optional (SNOW-DESIGN §2.8): a failed lookup builds snow from the line alone.
      const glaciersMissing = !!(this.state.features.snow && meta.snowOffered && this._mapFeatures && this._mapFeatures._glacierFailed);
      this._showWarnings([
        ...this._framingWarnings(),
        ...missing.map((key) => this._m(`missing_${key}`)),
        ...(leftOut.length ? [this._m('features_left_out', { features: this._featureWords(leftOut, true) })] : []),
        ...(tooDetailed.length ? [this._m('features_too_detailed', { features: this._featureWords(tooDetailed, true) })] : []),
        ...(glaciersMissing ? [this._m('glaciers_unavailable')] : []),
        ...(this._seaDetected ? [this._m('sea_detected')] : []),
      ]);
      // A height typed during this rebuild is newer than the one it built: keep it on the
      // controls (and in state) for the catch-up rebuild instead of snapping back.
      const owed = this._zOwed(meta);
      if (this.els.zmult) {
        // A computed cap need not be a multiple of the original 0.1 slider step.
        this.els.zmult.step = 'any';
        this.els.zmult.max = String(Math.max(0.2, meta.zMultMax));
        if (!owed) this.els.zmult.value = String(meta.zMultUsed);
      }
      if (!owed) {
        this.state.zMult = meta.zMultUsed;
        if (this.els.zmultOutput) this.els.zmultOutput.textContent = this._fmtZOut(meta.zMultUsed);
      }
      this._syncZNumber(owed ? this.state.zMult : meta.zMultUsed);
      this._applySnowMeta(meta);
      // A typed-value notice outlives the rebuild it caused, but not a new cap or another height.
      const adj = this._zAdjusted;
      if (adj && !owed && !(Math.abs(Math.max(0.2, meta.zMultMax) - adj.max) < 1e-6 && Math.abs(meta.zMultUsed - adj.x) < 0.005)) this._zAdjusted = null;
      this._renderZHint(meta);
      if (meta.audit && !meta.audit.ok) console.error('[terrain-studio] manifold audit failed', meta.audit.errors);
    }

    /** Snow line after a build (SNOW-DESIGN §5): range, value, readout and hint from meta. */
    _applySnowMeta(meta) {
      const s = this.state;
      const range = this._snowRange(meta);
      const line = Number.isFinite(meta.snowLineM) ? Math.round(meta.snowLineM) : null;
      // A legacy snowPct design: the engine converted it; keep that line as the shopper's own.
      if (range && s.snowPctLegacy != null && meta.snow && line != null) {
        s.snowLineM = line;
        s.snowLineUser = true;
        s.snowPctLegacy = null;
        this._save();
      }
      // Auto mode never copies the default back into state (it would stop recomputing). The
      // edit flag clears once this build carries the shopper's line (or clamped it, or the
      // engine does not report one); a queued rebuild otherwise still owes the newer value.
      if (s.snowLineM == null || line == null || !range || line === s.snowLineM || meta.snowLineClamped) this._snowEdited = false;
      this._renderSnowField();
    }

    /* =====================================================================
     * 3D preview
     * ===================================================================*/

    async _ensurePreview() {
      if (this._previewPromise) return this._previewPromise;
      this._previewPromise = (async () => {
        const mod = await import(this.config.previewModuleUrl);
        const preview = await mod.createPreview({
          host: this.els.canvas,
          config: { ...this.config, backpieceColour: this.config.backpieceColour },
          devMode: this._devMode,
        });
        this._preview = preview;
        preview.setColors(this._currentColors());
        this._onPreviewReady?.(preview);
        return preview;
      })();
      this._previewPromise.catch(() => {
        this._previewPromise = null;
      });
      return this._previewPromise;
    }

    /** The navy backpiece and a soft wall around the tile (preview only). */
    _setBackpiece(on) {
      this.state.backpiece = !!on;
      this._preview?.setBackpiece?.(this.state.backpiece);
      this._emit('ttv:studio_backpiece', { on: this.state.backpiece });
      this._render();
      this._save();
      // backpiecePreview rides in design.json: the prebuilt 3MF follows it (never the backpiece mesh).
      this._schedulePrebuild(PREZIP_SETTLE_MS);
    }

    _onPreviewReady(preview) {
      const c = this.config;
      if (!c.backpieceUrl || !preview.loadBackpiece) return;
      preview
        .loadBackpiece(c.backpieceUrl, c.backpieceColour)
        .then(() => preview.setBackpiece(!!this.state.backpiece))
        .catch((err) => console.warn('[terrain-studio] backpiece preview unavailable', err));
    }

    /* =====================================================================
     * Colours — v12 budget and presets, swatch inputs
     * ===================================================================*/

    _currentColors() {
      return { ...this.config.colors, ...this.state.colours };
    }

    // Four PLA filament slots; no dedicated PETG interface is reserved.
    // The switches count (what the next build will try to print), with one exception for Snow
    // (SNOW-DESIGN §5): Snow that is on but that a CURRENT build did not print (not offered, or
    // culled to nothing) costs no colour. Every other feature counts exactly as before.
    _colourBudget() {
      const colors = this._currentColors();
      const on = this.state?.features || {};
      const f = this._snowNotPrinted() ? { ...on, snow: false } : on;
      const keys = ['terrain', 'route', ...ALL_FEATURES.filter((k) => f[k] && (k !== 'snow' || this.config.showSnow))];
      const count = new Set(keys.map((k) => String(colors[k]).toUpperCase())).size;
      return { colors, keys, count, valid: count <= 4 };
    }

    _updateColourBudget({ silent = false } = {}) {
      const budget = this._colourBudget();
      const summary = this.els.colourBudget;
      if (summary) summary.textContent = (summary.dataset.template || '[count] of 4 colours used').replace('[count]', String(budget.count));
      if (this.els.colourError) this.els.colourError.hidden = budget.valid;
      for (const input of this.els.colourInputs) {
        const key = input.dataset.tsColour;
        const active = budget.keys.includes(key);
        const others = new Set(budget.keys.filter((k) => k !== key).map((k) => String(budget.colors[k]).toUpperCase()));
        input.checked = String(budget.colors[key]).toUpperCase() === input.value.toUpperCase();
        // Allow replacing a unique colour, or reusing an existing one. An enabled feature
        // may reveal an over-budget saved/default choice; keep it visible until the shopper
        // resolves it, without silently recolouring their design.
        input.disabled = !!this._busy || (active && !input.checked && others.size >= 4 && !others.has(input.value.toUpperCase()));
      }
      for (const row of this.els.colourRows) {
        const key = row.dataset.tsColourRow;
        row.querySelector('[role="radiogroup"]')?.setAttribute('aria-invalid', String(budget.keys.includes(key) && !budget.valid));
        const name = row.querySelector('[data-ts-colour-name]');
        if (name) name.textContent = this._colorName(budget.colors[key]);
      }
      // Every colour change passes through here, so the code always matches the swatches.
      if (this.els.colourCode) this.els.colourCode.value = this._colourCode(budget);
      this._renderSnowBudget(budget);
      if (!silent) this._emit('ttv:colours_changed', { count: budget.count, valid: budget.valid });
      return budget.valid;
    }

    /**
     * Fifth-colour hint under the Snow switch (SNOW-DESIGN §5): raised when the shopper turns
     * Snow on and it takes the design past four colours; cleared once it is back to four (or
     * Snow is off).
     */
    _renderSnowBudget(budget = this._colourBudget()) {
      const snowOn = !!(this.config.showSnow && this.state.features.snow);
      if (!snowOn || budget.count <= 4) this._snowBudgetHint = false;
      const el = this.els.snowBudget;
      if (!el) return;
      // The element is a live region that stays in the accessibility tree (never `hidden`; empty
      // it is visually hidden by CSS), so only its text changes and the change is announced.
      const text = this._snowBudgetHint ? this._snowText('budget') : '';
      if (el.textContent !== text) el.textContent = text;
    }

    _checkColourBudget() {
      if (this._updateColourBudget()) return true;
      if (this.els.colourError) this.setStatus(this.els.colourError.textContent.trim(), 'error');
      return false;
    }

    async _applyPreset(name) {
      if (this._busy) {
        this._render();
        return;
      }
      if (name === 'custom') {
        this.state.preset = 'custom';
        this._save();
        return;
      }
      if (!PRESETS[name]) return;
      this.state.preset = name;
      if (this.state.features.water && !PRESETS[name].includes('water')) this._waterUserOff = true;
      for (const key of ALL_FEATURES) this.state.features[key] = PRESETS[name].includes(key);
      if (this.state.features.buildings && ((this._fit && this._buildingsBlocked(this._spanKm(this._fit))) || this._buildingsTooMany())) {
        this.state.features.buildings = false;
        if (!this._buildingsTooMany()) this.setStatus(this._m('buildings_span', { km: this.config.buildingsBlocksMaxSpanKm }));
      }
      // Presets are predictable starting points using the store's filament defaults.
      this.state.colours = { ...this.config.colors };
      this._invalidateModel();
      this._applyColors({ quiet: true });
      this._render();
      this._save();
      if (!this._elevation || !this._built) return;
      this._pending = true;
      this._setBusy(true);
      let ok;
      try {
        ok = await this._withMapData();
      } finally {
        this._setBusy(false);
      }
      if (ok === false) {
        // The lookup failed: offer Update preview (bar and Map pane) instead of a dead end.
        this._updateFailed = true;
        this._render();
        return;
      }
      await this._rebuildForZ();
    }

    _applyColors({ quiet = false } = {}) {
      const hadModel = !!this._model;
      this._invalidateModel();
      const colors = this._currentColors();
      if (this._preview && this._preview.setColors) this._preview.setColors(colors);
      if (hadModel && !quiet) this.setStatus(this._m('colours_after_cart'));
      this._updateColourBudget();
      this._renderBar();
      if (this._stageMode() === 'canvas') this._drawRouteCanvas();
      this._mapApi?.setRouteColour?.(colors.route);
      // A colour is in the 3MF (displaycolor and design.json), not in the geometry: re-zip sooner.
      this._schedulePrebuild(PREZIP_SETTLE_MS);
    }

    _colorName(hex) {
      const found = this.config.palette.find((p) => p.hex === String(hex).toUpperCase());
      return found ? found.name : hex;
    }

    /** The layers printed on this tile, in the order of the Colours line. */
    _printedLayers(built = this._built) {
      const feat = this._activeFeatures(built);
      return ['terrain', 'route', ...['water', 'roads', 'buildings', 'forest', 'snow'].filter((k) => feat[k])];
    }

    /** The stocked filament for a palette colour ("" when the palette does not record one). */
    _filamentFor(hex) {
      const found = this.config.palette.find((p) => p.hex === String(hex).toUpperCase());
      return (found && found.filament) || '';
    }

    _syncInputsFromState() {
      const s = this.state;
      if (this.els.zmult) {
        this.els.zmult.value = String(s.zMult);
        if (this.els.zmultOutput) this.els.zmultOutput.textContent = this._fmtZOut(Number(s.zMult));
      }
      this._zAdjusted = null;
      this._zTyping = false;
      this._syncZNumber(Number(s.zMult));
      this._renderZHint();
      this._renderSnowField();
      this._updateColourBudget({ silent: true });
    }

    /* =====================================================================
     * Cart — AJAX add with the v12 property contract
     * ===================================================================*/

    _invalidateModel() {
      this._model = null;
      this._addedModelKey = null;
      // §7 #3: every design change passes through here; the background export follows the design
      // once it settles (a colour alone only re-makes the 3MF, see _runPrebuild).
      this._schedulePrebuild();
    }

    /**
     * Snow is switched on, but the current build (nothing pending, not failed) printed none:
     * not offered (climate/flat) or culled to nothing. Such Snow costs no colour and its colour
     * row is hidden.
     */
    _snowNotPrinted() {
      return !!(
        this.state?.features?.snow &&
        this._built &&
        !this._designDirty &&
        !this._updateFailed &&
        this._built.meta &&
        this._built.meta.snow === false
      );
    }

    /** Which features actually made it into the last build (meta, with toggle fallback). */
    _activeFeatures(built = this._built) {
      const m = (built && built.meta) || {};
      const f = this.state.features;
      return {
        roads: 'roads' in m ? !!m.roads : !!f.roads,
        buildings: 'buildings' in m ? !!m.buildings : !!f.buildings,
        water: 'water' in m ? !!m.water : !!f.water,
        forest: 'forest' in m ? !!m.forest : !!f.forest,
        snow: 'snow' in m ? !!m.snow : !!f.snow,
      };
    }

    _sourceTag() {
      const r = this.state.route;
      if (!r) return null;
      if (r.source === 'strava') {
        const ids = (r.legs || []).map((l) => l.stravaId).filter(Boolean);
        return `strava:${ids.join(',')}`;
      }
      return r.source === 'sample' ? 'sample' : 'gpx';
    }

    /** `built`: the build the file is made from (default the current one; the prebuilt export, §7 #3). */
    _modelParams(built = this._built) {
      const m = (built && built.meta) || {};
      const feat = this._activeFeatures(built);
      const fit = this._fit;
      const product = this.products[this.state.product];
      return {
        gpxName: this._routeName(),
        distanceKm: this.state.route?.parsed?.stats?.distanceKm ?? null,
        zMult: m.zMultUsed ?? null,
        reliefM: m.reliefM ?? null,
        heightMm: m.maxZmm ?? null,
        triangles: m.triangleCount ?? null,
        roads: feat.roads,
        buildings: feat.buildings,
        preset: this.state.preset || 'plain',
        water: feat.water,
        forest: feat.forest,
        snow: feat.snow,
        // Snow line contract (SNOW-DESIGN §6.3): all from the build that produced the file.
        snowPct: feat.snow ? (Number.isFinite(m.snowPct) ? m.snowPct : this.state.snowPctLegacy ?? null) : null,
        snowLineM: feat.snow && Number.isFinite(m.snowLineM) ? Math.round(m.snowLineM) : null,
        snowNatural: feat.snow && typeof m.snowNatural === 'boolean' ? m.snowNatural : null,
        // ROUTE-AND-BUILDINGS-DESIGN §E.3, all from the build that produced the file, so a
        // re-render never depends on a later change to the rules: the route cover version and its
        // resolved constants, the width, and the resolved terrain σ with its elevation source.
        routeWidthMm: Number.isFinite(m.routeWidthMm) ? m.routeWidthMm : null,
        routeOffsetMm: Number.isFinite(m.routeOffsetMm) ? m.routeOffsetMm : null,
        routeVersion: Number.isFinite(m.routeVersion) ? m.routeVersion : null,
        routeSmoothing: m.routeSmoothing && typeof m.routeSmoothing === 'object' ? { ...m.routeSmoothing } : null,
        // Route cover v4 (§E.2b): what the build did to the ground under the route (the largest cut
        // and fill, the area moved, where it was left untouched) and how the path was tidied.
        routeGrade: m.routeGrade && typeof m.routeGrade === 'object' ? { ...m.routeGrade } : null,
        routeFallback: m.routeFallback && typeof m.routeFallback === 'object' ? { ...m.routeFallback } : null,
        routeTidy: m.routeTidy && typeof m.routeTidy === 'object' ? { ...m.routeTidy } : null,
        // Since the review of 2 Oct 2026: where legs meet (the junction zones and their fillets), the
        // Terrain cap route v4 applies on steep or sheer ground (and whether it bound), and whether a route
        // was given that left no bead on the tile.
        routeJunction: m.routeJunction && typeof m.routeJunction === 'object' ? { ...m.routeJunction } : null,
        routeZMultCap: Number.isFinite(m.routeZMultCap) ? m.routeZMultCap : null,
        routeZCapped: m.routeZCapped && typeof m.routeZCapped === 'object' ? { ...m.routeZCapped } : null,
        routeEmpty: m.routeEmpty === true ? true : null,
        terrainSmooth: Number.isFinite(m.terrainSmooth) ? m.terrainSmooth : null,
        terrainSmoothM: Number.isFinite(m.terrainSmoothM) ? m.terrainSmoothM : null,
        terrainSmoothSource: typeof m.terrainSmoothSource === 'string' ? m.terrainSmoothSource : null,
        // Which sea mask the coastal pass used ('ocean' polygons, the raster's own 'sea', 'none').
        terrainSmoothMask: typeof m.terrainSmoothMask === 'string' ? m.terrainSmoothMask : null,
        // Sea flattening (worker): true for every new design, so a re-render flattens the same sea.
        seaFlatten: m.seaFlatten === true ? true : null,
        seaFlattened: Number.isFinite(m.seaFlattened) ? m.seaFlattened : null,
        seaFlattenedInHex: Number.isFinite(m.seaFlattenedInHex) ? m.seaFlattenedInHex : null,
        // null when Buildings is off.
        buildingsMode: feat.buildings ? m.buildingsMode || null : null,
        buildingsModeSource: feat.buildings && m.buildingsMode ? this._buildingsModeSource(fit ? this._spanKm(fit) : null) : null,
        // Only when the glacier lookup failed: this tile's snow was built from the line alone.
        ...(feat.snow && this._mapFeatures && this._mapFeatures._glacierFailed ? { snowGlaciers: 'unavailable' } : {}),
        colors: this._currentColors(),
        filaments: Object.fromEntries(this._printedLayers(built).map((k) => [k, this._filamentFor(this._currentColors()[k]) || null])),
        generator: 'ttv-3.0.0',
        source: this._sourceTag(),
        product: product ? product.handle : null,
        fit: fit
          ? {
              lat: round(mercYToLat(fit.mcy), 5),
              lon: round(mercXToLon(fit.mcx), 5),
              spanKm: round(this._spanKm(fit), 2),
            }
          : null,
        backpiecePreview: !!this.state.backpiece,
      };
    }

    /** Analytics payload: no coordinates, no file URLs. */
    _analyticsParams() {
      const p = this._modelParams();
      return {
        product: p.product,
        source: this.state.route?.source || null,
        zMult: p.zMult,
        heightMm: p.heightMm,
        triangles: p.triangles,
        preset: p.preset,
        features: ALL_FEATURES.filter((k) => p[k]),
        colours: this._colourBudget().count,
        backpiecePreview: p.backpiecePreview,
        routeWidthMm: p.routeWidthMm,
        terrainSmooth: p.terrainSmooth,
        buildingsMode: p.buildingsMode,
      };
    }

    _cartProperties() {
      const colors = this._currentColors();
      const feat = this._activeFeatures();
      const label = (hex) => this._colorName(hex);
      const props = {};
      // Customer-visible: a reassurance line only — no download link (the print file must
      // not be retrievable by the shopper). Vestige gets the file from the order via the
      // hidden `_ttv_model` gated URL / `_ttv_key`.
      const routeName = this._routeName();
      props['Custom terrain model'] = 'Attached to this order ✓' + (routeName ? ` — ${routeName}` : '');
      let colourLine = `Terrain ${label(colors.terrain)}, Route ${label(colors.route)}`;
      if (feat.water) colourLine += `, Water ${label(colors.water)}`;
      if (feat.roads) colourLine += `, Roads ${label(colors.roads)}`;
      if (feat.buildings) colourLine += `, Buildings ${label(colors.buildings)}`;
      if (feat.forest) colourLine += `, Forest ${label(colors.forest)}`;
      if (feat.snow) colourLine += `, Snow ${label(colors.snow)}`;
      props['Colours'] = colourLine;
      if (this.config.preorder) props['Pre-order'] = this.config.preorder;
      props['_ttv_model'] = this._model.url;
      if (this._model.key) props['_ttv_key'] = this._model.key;
      props['_ttv_params'] = JSON.stringify(this._model.params);
      props['_ttv_colors'] = JSON.stringify(colors);
      props['_ttv_features'] = JSON.stringify(feat);
      props['_ttv_attribution'] = this.config.attribution;
      props['_ttv_source'] = this._sourceTag();
      // For the print bench: shoppers see Vestige colour names; this line names the filament to load.
      props['_ttv_filaments'] = this._printedLayers()
        .map((k) => {
          const hex = String(colors[k]).toUpperCase();
          return `${k[0].toUpperCase()}${k.slice(1)}: ${label(hex)} = ${this._filamentFor(hex) || 'filament not recorded'} ${hex}`;
        })
        .join('; ');
      return props;
    }

    async _addToCart() {
      if (!this._checkColourBudget()) return;
      const product = this.products[this.state.product];
      if (!product) {
        this.setStatus(this._m('need_product'), 'error');
        return;
      }
      if (!product.available) {
        this.setStatus(this._m('product_unavailable'));
        return;
      }
      if (!this._built || this._busy || this._designDirty) return;
      // Phase 0 #7: never an incomplete preview (a map feature the shopper chose is missing).
      if (this._leftOut().length) return;
      if (!this.config.workerUrl) {
        this.setStatus(this._m('upload_not_configured'), 'error');
        return;
      }
      if (this._built.meta.audit && !this._built.meta.audit.ok) {
        this.setStatus(this._m('geometry_error_cart'), 'error');
        return;
      }
      this._adding = true;
      this._setBusy(true);
      this.setProgress(0.1);
      this._emit('ttv:add_to_cart_start', this._analyticsParams());
      let cartAttempted = false;
      const cartT0 = performance.now();
      this._cartTiming = null;
      try {
        if (this._model && (this._model.key || this._model.url)) {
          // A retry after a failed or uncertain cart add. Every design change nulls _model, so the
          // uploaded file still matches: reuse it (no second 3MF, no second upload) — and first
          // look in the cart, in case the lost reply had in fact landed.
          this.setStatus(this._m('adding_to_cart'));
          this._setStageNote(this._m('adding_to_cart'));
          const already = await this._confirmCartModel(this._model, { attempts: 1 });
          if (already) {
            await this._cartAdded({ items: [already] });
            return;
          }
        } else {
          // §7 #3: what came ready from the background (export, 3MF, upload) and how long each
          // stage took from this click — for the tests and the analytics event below.
          const timing = (this._cartTiming = { path: 'direct', export: null, exportMs: 0, zip: null, zipMs: 0, upload: null, uploadMs: 0, toModelMs: null, addedMs: null });
          const tClick = performance.now();
          const token = await this._turnstileToken();
          this.setProgress(0.2);

          this.setStatus(this._m('preparing'));
          // The preview is built on the coarser preview grid: export at full resolution.
          let exportFallback = null;
          if (this._lastBuildFast || !this._lastBuildExport) {
            // Usually built in the background on Customise already, or still building (then it is
            // awaited, not started again): _buildGeometry hands over the prebuilt export.
            const pre = this._prebuildFor();
            const tExport = performance.now();
            const previewBuilt = this._built;
            const exportKey = this._geometryRequest({ export: true }).geometryKey;
            // The previewed tile passed the same manifold audit on the 0.5 mm grid, so when the
            // export grid cannot be built the shopper's design stays purchasable: that model goes
            // to the order, and the order says so (exportFallback, gridMm) for the print bench.
            const canFallBack = !this._lastBuildFast && !!previewBuilt?.meta?.audit?.ok;
            const fallBack = () => {
              this._built = previewBuilt;
              exportFallback = { exportFallback: 'preview_grid', gridMm: previewBuilt.meta.gridMm };
            };
            if (canFallBack && this._exportTrapKeys && this._exportTrapKeys.has(exportKey)) {
              // §7 #1: this very export already trapped the engine in this visit (the background
              // build on Customise, or an earlier Add to cart). Building it again on a fresh
              // worker would only fail again, after the longest wait of the visit.
              console.warn('[terrain-studio] export known to exceed the engine; using the previewed model');
              fallBack();
            } else {
              timing.export = pre && pre.built ? 'prebuilt' : pre && pre.buildPromise && !pre.error ? 'awaited' : 'built';
              if (timing.export !== 'prebuilt') {
                this.setStatus(this._m('rendering'));
                this._setStageNote(this._m('rendering'));
              }
              try {
                this._built = await this._buildWithNote({ export: true });
              } catch (err) {
                // A WASM trap (the worker is already replaced) or the engine refusing the finer
                // grid falls back; a worker that did not load is a network fault: Add to cart
                // reports it and the shopper retries.
                if (err && err.engineTrap) this._noteExportTrap(exportKey);
                // Phase 0 #3: an export the watchdog stopped is slow, not broken. The coarser
                // preview grid would change the print, so the shopper tries again instead (with
                // a longer limit, _buildWatchdogMs).
                if (err && err.watchdog) {
                  this._exportTimeouts = (this._exportTimeouts || 0) + 1;
                  throw withMsg(this._m('cart_build_timeout'));
                }
                const exportBroken = !!(err && err.geometryError && !err.workerLoad && !err.workerRetired);
                if (!exportBroken || !canFallBack) throw err;
                console.warn('[terrain-studio] export build failed; using the previewed model', err);
                fallBack();
              }
            }
            this._updateDevPanel(this._built);
            if (!this._built.meta.audit.ok) throw withMsg(this._m('geometry_error_cart'));
            if (!exportFallback && this._lastGood && this._lastGood.previewBuilt === previewBuilt) {
              this._lastGood.built = this._built;
              this._lastGood.lastBuildExport = true;
              this._lastGood.lastBuildFast = false;
            }
            if (exportFallback) timing.export = 'preview_grid';
            timing.exportMs = Math.round(performance.now() - tExport);
          } else {
            timing.export = 'current';
          }
          // The file's inputs, exactly as before (name, colours, the verbatim GPX, _ttv_params).
          const file = this._cartFileInputs(this._built, exportFallback);
          const params = file.params;
          const tZip = performance.now();
          let blob = await this._takePrebuiltZip(this._built, file);
          timing.zip = blob ? 'prebuilt' : 'built';
          if (!blob) {
            const mesh = await import(this.config.meshModuleUrl);
            blob = await mesh.build3MF(this._built, {
              name: file.name,
              colors: file.colors,
              gpxText: file.gpxText,
              params,
            });
          }
          timing.zipMs = Math.round(performance.now() - tZip);
          this.setProgress(0.5);

          // Never uploaded twice: a pre-upload of exactly this file (§7 #3, opt-in) is used as is.
          const tUpload = performance.now();
          let uploaded = await this._takePreupload(file.zipKey);
          timing.upload = uploaded ? 'prebuilt' : 'uploaded';
          if (!uploaded) {
            this.setStatus(this._m('uploading'));
            this._setStageNote(this._m('uploading'));
            uploaded = await this._uploadModel(blob, token);
          }
          timing.uploadMs = Math.round(performance.now() - tUpload);
          this.setProgress(0.8);
          this._model = { url: uploaded.downloadUrl, key: uploaded.key, params };
          timing.path =
            timing.upload === 'prebuilt'
              ? 'preuploaded'
              : timing.zip === 'prebuilt'
                ? 'prebuilt_3mf'
                : timing.export === 'prebuilt' || timing.export === 'awaited'
                  ? 'prebuilt_export'
                  : 'direct';
          timing.toModelMs = Math.round(performance.now() - tClick);
          this._emit('ttv:model_uploaded', { bytes: blob.size, prebuilt: timing.path, ms: timing.toModelMs });
        }

        this.setStatus(this._m('adding_to_cart'));
        this._setStageNote(this._m('adding_to_cart'));
        cartAttempted = true;
        const added = await this._postCartAdd(product.variantId, this._cartProperties());
        await this._cartAdded(added);
        if (this._cartTiming && this._cartTiming.addedMs == null) this._cartTiming.addedMs = Math.round(performance.now() - cartT0);
      } catch (err) {
        // The reply was lost or unreadable, so the line may still be in the cart: look before
        // reporting, and a retry cannot create a duplicate line (v12's _confirmCartModel).
        if (err && err.cartUncertain && this._model) {
          const item = await this._confirmCartModel(this._model);
          if (item) {
            await this._cartAdded({ items: [item] });
            return;
          }
        }
        if (err && err.userMessage) this.setStatus(err.userMessage, 'error');
        else {
          console.error('[terrain-studio] add to cart failed', err);
          this.setStatus(this._m('cart_failed'), 'error');
        }
        this._emit('ttv:add_to_cart_error', { message: (err && err.message) || 'unknown', cartAttempted: cartAttempted && !!err?.cartUncertain });
      } finally {
        this._adding = false;
        this._setStageNote(null);
        this.setProgress(null);
        this._setBusy(false);
      }
    }

    /** The line is in the cart: remember the model, tell the shopper, hand over to the cart UI. */
    async _cartAdded(added) {
      this._addedModelKey = this._model.key || this._model.url;
      this._model.key = this._addedModelKey;
      // Bundle products are split into their component lines at checkout (Simple Bundles 2.0
      // cart transform) and the line's properties do not survive the split. The order-level
      // attributes below do, so the print bench always finds the design on the order.
      await this._recordOrderAttributes();
      this.setProgress(1);
      this.setStatus(this.t.added);
      // The design is in the cart now; the device no longer needs to remember it.
      this._clearAutosave();
      await this._afterCartAdd(added);
    }

    /**
     * Mirror the design onto the cart (order note attributes): one numbered block per design
     * added in this cart, readable in the order's "Additional details". Non-fatal: the line
     * properties still carry everything for products that are not bundles.
     *
     * Read the cart, merge our block into its attributes, write, then read again: a cart app
     * writing its own attributes at the same moment can overwrite ours (order #1002, a single
     * Terrain Tile: no "Terrain design 1" / _ttv_design_1_* but the Essential Cart Drawer's
     * __essential_cart_* were there). If any of our keys is missing or changed, write once more
     * after a short delay. Only our keys are ever posted: /cart/update.js merges attributes by
     * key, so another app's attributes are never removed.
     */
    async _recordOrderAttributes() {
      if (!this._model || !this._model.url) return;
      const readAttributes = async () => {
        try {
          const res = await fetch(`${this.config.cartUrl}.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
          if (res.ok) return (await res.json()).attributes || {};
        } catch (e) {
          /* network hiccup: treated as unknown */
        }
        return null;
      };
      const writeAttributes = async (attributes) => {
        const res = await fetch(`${this.config.cartUrl}/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ attributes }),
        });
        if (!res.ok) console.warn('[terrain-studio] order attributes not recorded', res.status);
        return res.ok;
      };
      try {
        const existing = (await readAttributes()) || {};
        const key = this._model.key || this._model.url;
        // Same design already recorded (a retry after a lost reply): keep its number.
        let n = Object.keys(existing).find((k) => /^_ttv_design_\d+_key$/.test(k) && existing[k] === key);
        n = n ? Number(n.match(/\d+/)[0]) : 1 + Object.keys(existing).filter((k) => /^_ttv_design_\d+_key$/.test(k)).length;
        const product = this.products[this.state.product] || {};
        const props = this._cartProperties();
        const attributes = {};
        attributes[`Terrain design ${n}`] = `${product.title || this.state.product || 'Terrain tile'} — ${this._routeName() || 'route'} — ${props['Colours']}`;
        attributes[`_ttv_design_${n}_key`] = key;
        attributes[`_ttv_design_${n}_model`] = this._model.url;
        attributes[`_ttv_design_${n}_product`] = product.handle || String(this.state.product || '');
        attributes[`_ttv_design_${n}_source`] = props['_ttv_source'] || '';
        attributes[`_ttv_design_${n}_filaments`] = props['_ttv_filaments'] || '';
        await writeAttributes(attributes);
        // Shopify drops a blank attribute, so a blank value of ours is not looked for.
        const missing = (now) => !now || Object.entries(attributes).some(([k, v]) => v !== '' && now[k] !== v);
        if (missing(await readAttributes())) {
          await new Promise((resolve) => setTimeout(resolve, ORDER_ATTRIBUTES_RETRY_MS));
          await writeAttributes(attributes);
          if (missing(await readAttributes())) console.warn('[terrain-studio] order attributes still missing after a second write');
        }
      } catch (e) {
        console.warn('[terrain-studio] order attributes not recorded', e);
      }
    }

    /**
     * Look for the uploaded model in the cart (/cart.js lists hidden _ttv_* properties). Used
     * when the add reply was lost and before a retry re-posts. Fetch errors keep polling.
     * @returns {Promise<object|null>} the cart line, or null
     */
    async _confirmCartModel(model, { attempts = 6, delayMs = 500 } = {}) {
      if (!model || (!model.key && !model.url)) return null;
      for (let i = 0; i < attempts; i++) {
        if (i) await new Promise((resolve) => setTimeout(resolve, delayMs));
        try {
          const res = await fetch(`${this.config.cartUrl}.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
          if (!res.ok) continue;
          const cart = await res.json();
          const item = (cart && cart.items ? cart.items : []).find((it) => {
            const p = (it && it.properties) || {};
            return (model.key && p._ttv_key === model.key) || (model.url && p._ttv_model === model.url);
          });
          if (item) return item;
        } catch (e) {
          /* network hiccup: try again */
        }
      }
      return null;
    }

    async _postCartAdd(variantId, properties) {
      const drawer = document.querySelector('cart-drawer');
      const body = { items: [{ id: Number(variantId), quantity: 1, properties }] };
      if (drawer && typeof drawer.getSectionsToRender === 'function') {
        body.sections = drawer.getSectionsToRender().map((s) => s.id);
        body.sections_url = window.location.pathname;
      }
      let res;
      try {
        res = await fetch(`${this.config.cartAddUrl}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify(body),
        });
      } catch (e) {
        const err = withMsg(this._m('cart_uncertain'));
        err.cartUncertain = true;
        throw err;
      }
      let data = null;
      try {
        data = await res.json();
      } catch (e) {
        data = null;
      }
      if (!res.ok || !data || data.status) {
        const err = withMsg(
          (data && (data.description || data.message)) || this._m('cart_add_failed')
        );
        err.cartUncertain = !data || res.status >= 500;
        throw err;
      }
      return data;
    }

    async _afterCartAdd(added) {
      const drawer = document.querySelector('cart-drawer');
      let cartData = null;
      try {
        const res = await fetch(`${this.config.cartUrl}.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
        if (res.ok) cartData = await res.json();
      } catch (e) {
        cartData = null;
      }
      try {
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'terrain-studio', cartData: cartData ?? undefined });
        }
      } catch (e) {
        /* the cart page is authoritative */
      }
      if (drawer && typeof drawer.renderContents === 'function' && added?.sections) {
        try {
          drawer.classList.remove('is-empty');
          drawer.renderContents({ ...added, id: added.items?.[0]?.variant_id });
          return;
        } catch (e) {
          console.warn('[terrain-studio] cart drawer refresh failed', e);
        }
      }
      if (!this._devMode || !/[?&]ttv_stay=1\b/.test(window.location.search)) {
        window.location.assign(this.config.cartUrl);
      }
    }

    async _uploadModel(blob, turnstileToken, signal) {
      const base = this.config.workerUrl;
      let res;
      try {
        res = await fetch(`${base}/upload?ext=3mf`, {
          method: 'POST',
          headers: {
            'Content-Type': 'model/3mf',
            ...(turnstileToken ? { 'CF-Turnstile-Response': turnstileToken } : {}),
          },
          body: blob,
          ...(signal ? { signal } : {}),
        });
      } catch (e) {
        if (e && e.name === 'AbortError') throw e; // a cancelled pre-upload (§7 #3), not a network fault
        throw withMsg(this._m('upload_network'));
      }
      if (!res.ok) {
        const e = new Error(`upload ${res.status}`);
        e.userMessage =
          res.status === 413
            ? this._m('upload_too_large')
            : res.status === 429
            ? this._m('upload_rate')
            : res.status === 401
            ? this._m('upload_verify')
            : this._m('upload_failed');
        throw e;
      }
      const data = await res.json();
      const downloadUrl = data.downloadUrl || data.publicUrl; // publicUrl: back-compat with older worker
      if (!downloadUrl) throw withMsg(this._m('upload_no_link'));
      return { downloadUrl, key: data.key || null };
    }

    async _turnstileToken() {
      const sitekey = this.config.turnstileSitekey;
      if (!sitekey) return null;
      await this._turnstileReady();
      if (this._tsWidgetId == null) {
        this.els.turnstile.hidden = false;
        this._tsWidgetId = window.turnstile.render(this.els.turnstile, { sitekey });
      } else {
        window.turnstile.reset(this._tsWidgetId);
      }
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          clearInterval(poll);
          reject(withMsg(this._m('verify_timeout')));
        }, 25000);
        const poll = setInterval(() => {
          const tok = window.turnstile.getResponse(this._tsWidgetId);
          if (tok) {
            clearInterval(poll);
            clearTimeout(timeout);
            resolve(tok);
          }
        }, 200);
      });
    }

    _turnstileReady() {
      return new Promise((resolve, reject) => {
        if (window.turnstile) return resolve();
        let n = 0;
        const i = setInterval(() => {
          if (window.turnstile) {
            clearInterval(i);
            resolve();
          } else if (++n > 100) {
            clearInterval(i);
            reject(withMsg(this._m('verify_widget_failed')));
          }
        }, 100);
      });
    }

    /* =====================================================================
     * Prebuilt export (SCALE-AND-SPEED-REPORT §7 #3)
     *
     * On Customise, once the preview has settled, the 0.4 mm export is built in the background
     * and its 3MF is made in slices (the page stays responsive between them). Add to cart takes
     * what is ready and matches the design exactly: the export by its geometry key (the same key
     * the geometry cache uses), the 3MF by its inputs (name, colours, the verbatim GPX and
     * design.json); anything else is made at the click, as before. A colour or backpiece change
     * never rebuilds the geometry: colours are written into the 3MF, so only the 3MF is re-made.
     * The background build shares the worker with the preview: any other build cancels it
     * (the worker is replaced), so a slider move is never queued behind a background export.
     *
     * The upload stays at Add to cart. Pre-uploading is opt-in (data-preupload-model="true") and
     * never runs with a Turnstile site key (a token is per upload and is asked for at the click);
     * the privacy policy says the print file is uploaded when the tile is added to the cart.
     * ===================================================================*/

    /** Prebuild state for tests and diagnostics (no geometry and no file in it). */
    get prebuildState() {
      const pb = this._prebuild;
      if (!pb) return { status: 'none' };
      return {
        id: pb.id,
        status: pb.status,
        fromCurrent: pb.fromCurrent,
        buildMs: pb.buildMs,
        zipMs: pb.zip ? pb.zip.ms : null,
        bytes: pb.zip ? pb.zip.bytes : null,
        zips: pb.zips,
        uploaded: !!pb.upload,
        uploadMs: pb.upload ? pb.upload.ms : null,
        uploads: pb.uploads,
        error: pb.error ? String(pb.error.message || pb.error) : null,
      };
    }

    /** Run _runPrebuild once the design has been still for `delay` ms, in an idle moment. */
    _schedulePrebuild(delay = PREBUILD_SETTLE_MS) {
      if (!this.config?.prebuildExport || !this.state) return;
      clearTimeout(this._prebuildTimer);
      if (this.state.step !== 'customise') return;
      this._prebuildTimer = setTimeout(() => {
        const run = () => this._runPrebuild().catch((err) => console.warn('[terrain-studio] background export skipped', err));
        if (typeof requestIdleCallback === 'function') requestIdleCallback(run, { timeout: 2000 });
        else run();
      }, delay);
    }

    /** The design on screen is settled and could go to the cart as it is. */
    _prebuildEligible() {
      const c = this.config;
      const b = this._built;
      return !!(
        c.prebuildExport &&
        c.meshModuleUrl &&
        this.isConnected &&
        this.state.step === 'customise' &&
        this.state.route &&
        b &&
        b.meta?.audit?.ok &&
        this._elevation &&
        this._fit &&
        !this._busy &&
        !this._adding &&
        !this._exporting &&
        !this._designDirty &&
        !this._updateFailed &&
        !(this._model && this._addedModelKey === this._model.key) // already in the cart
      );
    }

    /** The prebuild for the design as it is now (same export geometry key), or null. */
    _prebuildFor() {
      const pb = this._prebuild;
      if (!pb || !this._fit || !this._elevation) return null;
      return pb.geometryKey === this._geometryRequest({ export: true }).geometryKey ? pb : null;
    }

    async _runPrebuild() {
      if (!this._prebuildEligible()) return;
      const { geometryKey } = this._geometryRequest({ export: true });
      let pb = this._prebuild;
      if (pb && pb.geometryKey !== geometryKey) {
        this._cancelPrebuild('stale');
        pb = null;
      }
      if (!pb) {
        this._prebuildSeq = (this._prebuildSeq || 0) + 1;
        pb = {
          id: this._prebuildSeq,
          geometryKey,
          status: 'building',
          built: null,
          buildPromise: null,
          buildMs: null,
          error: null,
          fromCurrent: false,
          worker: null,
          zip: null,
          zipping: null,
          zips: 0,
          upload: null,
          uploading: null,
          uploads: 0,
          t0: performance.now(),
        };
        if (this._lastBuildExport && !this._lastBuildFast) {
          // The build on screen is the export already (made by an earlier Add to cart).
          pb.built = this._built;
          pb.fromCurrent = true;
          pb.buildMs = 0;
          pb.status = 'built';
          this._prebuild = pb;
        } else {
          // Only an idle worker takes a background build; try again once it settles.
          if (this._workerJobs && this._workerJobs.size) {
            this._schedulePrebuild();
            return;
          }
          this._prebuild = pb;
          pb.buildPromise = this._buildGeometry({ export: true, prebuild: true, onPosted: (w) => (pb.worker = w) });
          let built;
          try {
            built = await pb.buildPromise;
          } catch (err) {
            if (this._prebuild !== pb) return; // cancelled on purpose (its worker was replaced)
            // A trap is remembered for this export: Add to cart then goes straight to the
            // previewed model instead of trapping a second time (_addToCart).
            if (err && err.engineTrap) this._noteExportTrap(geometryKey);
            pb.error = err;
            pb.status = 'failed';
            pb.buildPromise = null;
            // A trap: Add to cart uses the previewed model. Anything else (a worker that did not
            // load): Add to cart builds it again on a fresh worker (§7 #1).
            console.warn('[terrain-studio] background export failed', err);
            return;
          }
          if (this._prebuild !== pb) return;
          pb.built = built;
          pb.buildMs = Math.round(performance.now() - pb.t0);
          pb.status = 'built';
          pb.buildPromise = null;
        }
      }
      if (pb.status === 'building' || pb.status === 'failed') return;
      await this._prezip(pb);
    }

    /** An export (by geometry key) that trapped the engine in this visit: never built again. */
    _noteExportTrap(key) {
      (this._exportTrapKeys ||= new Set()).add(key);
    }

    /** Name, colours, GPX and design.json of the cart's 3MF for `built`, and their key. */
    _cartFileInputs(built, extraParams = null) {
      const name = `Vestige terrain tile — ${this._routeName() || 'route'}`;
      const colors = this._currentColors();
      const params = { ...this._modelParams(built), ...(extraParams || {}) };
      return { name, colors, gpxText: this.state.route.gpxText, params, zipKey: JSON.stringify([name, colors, params]) };
    }

    /** Make the 3MF for the prebuilt export and the current colours (in slices), unless it exists. */
    async _prezip(pb) {
      if (!pb.built || !pb.built.meta?.audit?.ok || !this.state.route || !this._colourBudget().valid) return;
      const input = this._cartFileInputs(pb.built);
      const same = (r) => !!r && r.key === input.zipKey && r.built === pb.built && r.gpxText === input.gpxText;
      if (same(pb.zip)) {
        this._schedulePreupload(pb);
        return;
      }
      if (same(pb.zipping)) return;
      if (pb.zipping) pb.zipping.cancelled = true; // older colours
      const job = { key: input.zipKey, built: pb.built, gpxText: input.gpxText, cancelled: false, promise: null };
      pb.zipping = job;
      pb.status = 'zipping';
      const t0 = performance.now();
      job.promise = (async () => {
        const mesh = await import(this.config.meshModuleUrl);
        const blob = await mesh.build3MF(pb.built, {
          name: input.name,
          colors: input.colors,
          gpxText: input.gpxText,
          params: input.params,
          pause: () => this._prebuildPause(job),
        });
        return { key: job.key, built: job.built, gpxText: job.gpxText, blob, bytes: blob.size, ms: Math.round(performance.now() - t0) };
      })();
      let zip = null;
      try {
        zip = await job.promise;
      } catch (err) {
        if (!job.cancelled) console.warn('[terrain-studio] background 3MF failed; Add to cart will make it', err);
      }
      if (pb.zipping === job) pb.zipping = null;
      if (!zip || job.cancelled || this._prebuild !== pb) return;
      pb.zip = zip;
      pb.zips++;
      pb.status = 'ready';
      this._schedulePreupload(pb);
    }

    /** Between two slices of the background 3MF: let input and rendering run; stop if superseded. */
    _prebuildPause(job) {
      return new Promise((resolve, reject) => {
        const go = () => (job.cancelled ? reject(new Error('background 3MF superseded')) : resolve());
        if (typeof requestIdleCallback === 'function') requestIdleCallback(go, { timeout: 200 });
        else setTimeout(go, 0);
      });
    }

    /** The prebuilt 3MF for exactly these inputs (waiting for one still being made), or null. */
    async _takePrebuiltZip(built, file) {
      const pb = this._prebuild;
      if (!pb) return null;
      const same = (r) => !!r && r.key === file.zipKey && r.built === built && r.gpxText === file.gpxText;
      if (same(pb.zip)) return pb.zip.blob;
      const job = pb.zipping;
      if (same(job) && job.promise) {
        try {
          const zip = await job.promise;
          return zip && !job.cancelled ? zip.blob : null;
        } catch (e) {
          return null;
        }
      }
      return null;
    }

    _preuploadAllowed() {
      const c = this.config;
      return !!(
        c.preuploadModel &&
        !c.turnstileSitekey &&
        c.workerUrl &&
        !this._preuploadOff &&
        (this._preuploadCount || 0) < PREUPLOAD_MAX &&
        !(typeof navigator !== 'undefined' && navigator.connection && navigator.connection.saveData)
      );
    }

    _schedulePreupload(pb) {
      clearTimeout(this._preuploadTimer);
      if (!this._preuploadAllowed()) return;
      this._preuploadTimer = setTimeout(() => {
        this._runPreupload(pb).catch((err) => console.warn('[terrain-studio] pre-upload skipped', err));
      }, PREUPLOAD_SETTLE_MS);
    }

    async _runPreupload(pb) {
      const zip = pb.zip;
      if (this._prebuild !== pb || !zip || !this._preuploadAllowed() || !this._prebuildEligible()) return;
      if ((pb.upload && pb.upload.key === zip.key) || (pb.uploading && pb.uploading.key === zip.key)) return;
      if (pb.uploading) pb.uploading.controller.abort(); // an older 3MF (other colours)
      const controller = new AbortController();
      const t0 = performance.now();
      const job = { key: zip.key, controller, promise: this._uploadModel(zip.blob, null, controller.signal) };
      pb.uploading = job;
      pb.status = 'uploading';
      this._preuploadCount = (this._preuploadCount || 0) + 1;
      try {
        const up = await job.promise;
        if (this._prebuild === pb && pb.uploading === job) {
          pb.upload = { key: job.key, url: up.downloadUrl, modelKey: up.key, ms: Math.round(performance.now() - t0), used: false };
          pb.uploads++;
        }
      } catch (err) {
        // A refused or failed pre-upload stops pre-uploading for this visit; Add to cart uploads.
        if (!(err && err.name === 'AbortError')) this._preuploadOff = true;
      } finally {
        if (pb.uploading === job) pb.uploading = null;
        if (pb.status === 'uploading') pb.status = pb.upload ? 'uploaded' : 'ready';
      }
    }

    /**
     * The pre-uploaded file for exactly this 3MF (waiting for one on its way), or null. A file is
     * used for one cart line only: another line uploads its own, as before.
     */
    async _takePreupload(zipKey) {
      const pb = this._prebuild;
      if (!pb) return null;
      const take = (u) => {
        u.used = true;
        return { downloadUrl: u.url, key: u.modelKey };
      };
      if (pb.upload && pb.upload.key === zipKey && !pb.upload.used) return take(pb.upload);
      const job = pb.uploading;
      if (job && job.key === zipKey) {
        try {
          const up = await job.promise;
          if (pb.upload && pb.upload.key === zipKey) return pb.upload.used ? null : take(pb.upload);
          pb.upload = { key: zipKey, url: up.downloadUrl, modelKey: up.key, ms: null, used: false };
          return take(pb.upload);
        } catch (e) {
          return null; // Add to cart uploads it itself
        }
      }
      return null;
    }

    /** Before another build takes the worker: stop an unfinished background build; drop a stale one. */
    _yieldPrebuildWorker() {
      const pb = this._prebuild;
      if (!pb) return;
      if (pb.status === 'building') this._cancelPrebuild('foreground');
      else if (!this._fit || !this._elevation || pb.geometryKey !== this._geometryRequest({ export: true }).geometryKey) this._cancelPrebuild('stale');
    }

    /** Forget the prebuild: stop its build (replacing the worker it holds), its 3MF and its upload. */
    _cancelPrebuild(reason) {
      clearTimeout(this._prebuildTimer);
      clearTimeout(this._preuploadTimer);
      const pb = this._prebuild;
      if (!pb) return;
      this._prebuild = null;
      pb.cancelled = reason || 'cancelled';
      if (pb.zipping) pb.zipping.cancelled = true;
      if (pb.uploading) pb.uploading.controller.abort();
      if (pb.status === 'building' && pb.worker && pb.worker === this._worker && this._workerJobs) {
        // The background build holds the worker: replace it. The next build starts a fresh one.
        this._retireWorker(pb.worker, this._workerJobs, false);
      }
    }

    /* =====================================================================
     * Persistence (sessionStorage) — survives the Strava redirect and reloads
     * ===================================================================*/

    _save() {
      clearTimeout(this._saveTimer);
      this._saveTimer = setTimeout(() => this._saveNow(), 120);
    }

    _saveNow() {
      const s = this.state;
      const data = {
        v: 1,
        step: s.step,
        product: s.product,
        features: s.features,
        colours: s.colours,
        preset: s.preset,
        zMult: s.zMult,
        ...this._savedSnow(),
        ...this._savedDesignOptions(),
        backpiece: s.backpiece,
        ...this._savedWaterChoice(),
        fit: this._fit && this._fitMode === 'user' ? fitParams(this._fit) : null,
        route: null,
      };
      if (s.route) {
        const bytes = s.route.sources.reduce((n, src) => n + src.text.length, 0);
        if (bytes <= MAX_STORED_GPX) data.route = { source: s.route.source, sources: s.route.sources };
      }
      try {
        sessionStorage.setItem(STORE_KEY, JSON.stringify(data));
      } catch (e) {
        // Quota exceeded: keep the choices, drop the route.
        try {
          data.route = null;
          sessionStorage.setItem(STORE_KEY, JSON.stringify(data));
        } catch (e2) {
          /* storage unavailable */
        }
      }
      this._autosave();
    }

    /* ---------- autosave across visits (plan §11a): localStorage + IndexedDB ---------- */

    _autosave() {
      const s = this.state;
      if (!s.route || this._resumeData) return;
      const design = {
        v: 1,
        savedAt: Date.now(),
        step: s.step,
        product: s.product,
        features: s.features,
        colours: s.colours,
        preset: s.preset,
        zMult: s.zMult,
        ...this._savedSnow(),
        ...this._savedDesignOptions(),
        backpiece: s.backpiece,
        ...this._savedWaterChoice(),
        fit: this._fit && this._fitMode === 'user' ? fitParams(this._fit) : null,
        route: { name: this._routeName(), source: s.route.source },
        thumb: this._thumb || null,
      };
      try {
        localStorage.setItem(SAVED_KEY, JSON.stringify(design));
      } catch (e) {
        try {
          design.thumb = null;
          localStorage.setItem(SAVED_KEY, JSON.stringify(design));
        } catch (e2) {
          return;
        }
      }
      const routeKey = `${s.route.source}:${s.route.gpxText.length}:${this._routeName()}`;
      if (this._savedRouteKey !== routeKey) {
        this._savedRouteKey = routeKey;
        idbSet('last', { sources: s.route.sources, savedAt: Date.now() }).catch(() => {
          this._savedRouteKey = null;
        });
      }
    }

    _clearAutosave() {
      try {
        localStorage.removeItem(SAVED_KEY);
      } catch (e) {
        /* storage unavailable */
      }
      this._savedRouteKey = null;
      this._resumeData = null;
      idbDel('last').catch(() => {});
      if (this.els.resume) this.els.resume.hidden = true;
    }

    /** No design in this tab? Offer the last one saved on this device. */
    async _offerResume() {
      if (this.state.route || !this.els.resume) return;
      let saved = null;
      try {
        saved = JSON.parse(localStorage.getItem(SAVED_KEY) || 'null');
      } catch (e) {
        saved = null;
      }
      if (!saved || saved.v !== 1 || !saved.route) return;
      // A route is a GPS trace: on a shared device the offer lasts 30 days, then design and route go.
      if (!Number.isFinite(saved.savedAt) || Date.now() - saved.savedAt > AUTOSAVE_MAX_AGE_MS) {
        this._clearAutosave();
        return;
      }
      this._resumeData = saved;
      if (this.els.resumeName) this.els.resumeName.textContent = saved.route.name || '';
      if (this.els.resumeThumb) {
        const ok = typeof saved.thumb === 'string' && /^data:image\/(png|jpeg);base64,/.test(saved.thumb);
        this.els.resumeThumb.hidden = !ok;
        if (ok) this.els.resumeThumb.src = saved.thumb;
      }
      this.els.resume.hidden = false;
    }

    async _resumeContinue() {
      const saved = this._resumeData;
      if (!saved || this._busy) return;
      let record = null;
      try {
        record = await idbGet('last');
      } catch (e) {
        record = null;
      }
      if (!record || !Array.isArray(record.sources) || !record.sources.length) {
        this._clearAutosave();
        this.setStatus(this.t.resumeFailed, 'error');
        this._emit('ttv:studio_resume', { accepted: true, restored: false });
        return;
      }
      const s = this.state;
      for (const key of ALL_FEATURES) s.features[key] = !!saved.features?.[key];
      if (!this.config.showSnow) s.features.snow = false;
      for (const [k, v] of Object.entries(saved.colours || {})) {
        if (k in s.colours && /^#[0-9A-F]{6}$/i.test(v)) s.colours[k] = v.toUpperCase();
      }
      if (saved.preset && (PRESETS[saved.preset] || saved.preset === 'custom')) s.preset = saved.preset;
      if (Number.isFinite(saved.zMult)) s.zMult = Math.min(8, Math.max(0.1, saved.zMult));
      this._restoreSnow(saved);
      const predates = this._restoreDesignOptions(saved);
      if (typeof saved.backpiece === 'boolean') s.backpiece = saved.backpiece;
      if (saved.product && this.products[saved.product]?.available) s.product = saved.product;
      const ok = await this._acceptSources(record.sources, saved.route.source || 'gpx', { silent: true });
      if (!ok) {
        this._clearAutosave();
        this.setStatus(this.t.resumeFailed, 'error');
        this._emit('ttv:studio_resume', { accepted: true, restored: false });
        return;
      }
      this._restoreWaterChoice(saved);
      if (saved.fit && Number.isFinite(saved.fit.k)) {
        this._setFit(fitFromParams(saved.fit), 'user', { silent: true });
        this._mapApi?.showFit(this._fit);
      }
      this._noticeRestoredDesign(predates);
      this._thumb = saved.thumb || null;
      this._resumeData = null;
      this.els.resume.hidden = true;
      s.step = this._canEnter('map') ? 'map' : 'product';
      if (s.step === 'map') this._ensureMap();
      this._syncInputsFromState();
      this._applyColors({ quiet: true });
      this._render();
      this._save();
      this._emit('ttv:studio_resume', { accepted: true, restored: true });
      this.setStatus(this._m('welcome_resume', { name: this._routeName() || this._m('route_fallback') }));
      this._focusPane(s.step);
    }

    _resumeFresh() {
      this._clearAutosave();
      this._emit('ttv:studio_resume', { accepted: false });
      this._focusPane('route');
    }

    /* ---------- colour code (plan §11a): e.g. bone-red-navy-mistletoe ---------- */

    _colourSlugs() {
      const firsts = this.config.palette.map((p) => p.name.split(/\s+/)[0].toLowerCase().replace(/[^a-z0-9]/g, ''));
      return this.config.palette.map((p, i) => ({
        hex: p.hex,
        slug: firsts.filter((f) => f === firsts[i]).length > 1 ? p.name.toLowerCase().replace(/[^a-z0-9]+/g, '') : firsts[i],
        full: p.name.toLowerCase().replace(/[^a-z0-9]+/g, ''),
      }));
    }

    _colourCode(budget = this._colourBudget()) {
      const slugs = this._colourSlugs();
      const colors = this._currentColors();
      const parts = ['terrain', 'route', 'water', 'forest'];
      // Snow (when it counts in the colour budget) goes seventh, after roads and buildings, which
      // are then always written so its position is unambiguous (SNOW-DESIGN §5).
      const snow = budget.keys.includes('snow');
      if (this.state.features.roads || this.state.features.buildings || snow) parts.push('roads', 'buildings');
      if (snow) parts.push('snow');
      return parts.map((k) => (slugs.find((s) => s.hex === String(colors[k]).toUpperCase()) || { slug: '?' }).slug).join('-');
    }

    async _copyColourCode() {
      const code = this._colourCode();
      try {
        // Some browsers never settle a clipboard write without permission: don't wait forever.
        await Promise.race([
          navigator.clipboard.writeText(code),
          new Promise((_, reject) => setTimeout(() => reject(new Error('clipboard timeout')), 1500)),
        ]);
      } catch (e) {
        this.els.colourCode?.select();
        try {
          document.execCommand('copy');
        } catch (e2) {
          /* the code is selected for a manual copy */
        }
      }
      this.setStatus(this.t.codeCopied);
    }

    _applyColourCode(value) {
      if (this._busy) return;
      const words = String(value || '')
        .trim()
        .toLowerCase()
        .split(/[\s,\-–:;·|]+/)
        .map((w) => w.replace(/[^a-z0-9]/g, ''))
        .filter(Boolean);
      // Positional order, as _colourCode writes it; a role word ("Snow Chalk") also assigns.
      const parts = ['terrain', 'route', 'water', 'forest', 'roads', 'buildings', 'snow'];
      const roles = parts;
      const slugs = this._colourSlugs();
      const match = (word) => slugs.find((s) => s.slug === word || s.full === word);
      // Longest run of words first, so "Mistletoe Green" reads as one colour. A layer name
      // ("Terrain Bone, Route Red, …" — the order's Colours line) assigns the colour after it.
      const items = [];
      let role = null;
      for (let i = 0; i < words.length; ) {
        if (roles.includes(words[i]) && !match(words[i])) {
          role = words[i];
          i += 1;
          continue;
        }
        let hit = null;
        let used = 1;
        for (let n = Math.min(3, words.length - i); n >= 1 && !hit; n--) {
          hit = match(words.slice(i, i + n).join('')) || null;
          if (hit) used = n;
        }
        items.push({ role, word: words[i], hit });
        role = null;
        i += used;
      }
      const labelled = items.some((it) => it.role);
      if (items.length < 2 || items.length > (labelled ? roles.length : parts.length)) {
        this.setStatus(this.t.codeLength, 'error');
        return;
      }
      const next = {};
      for (let i = 0; i < items.length; i++) {
        const { hit, word } = items[i];
        if (!hit) {
          this.setStatus(this.t.codeInvalid.replace('[name]', word), 'error');
          return;
        }
        next[items[i].role || parts[i]] = hit.hex;
      }
      Object.assign(this.state.colours, next);
      this._applyColors();
      this._render();
      this._save();
      if (this.els.codePaste) this.els.codePaste.value = '';
      this.setStatus(this.t.codeApplied);
    }

    _clearSaved() {
      try {
        const raw = sessionStorage.getItem(STORE_KEY);
        if (!raw) return;
        const data = JSON.parse(raw);
        data.route = null;
        data.fit = null;
        data.step = 'route';
        sessionStorage.setItem(STORE_KEY, JSON.stringify(data));
      } catch (e) {
        /* ignore */
      }
    }

    async _restore() {
      let saved = null;
      try {
        saved = JSON.parse(sessionStorage.getItem(STORE_KEY) || 'null');
      } catch (e) {
        saved = null;
      }
      const s = this.state;
      let predates = false;
      if (saved && saved.v === 1) {
        predates = this._restoreDesignOptions(saved);
        for (const key of ALL_FEATURES) s.features[key] = !!saved.features?.[key];
        if (!this.config.showSnow) s.features.snow = false;
        if (saved.colours && typeof saved.colours === 'object') {
          for (const [k, v] of Object.entries(saved.colours)) {
            if (k in s.colours && /^#[0-9A-F]{6}$/i.test(v)) s.colours[k] = v.toUpperCase();
          }
        }
        if (saved.preset && (PRESETS[saved.preset] || saved.preset === 'custom')) s.preset = saved.preset;
        if (Number.isFinite(saved.zMult)) s.zMult = Math.min(8, Math.max(0.1, saved.zMult));
        this._restoreSnow(saved);
        if (typeof saved.backpiece === 'boolean') s.backpiece = saved.backpiece;
        if (saved.product && this.products[saved.product]?.available) s.product = saved.product;
      }
      const params = new URLSearchParams(window.location.search);
      const fromQuery = this._productKeyFor(params.get('product'));
      if (fromQuery && this.products[fromQuery]?.available) s.product = fromQuery;
      else if (!s.product && this.config.defaultProduct !== 'none' && this.products[this.config.defaultProduct]?.available) {
        s.product = this.config.defaultProduct;
      }

      if (saved?.route?.sources?.length) {
        const ok = await this._acceptSources(saved.route.sources, saved.route.source, { silent: true });
        if (ok) {
          this._restoreWaterChoice(saved);
          if (saved.fit && Number.isFinite(saved.fit.k)) {
            this._setFit(fitFromParams(saved.fit), 'user', { silent: true });
            this._mapApi?.showFit(this._fit);
          }
          const want = saved.step === 'customise' ? 'map' : saved.step;
          s.step = this._canEnter(want) ? want : 'product';
          if (s.step === 'map') this._ensureMap();
          this._noticeRestoredDesign(predates);
          this.setStatus(this._m('welcome_back', { name: this._routeName() || this._m('route_fallback') }));
        }
      }
      if (!s.route) await this._offerResume();
      await this._afterRestore?.();
    }

    /* =====================================================================
     * Developer tools (?ttv_dev=1): 3MF/STL download for slicer checks
     * ===================================================================*/

    _mountDevPanel() {
      if (this._devPanel) return;
      const box = document.createElement('div');
      box.className = 'ts__dev';
      box.innerHTML =
        '<p class="ts__label">Developer tools</p>' +
        '<div class="ts__dev-buttons"><button type="button" class="button button--secondary" data-ts-download="3mf" disabled>Download 3MF</button>' +
        '<button type="button" class="button button--secondary" data-ts-download="stl" disabled>Download STL</button></div>' +
        '<pre class="ts__audit" data-ts-audit></pre>';
      this.els.panel.appendChild(box);
      this._devPanel = box;
      box.querySelectorAll('[data-ts-download]').forEach((btn) =>
        btn.addEventListener('click', () => this._downloadMesh(btn.dataset.tsDownload))
      );
      if (this._built) this._updateDevPanel(this._built);
    }

    _updateDevPanel(built) {
      if (!this._devMode || !this._devPanel) return;
      const m = built.meta;
      const a = m.audit;
      const bt = (k) => (built.bodies[k] ? built.bodies[k].count / 3 : 0);
      this._devPanel.querySelector('[data-ts-audit]').textContent = [
        `manifold      ${a.ok ? 'PASS' : 'FAIL'}`,
        `triangles     ${m.triangleCount.toLocaleString()}  (terrain ${bt('terrain').toLocaleString()}, route ${bt('route').toLocaleString()}, water ${bt('water').toLocaleString()}, forest ${bt('forest').toLocaleString()}, roads ${bt('roads').toLocaleString()}, buildings ${bt('buildings').toLocaleString()})`,
        `vertices      ${m.vertexCount.toLocaleString()}`,
        `volume        ${(a.stats.volumeMm3 / 1000).toFixed(1)} cm³`,
        `height        ${m.maxZmm.toFixed(2)} mm  (base ${m.baseMm}, cap ${m.maxHeightMm})`,
        `grid          ${m.gridMm.toFixed(4)} mm  ${m.gridNodes[0]}×${m.gridNodes[1]}`,
        `elevation     ${m.elevationMinM.toFixed(0)}–${m.elevationMaxM.toFixed(0)} m  (relief ${m.reliefM.toFixed(0)} m)`,
        `zMult         ${m.zMultUsed.toFixed(3)} of max ${m.zMultMax.toFixed(3)}${m.zMultClamped ? '  (clamped)' : ''}`,
        `span          ${this._fit ? this._spanKm(this._fit).toFixed(2) : '—'} km across points`,
        `coast         sea flattened ${Number.isFinite(m.seaFlattened) ? m.seaFlattened.toLocaleString() + ' cells' + (Number.isFinite(m.seaFlattenedInHex) ? ` (${m.seaFlattenedInHex.toLocaleString()} in the tile)` : '') : '—'}${m.seaFlattenSkipped ? ` (skipped: ${m.seaFlattenSkipped}${m.seaFlattenBasin ? ' ' + m.seaFlattenBasin : ''})` : ''}  auto Water ${m.autoWater || '—'}`,
        ...this._devSnowLines(m),
        ...(a.errors.length ? ['', ...a.errors] : []),
      ].join('\n');
      this._devPanel.querySelectorAll('[data-ts-download]').forEach((b) => (b.disabled = false));
    }

    /** Snow meta for the dev panel (empty until the engine reports the snow-line fields). */
    _devSnowLines(m) {
      if (!('snowOffered' in m) && !('snowRangeM' in m)) return [];
      const f = (v, d = 0) => (Number.isFinite(v) ? v.toFixed(d) : '—');
      const r = Array.isArray(m.snowRangeM) ? m.snowRangeM : [];
      const frac = Number.isFinite(m.snowAreaFrac) ? (m.snowAreaFrac * 100).toFixed(1) : '—';
      return [
        `snow          ${m.snow ? 'on' : 'off'}  offered ${m.snowOffered ? 'yes' : `no (${m.snowReason || '—'}${m.snowReason === 'climate' ? ` below ${f(m.snowClimateLineM)} m` : ''})`}${m.snowOffered && m.snowReason ? ` (${m.snowReason})` : ''}  natural ${m.snowNatural === false ? 'no' : m.snowNatural ? 'yes' : '—'}`,
        `snow line     ${f(m.snowLineM)} m  (default ${f(m.snowDefaultM)}, range ${f(r[0])}–${f(r[1])}${m.snowLineClamped ? ', clamped' : ''}; ${f(m.snowPct, 1)} %, z ${f(m.snowLineZmm, 2)} mm; P5/P70/P97 ${Array.isArray(m.snowQuantilesM) ? m.snowQuantilesM.map((v) => f(v)).join('/') : '—'})`,
        `snow cap      ${f(m.snowAreaMm2)} mm²  (${frac} % of ${Number.isFinite(m.snowLandAreaMm2) ? `${f(m.snowLandAreaMm2)} mm² land` : 'tile'}, pre-priority), glacier ${f(m.snowGlacierAreaMm2)} mm², ${m.snowAreas ?? '—'} areas, ${m.snowHoles ?? '—'} holes, boundary ${f(m.snowBoundaryMm)} mm, ${f(m.snowMs)} ms, seed ${m.snowSeed ?? '—'}`,
      ];
    }

    async _downloadMesh(format) {
      if (!this._checkColourBudget()) return;
      if (!this._built || this._exporting || this._busy) return;
      this._exporting = true;
      try {
        if (this._lastBuildFast || !this._lastBuildExport) {
          this.setStatus(this._m('rendering'));
          this._built = await this._buildGeometry({ export: true });
          this._updateDevPanel(this._built);
        }
        const mod = await import(this.config.meshModuleUrl);
        const base =
          (this._routeName() || 'vestige-terrain').replace(/[^\w-]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60) ||
          'vestige-terrain';
        let blob;
        let filename;
        if (format === 'stl') {
          const buf = mod.buildBinarySTL(this._built.positions, this._built.indices, `Vestige — ${base}`);
          blob = new Blob([buf], { type: 'model/stl' });
          filename = `${base}.stl`;
        } else {
          blob = await mod.build3MF(this._built, {
            name: `Vestige terrain tile — ${base}`,
            colors: this._currentColors(),
            gpxText: this.state.route.gpxText,
            params: this._modelParams(),
          });
          filename = `${base}.3mf`;
        }
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 10000);
        this.setStatus(`Downloaded ${filename} (${(blob.size / 1048576).toFixed(2)} MB).`);
      } catch (err) {
        console.error('[terrain-studio] export failed', err);
        this.setStatus('Export failed — see console.', 'error');
      } finally {
        this._exporting = false;
      }
    }

    /* =====================================================================
     * Status, progress, busy, analytics
     * ===================================================================*/

    _setBusy(busy) {
      this._busy = !!busy;
      this._render();
      // A build (or cart attempt) finished: the background export may start once things settle.
      if (!busy) this._schedulePrebuild();
    }

    setStatus(text, state) {
      if (!this.els.status) return;
      // Phase 0 #5: while the studio works, the chip pinned to the stage carries the same line, so
      // it is in view on a phone however far the panel is scrolled (errors also go to the toast).
      if (this._busy && text && state !== 'error') this._setStageNote(text);
      // §7 #1 / #12: an error also shows in the toast by the primary button, which is in view on
      // every layout (the status line can sit below the fold). The toast is the live region that
      // announces it, so the status line stays quiet for that one message (no double reading).
      const toast = state === 'error' && !!text && !!this.els.toast;
      this.els.status.setAttribute('aria-live', toast ? 'off' : 'polite');
      this._showToast(toast ? text : '');
      this.els.status.textContent = text || '';
      if (state) this.els.status.setAttribute('data-state', state);
      else this.els.status.removeAttribute('data-state');
      // Desktop: the panel scrolls on its own, so bring the full error line into it as well (the
      // page itself does not move). Phones keep their scroll position; the toast is in view there.
      if (toast && window.matchMedia('(min-width: 990px)').matches) {
        const el = this.els.status;
        requestAnimationFrame(() => el.isConnected && el.scrollIntoView({ block: 'nearest' }));
      }
    }

    /** The error toast above the bar (empty = closed). Only its text changes: an assertive live region. */
    _showToast(text) {
      const el = this.els.toast;
      if (!el) return;
      const value = text || '';
      const node = this.els.toastText;
      if (node) {
        if (value && node.textContent === value) {
          // The same error again (a second failed attempt): empty it for a frame so it is announced.
          node.textContent = '';
          requestAnimationFrame(() => {
            if (this._toastValue === value) node.textContent = value;
          });
        } else if (node.textContent !== value) node.textContent = value;
      }
      this._toastValue = value;
      el.classList.toggle('is-open', !!value);
      if (this.els.toastClose) this.els.toastClose.hidden = !value;
    }

    setProgress(frac) {
      if (!this.els.progress) return;
      if (frac == null) {
        this._progressShown = 0;
        this.els.progress.hidden = true;
        return;
      }
      // Phase 0 #4: within one piece of work the bar only moves forward (a second lookup or build
      // pass in the same Generate does not send it back).
      const value = Math.max(0, Math.min(1, frac));
      if (!this.els.progress.hidden && value < (this._progressShown || 0)) return;
      this._progressShown = value;
      this.els.progress.hidden = false;
      this.els.progressBar.style.width = `${Math.round(value * 100)}%`;
    }

    /**
     * Phase 0 #4: move the bar through `range` (from where it already is, _progressSpan) over about
     * `ms`, never reaching its end. By default it slows as it goes, about two thirds of the way
     * at `ms`, so a wait that runs to its budget still moves every two seconds or so; `linear`
     * moves evenly (a wait with a known end). step(f) lifts it at once to the fraction f of the
     * range (a kind received) and keeps creeping from there.
     */
    _progressRun(range, ms, { linear = false } = {}) {
      const [from, to] = this._progressSpan(range);
      let base = 0;
      let t0 = performance.now();
      const ease = (x) => (linear ? Math.min(1, x) : 1 - Math.exp(-x));
      const value = () => base + (1 - base) * ease((performance.now() - t0) / Math.max(1, ms));
      const tick = () => this.setProgress(from + (to - from) * Math.min(0.97, value()));
      tick();
      const timer = setInterval(tick, 250);
      return {
        step: (f) => {
          const now = value();
          if (f > now) {
            base = Math.min(1, f);
            t0 = performance.now();
          }
          tick();
        },
        stop: () => clearInterval(timer),
      };
    }

    /**
     * Phase 0 #4: `range` for the next piece of work, starting where the bar already is (it never
     * goes back, setProgress), and at least a few points long.
     */
    _progressSpan([from, to]) {
      const shown = this.els.progress && !this.els.progress.hidden ? this._progressShown || 0 : 0;
      const start = Math.max(from, shown);
      return [start, Math.max(to, Math.min(0.99, start + 0.03))];
    }

    /**
     * Phase 0 #5: after LOOKUP_SLOW_MS of work, bring the stage's status chip into view if it is
     * not (a phone scrolled past the stage). Returns a cancel.
     */
    _revealStageNoteLater() {
      const timer = setTimeout(() => {
        const el = this.els.stageNote;
        if (!el || el.hidden || !this._busy) return;
        const r = el.getBoundingClientRect();
        if (r.top >= 0 && r.bottom <= window.innerHeight) return;
        const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        el.scrollIntoView({ block: 'nearest', behavior: smooth ? 'smooth' : 'auto' });
      }, LOOKUP_SLOW_MS);
      return () => clearTimeout(timer);
    }

    _setStageNote(text) {
      if (!this.els.stageNote) return;
      this.els.stageNote.textContent = text || '';
      this.els.stageNote.hidden = !text;
    }

    _showWarnings(warnings) {
      if (!this.els.warnings) return;
      const text = (warnings || []).filter(Boolean).join(' ');
      // A permanent role="status" region (never hidden, collapses when empty): write only on
      // change, so a pan stop or a regenerate with the same warnings is not announced again.
      if (this.els.warnings.textContent === text) return;
      this.els.warnings.textContent = text;
    }

    _emit(name, detail) {
      try {
        this.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
        if (typeof publish === 'function') publish(name, detail);
      } catch (e) {
        /* analytics must never break the flow */
      }
    }
  }

  customElements.define('terrain-studio', TerrainStudio);
  window.TerrainStudio = TerrainStudio;

  /* =======================================================================
   * Helpers (pure)
   * =====================================================================*/

  function withMsg(message) {
    const e = new Error(message);
    e.userMessage = message;
    return e;
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  function stripExt(name) {
    return String(name || '').replace(/\.gpx$/i, '').trim();
  }

  function round(n, dp) {
    const f = 10 ** dp;
    return Math.round(n * f) / f;
  }

  function formatKm(km) {
    return km >= 10 ? km.toFixed(0) : km.toFixed(1);
  }

  const MERC_MAX_LAT = 85.05112878;
  function lonToMercX(lon) {
    return (lon + 180) / 360;
  }
  function latToMercY(lat) {
    const clamped = Math.max(-MERC_MAX_LAT, Math.min(MERC_MAX_LAT, lat));
    const s = Math.sin((clamped * Math.PI) / 180);
    return 0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI);
  }
  function mercXToLon(mx) {
    return mx * 360 - 180;
  }
  function mercYToLat(my) {
    const n = Math.PI * (1 - 2 * my);
    return (180 / Math.PI) * Math.atan(Math.sinh(n));
  }

  /**
   * A worker 'error' reply as an Error the page can act on. geometryError is set on every one (the
   * worker is replaced, see _ensureWorker), and exactly one kind: engineTrap (the WASM kernel ran
   * out of memory or trapped: too much map detail), workerLoad (the modules or the kernel did not
   * load: a network fault, a plain retry helps) or neither (the engine refused the design with its
   * own message, e.g. a building too tall for the tile). engineWords is set only for that refusal:
   * the engine throws a plain Error with a shopper sentence; a TypeError or RangeError from a fault
   * is never shown verbatim.
   */
  function workerError(message, { name = null, stage = null } = {}) {
    const text = String(message || 'Geometry worker failed.');
    const err = new Error(text);
    err.geometryError = true;
    err.workerStage = stage;
    err.workerLoad = stage === 'load' || /dynamically imported module|importScripts|Failed to fetch|NetworkError|Load failed|WebAssembly\.(instantiate|compile)/i.test(text);
    err.engineTrap =
      !err.workerLoad &&
      (name === 'RuntimeError' ||
        /null function|memory access out of bounds|out of bounds|unreachable|table index|Aborted\(|out of memory|Cannot enlarge memory|call stack|stack overflow|function signature mismatch/i.test(text));
    err.engineWords = !err.workerLoad && !err.engineTrap && stage !== 'message' && (name == null || name === 'Error');
    return err;
  }

  /** The same object shape makeFitTransform returns, from its numeric parameters. */
  function fitFromParams(p) {
    const fit = { k: p.k, mcx: p.mcx, mcy: p.mcy, latMid: p.latMid, vertScaleMmPerM: p.vertScaleMmPerM };
    if (!Number.isFinite(fit.vertScaleMmPerM)) {
      fit.vertScaleMmPerM = fit.k / (2 * Math.PI * EARTH_R * Math.cos((fit.latMid * Math.PI) / 180));
    }
    fit.modelToMerc = (x, y) => ({ merX: x / fit.k + fit.mcx, merY: -y / fit.k + fit.mcy });
    fit.mercToModel = (merX, merY) => ({ x: (merX - fit.mcx) * fit.k, y: -(merY - fit.mcy) * fit.k });
    return fit;
  }

  function fitParams(fit) {
    return { k: fit.k, mcx: fit.mcx, mcy: fit.mcy, latMid: fit.latMid, vertScaleMmPerM: fit.vertScaleMmPerM };
  }

  function fitKey(fit) {
    if (!fit) return 'none';
    return [fit.k, fit.mcx, fit.mcy].map((n) => n.toPrecision(12)).join('|');
  }

  function haversineKm(lat1, lon1, lat2, lon2) {
    const R = 6371;
    const toRad = Math.PI / 180;
    const dLat = (lat2 - lat1) * toRad;
    const dLon = (lon2 - lon1) * toRad;
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * toRad) * Math.cos(lat2 * toRad) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  /** Climb from GPX elevations with a 3 m hysteresis, to ignore barometric noise. */
  function elevationGain(points) {
    let gain = 0;
    let ref = null;
    let seen = false;
    for (const p of points || []) {
      if (p.ele == null || !Number.isFinite(p.ele)) continue;
      if (p.breakBefore) ref = null;
      if (ref == null) {
        ref = p.ele;
        seen = true;
        continue;
      }
      const d = p.ele - ref;
      if (d >= 3) {
        gain += d;
        ref = p.ele;
      } else if (d <= -3) {
        ref = p.ele;
      }
    }
    return seen ? gain : null;
  }

  /* ---------- IndexedDB (route files for "Continue your last design") ---------- */
  function idbOpen() {
    return new Promise((resolve, reject) => {
      if (typeof indexedDB === 'undefined') return reject(new Error('no indexedDB'));
      const req = indexedDB.open(IDB_NAME, 1);
      req.onupgradeneeded = () => req.result.createObjectStore(IDB_STORE);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }
  async function idbRun(mode, fn) {
    const db = await idbOpen();
    try {
      return await new Promise((resolve, reject) => {
        const tx = db.transaction(IDB_STORE, mode);
        const req = fn(tx.objectStore(IDB_STORE));
        tx.oncomplete = () => resolve(req && req.result);
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error);
      });
    } finally {
      db.close();
    }
  }
  function idbGet(key) {
    return idbRun('readonly', (st) => st.get(key));
  }
  function idbSet(key, value) {
    return idbRun('readwrite', (st) => st.put(value, key));
  }
  function idbDel(key) {
    return idbRun('readwrite', (st) => st.delete(key));
  }

  TerrainStudio.helpers = { fitFromParams, fitParams, fitKey, lonToMercX, latToMercY, mercXToLon, mercYToLat, elevationGain };
})();
